import React, { useState, useEffect, useRef } from 'react';
import {
  FaShoppingCart,
  FaSearch,
  FaLeaf,
  FaDrumstickBite,
  FaPlus,
  FaMinus,
  FaTimes,
  FaTrash,
  FaHotel,
  FaUsers,
  FaBox,
  FaArrowRight,
  FaChevronDown,
  FaChevronUp,
  FaUser,
  FaPhone,
  FaIdCard,
  FaFileInvoice,
  FaCreditCard,
  FaTable,
  FaCircle,
  FaMoneyBill,
  FaDigitalTiling,
  FaList,
  FaPlusCircle,
  FaDigitalOcean,
  FaInfoCircle,
  FaBarcode,
  FaCheckCircle,
  FaBuilding,
  FaSpinner,
  FaRupeeSign,
  FaClock,
  FaUtensils,
  FaTag,
  FaImage,
  FaMicrophone,
  FaBan,
  FaUserShield,
  FaExclamationTriangle,
} from 'react-icons/fa';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;
const REACT_APP_RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

// ── IN-HOUSE ── JWT helper
const TOKEN_STORAGE_KEYS = ['access_token', 'token', 'jwt', 'auth_token'];
const USER_ID_STORAGE_KEY = 'aq_user_id';

const getStoredToken = () => {
  try {
    for (const k of TOKEN_STORAGE_KEYS) {
      const v = window.localStorage.getItem(k);
      if (v && v.length > 10) return v;
    }
  } catch (e) {
    // localStorage may be unavailable (private mode); fail soft
  }
  return null;
};

const getStoredUserId = () => {
  try {
    const v = window.localStorage.getItem(USER_ID_STORAGE_KEY);
    if (!v) return null;
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch (e) {
    return null;
  }
};

const getAuthHeaders = () => {
  const token = getStoredToken();
  const headers = { accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const uid = getStoredUserId();
  if (uid) headers['X-User-Id'] = String(uid);
  return headers;
};

const getAuthJsonHeaders = () => {
  const headers = getAuthHeaders();
  headers['Content-Type'] = 'application/json';
  return headers;
};

// ---------- helpers ----------
const isItemCancelled = (item) => {
  if (!item) return false;
  if (item.is_cancelled === true) return true;
  const s = (item.item_status || item.status || '').toString().toLowerCase();
  return s === 'cancelled' || s === 'canceled';
};

const isOrderCancelled = (order) => {
  if (!order) return false;
  const s = (order.status || order.order_status || '').toString().toLowerCase();
  return s === 'cancelled' || s === 'canceled';
};

const IN_HOUSE_REASONS = [
  { value: 'family', label: 'Family' },
  { value: 'owner', label: 'Owner' },
  { value: 'staff', label: 'Staff' },
  { value: 'complimentary', label: 'Complimentary' },
  { value: 'management', label: 'Management' },
  { value: 'vip', label: 'VIP Guest' },
  { value: 'other', label: 'Other' },
];

function FoodOrder() {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [justAddedItemId, setJustAddedItemId] = useState(null);
  const [specialRequest, setSpecialRequest] = useState('');
  const [discount, setDiscount] = useState(0);
  const [orderType, setOrderType] = useState('dining');
  const [tableNumber, setTableNumber] = useState('');
  const [roomNumber, setRoomNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [numberOfGuests, setNumberOfGuests] = useState(1);
  const [gstNumber, setGstNumber] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [showAdditionalDetails, setShowAdditionalDetails] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [propertyId, setPropertyId] = useState(null);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [orderId, setOrderId] = useState(null);
  const [billingInfo, setBillingInfo] = useState(null);
  const [tables, setTables] = useState([]);
  const [filteredTables, setFilteredTables] = useState([]);
  const [tableSearchText, setTableSearchText] = useState('');
  const [showTableDropdown, setShowTableDropdown] = useState(false);
  const [showOrderItems, setShowOrderItems] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [parcelOrders, setParcelOrders] = useState([]);
  const [filteredParcelOrders, setFilteredParcelOrders] = useState([]);
  const [parcelSearchText, setParcelSearchText] = useState('');
  const [showParcelDropdown, setShowParcelDropdown] = useState(false);
  const [selectedParcelOrder, setSelectedParcelOrder] = useState(null);
  const [parcelOrderDetails, setParcelOrderDetails] = useState(null);
  const [showParcelItems, setShowParcelItems] = useState(false);
  const [roomOrders, setRoomOrders] = useState([]);
  const [filteredRoomOrders, setFilteredRoomOrders] = useState([]);
  const [roomSearchText, setRoomSearchText] = useState('');
  const [showRoomDropdown, setShowRoomDropdown] = useState(false);
  const [selectedRoomOrder, setSelectedRoomOrder] = useState(null);
  const [roomOrderId, setRoomOrderId] = useState(null);
  const [roomBillingInfo, setRoomBillingInfo] = useState(null);
  const [roomOrderItems, setRoomOrderItems] = useState([]);
  const [roomTableSearchText, setRoomTableSearchText] = useState('');
  const [showRoomTableDropdown, setShowRoomTableDropdown] = useState(false);
  const [availableTables, setAvailableTables] = useState([]);
  const [filteredRoomTables, setFilteredRoomTables] = useState([]);
  const [showTaxBreakdown, setShowTaxBreakdown] = useState(false);
  const [cgstAmount, setCgstAmount] = useState(0);
  const [sgstAmount, setSgstAmount] = useState(0);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [isAddingItems, setIsAddingItems] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companySearchText, setCompanySearchText] = useState('');
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const companyDropdownRef = useRef(null);
  const [pendingQuantities, setPendingQuantities] = useState({});
  const [activeSpeechTarget, setActiveSpeechTarget] = useState(null);

  // Discount States
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountError, setDiscountError] = useState('');

  // ── IN-HOUSE ── toggle state
  const [isInHouse, setIsInHouse] = useState(false);
  const [showInHouseModal, setShowInHouseModal] = useState(false);
  const [inHouseReason, setInHouseReason] = useState('');
  const [inHouseNotes, setInHouseNotes] = useState('');
  const [draftReason, setDraftReason] = useState('');
  const [draftNotes, setDraftNotes] = useState('');

  // ── KITCHEN INSTRUCTIONS ── state for the per-item instruction modal
  const [showKitchenInstructionModal, setShowKitchenInstructionModal] = useState(false);
  const [instructionItem, setInstructionItem] = useState(null);
  const [instructionText, setInstructionText] = useState('');
  const [isSendingInstruction, setIsSendingInstruction] = useState(false);
  const [activeInstructionSpeech, setActiveInstructionSpeech] = useState(false);

  const navigate = useNavigate();

  const {
    transcript,
    listening: isListening,
    resetTranscript,
    browserSupportsSpeechRecognition: isSupported,
  } = useSpeechRecognition({
    clearTranscriptOnListen: true,
  });

  // ── KITCHEN INSTRUCTIONS ── separate speech recognizer for the instruction modal
  const {
    transcript: instructionTranscriptRaw,
    listening: isInstructionListening,
    resetTranscript: resetInstructionTranscript,
    browserSupportsSpeechRecognition: isInstructionSpeechSupported,
  } = useSpeechRecognition({ clearTranscriptOnListen: true });

  // ============== DISCOUNT & TAX CALCULATION LOGIC ==============
  const totalAmount = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  useEffect(() => {
    const discountValue = (totalAmount * discountPercent) / 100;
    setDiscountAmount(discountValue);

    if (discountPercent > 25) {
      setDiscountError('Discount cannot exceed 25%');
    } else {
      setDiscountError('');
    }
  }, [discountPercent, totalAmount]);

  const taxableAmount = totalAmount - discountAmount;

  const calculateTaxes = () => {
    if (isInHouse) return 0;
    let totalTax = 0;
    if (cart.length === 0) return 0;

    cart.forEach((item) => {
      const itemSubtotal = item.price * item.quantity;
      const itemPercentage = totalAmount > 0 ? itemSubtotal / totalAmount : 0;
      const discountedItemAmount = itemSubtotal - discountAmount * itemPercentage;

      if (item.value_type === 'P') {
        totalTax += discountedItemAmount * (item.value / 100);
      } else {
        totalTax += item.value * item.quantity;
      }
    });

    return totalTax;
  };

  const taxes = calculateTaxes();

  useEffect(() => {
    const halfTax = taxes / 2;
    setCgstAmount(halfTax);
    setSgstAmount(halfTax);
  }, [taxes]);

  const finalTotalWithDiscount = taxableAmount + taxes;

  useEffect(() => {
    if (transcript && activeSpeechTarget === 'search') {
      setSearch(transcript);
    }
  }, [transcript, activeSpeechTarget]);

  useEffect(() => {
    if (transcript && activeSpeechTarget === 'special') {
      setSpecialRequest(transcript);
    }
  }, [transcript, activeSpeechTarget]);

  // ── KITCHEN INSTRUCTIONS ── pipe speech transcript into the textarea
  useEffect(() => {
    if (instructionTranscriptRaw && activeInstructionSpeech) {
      setInstructionText((prev) => {
        const base = prev ? prev + ' ' : '';
        return (base + instructionTranscriptRaw).trim();
      });
      resetInstructionTranscript();
    }
  }, [instructionTranscriptRaw, activeInstructionSpeech, resetInstructionTranscript]);

  const startSearchListening = () => {
    setActiveSpeechTarget('search');
    resetTranscript();
    if (isListening) {
      SpeechRecognition.stopListening();
    }
    SpeechRecognition.startListening({
      continuous: false,
      language: 'en-IN',
      interimResults: true,
    });
    toast.info('🎤 Listening for search... Speak now');
  };

  const stopSearchListening = () => {
    setActiveSpeechTarget(null);
    SpeechRecognition.stopListening();
    toast.info('👂 Stopped listening');
  };

  const startSpecialListening = () => {
    setActiveSpeechTarget('special');
    resetTranscript();
    if (isListening) {
      SpeechRecognition.stopListening();
    }
    SpeechRecognition.startListening({
      continuous: false,
      language: 'en-IN',
      interimResults: true,
    });
    toast.info('🎤 Listening for special instructions... Speak now');
  };

  const stopSpecialListening = () => {
    setActiveSpeechTarget(null);
    SpeechRecognition.stopListening();
    toast.info('👂 Stopped listening');
  };

  useEffect(() => {
    return () => {
      if (isListening) {
        SpeechRecognition.stopListening();
      }
    };
  }, []);

  const clearSearch = () => {
    setSearch('');
    setSelectedCategory(null);
    setSelectedSubcategory(null);
  };

  const handlePendingQuantityChange = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    setPendingQuantities((prev) => ({
      ...prev,
      [itemId]: newQuantity,
    }));
  };

  const handleUpdateQuantityWithButton = async (item) => {
    if (isItemCancelled(item)) {
      toast.info('This item is cancelled and cannot be updated');
      return;
    }

    const newQuantity = pendingQuantities[item.order_item_id] || item.quantity || 1;

    if (newQuantity === item.quantity) {
      toast.info('Quantity is already set to this value');
      return;
    }

    await handleUpdateItemQuantity(item, newQuantity);

    setPendingQuantities((prev) => ({
      ...prev,
      [item.order_item_id]: undefined,
    }));
  };

  const getTodayDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = String(today.getFullYear()).slice(-2);
    return `${day}${month}${year}`;
  };

  useEffect(() => {
    const loadRazorpayScript = () => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => setRazorpayLoaded(true);
      script.onerror = () => {
        toast.error('Error loading Razorpay script. Please try again.');
      };
      document.body.appendChild(script);
    };

    loadRazorpayScript();
  }, []);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/companies`, {
          headers: getAuthHeaders(),
        });

        if (response.ok) {
          const data = await response.json();
          setCompanies(data.companies || []);
        } else {
          console.error('Failed to fetch companies');
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };

    fetchCompanies();
  }, []);

  useEffect(() => {
    if (cart.length > 0) {
      setShowAdditionalDetails(false);
    }
  }, [cart]);

  useEffect(() => {
    if (companySearchText && companySearchText.length > 1) {
      const filtered = companies.filter((company) =>
        company.companyname.toLowerCase().includes(companySearchText.toLowerCase())
      );
      setFilteredCompanies(filtered);
      setShowCompanyDropdown(true);
    } else {
      setFilteredCompanies([]);
      setShowCompanyDropdown(false);
    }
  }, [companySearchText, companies]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
        setShowCompanyDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await fetch(`${AQ_BASE_URL}/aq/api/currentuser`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        });

        if (response.ok) {
          const userData = await response.json();
          setCurrentUser(userData);
          setPropertyId(userData.property_id);

          if (userData.id) {
            try {
              window.localStorage.setItem(USER_ID_STORAGE_KEY, String(userData.id));
            } catch (e) {
              // localStorage unavailable
            }
          }

          console.log('Current user loaded:', userData);
        } else {
          console.error('Failed to fetch current user');
          setPropertyId(1);
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
        setPropertyId(1);
      }
    };

    fetchCurrentUser();
  }, []);

  const fetchTables = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/dining-tables/`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        const activeTables = data.tables.filter((table) => table.status !== 'out_of_service');
        setTables(activeTables);
        setFilteredTables(activeTables);
      } else {
        console.error('Failed to fetch tables');
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
      toast.error('Failed to load tables');
    }
  };

  useEffect(() => {
    fetchTables();
  }, []);

  useEffect(() => {
    if (orderType === 'room') {
      fetchRoomOrders();
    }
  }, [orderType]);

  const fetchAvailableTablesForRoom = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/dining-tables/`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        const available = data.tables.filter((table) => table.status === 'available');
        setAvailableTables(available);
        setFilteredRoomTables(available);
      } else {
        console.error('Failed to fetch available tables');
      }
    } catch (error) {
      console.error('Error fetching available tables:', error);
    }
  };

  useEffect(() => {
    if (orderType === 'room') {
      fetchAvailableTablesForRoom();
    }
  }, [orderType]);

  const fetchRoomOrders = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Room orders response:', data);

        const transformedOrders = data
          .filter((order) => {
            if (order.order_type !== 'room') return false;
            if (order.status?.toLowerCase() === 'delivered') return false;
            if (order.status === 'Completed' || order.status === 'Cancelled') return false;
            if (order.billing && order.billing.is_paid === true) return false;
            return true;
          })
          .map((order) => {
            const hasBilling = order.billing !== null;
            const isPaid = hasBilling ? order.billing.is_paid : false;
            const billingId = hasBilling ? order.billing.billing_id : null;

            let paymentMethod = 'Not Paid';
            if (order.payments && order.payments.length > 0) {
              const payment = order.payments[0];
              paymentMethod = payment.payment_method || 'Not Paid';
              paymentMethod = paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1);
            } else if (isPaid) {
              paymentMethod = 'Paid';
            }

            const filteredItems = (order.items || []).filter(
              (item) => item.status?.toLowerCase() !== 'delivered'
            );

            return {
              id: order.id,
              order_id: order.id,
              order_type: order.order_type,
              order_status: order.status || 'pending',
              customer_name: order.customer_name || 'Guest',
              customer_phone: order.phone_number || null,
              customer_email: null,
              table_number: order.table_number || null,
              parcel_number: order.parcel_number || null,
              room_number: order.room_number || null,
              kot_number: null,
              totalprice: order.totalprice || 0,
              total_amount: order.totalprice || 0,
              is_paid: isPaid,
              ordered_at: order.created_at,
              created_at: order.created_at,
              items: filteredItems,
              billing: order.billing || null,
              billing_id: billingId,
              booking_id: order.booking_id || null,
              special_request: order.special_request || null,
              gst_number: null,
              has_billing: hasBilling,
              payment_method: paymentMethod,
              billings: order.billing ? [order.billing] : [],
              payments: order.payments || [],
              status: order.status || 'pending',
              is_in_house: !!order.is_in_house,
              in_house_reason: order.in_house_reason || null,
            };
          });

        transformedOrders.sort((a, b) => {
          return new Date(b.created_at) - new Date(a.created_at);
        });

        setRoomOrders(transformedOrders);
        setFilteredRoomOrders(transformedOrders);

        return transformedOrders;
      }
      return [];
    } catch (error) {
      console.error('Error fetching room orders:', error);
      return [];
    }
  };

  const fetchRoomOrderByNumber = async (roomNum) => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        const roomOrder = data.find(
          (order) =>
            order.order_type === 'room' &&
            order.room_number === roomNum &&
            order.status !== 'Completed' &&
            order.status !== 'Cancelled' &&
            !(order.billing && order.billing.is_paid === true)
        );
        if (roomOrder) {
          roomOrder.billing = roomOrder.billing || null;
          roomOrder.booking_id = roomOrder.booking_id;
        }
        return roomOrder;
      }
      return null;
    } catch (error) {
      console.error('Error fetching room order:', error);
      return null;
    }
  };

  const handleAddItemsToRoomOrder = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    if (!roomNumber) {
      toast.error('Room number is required');
      return;
    }

    if (isOrderCancelled(selectedRoomOrder)) {
      toast.error('This order has been cancelled. Cannot add items.');
      return;
    }

    const items = cart.map((item) => ({
      foodid: item.id,
      quantity: item.quantity,
      specialRequest: item.specialRequest || null,
    }));

    const addItemsData = {
      items: items,
      specialRequest: specialRequest || null,
    };

    setIsAddingItems(true);
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/orders/room/${roomNumber}/add-items`, {
        method: 'POST',
        headers: getAuthJsonHeaders(),
        body: JSON.stringify(addItemsData),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Items added successfully!');
        const roomOrder = await fetchRoomOrderByNumber(roomNumber);
        if (roomOrder) {
          const mappedItems = (roomOrder.items || []).map((item) => ({
            order_item_id: item.id,
            food_id: item.food_id,
            food_name: item.food_title || item.food_name || 'Item',
            quantity: item.quantity,
            unit_price: item.food_price || item.unit_price || 0,
            total_amount: item.total_price || item.food_price * item.quantity,
            special_request: item.special_request,
            item_status: item.item_status || item.status,
            is_cancelled: isItemCancelled(item),
          }));

          setRoomOrderId(roomOrder.id);
          setRoomOrderItems(mappedItems);
          setSelectedRoomOrder((prev) => ({
            ...prev,
            ...roomOrder,
            items: mappedItems,
            booking_id: roomOrder.booking_id || prev?.booking_id,
          }));

          if (roomOrder.billing && roomOrder.billing.billing_id) {
            setRoomBillingInfo({
              billing_id: roomOrder.billing.billing_id,
              final_amount: roomOrder.billing.final_amount || roomOrder.totalprice,
              is_paid: roomOrder.billing.is_paid || false,
            });
          } else if (roomOrder.booking_id) {
            await createRoomOrderBilling(roomOrder.id, roomOrder.booking_id);
          }
        }
        setCart([]);
        setSpecialRequest('');
        clearSearch();
      } else {
        throw new Error(data.detail || 'Failed to add items');
      }
    } catch (error) {
      console.error('Error adding items to room order:', error);
      toast.error(error.message || 'Failed to add items');
    } finally {
      setIsAddingItems(false);
    }
  };

  useEffect(() => {
    if (roomSearchText) {
      const filtered = roomOrders.filter(
        (order) =>
          order.room_number?.toLowerCase().includes(roomSearchText.toLowerCase()) ||
          order.customer_name?.toLowerCase().includes(roomSearchText.toLowerCase()) ||
          order.id?.toLowerCase().includes(roomSearchText.toLowerCase())
      );
      setFilteredRoomOrders(filtered);
    } else {
      setFilteredRoomOrders(roomOrders);
    }
  }, [roomSearchText, roomOrders]);

  useEffect(() => {
    if (orderType === 'parcel') {
      fetchParcelOrders();
    }
  }, [orderType]);

  const fetchParcelOrders = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders?order_type=parcel`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        const todayDate = getTodayDate();
        const todayOrders = data.orders.filter((order) => {
          const parcelNumber = order.parcel_number;
          if (parcelNumber && parcelNumber.includes(todayDate)) {
            return order.order_status === 'pending' || order.order_status === 'ready';
          }
          return false;
        });
        setParcelOrders(todayOrders);
        setFilteredParcelOrders(todayOrders);
      } else {
        console.error('Failed to fetch parcel orders');
      }
    } catch (error) {
      console.error('Error fetching parcel orders:', error);
      toast.error('Failed to load parcel orders');
    }
  };

  useEffect(() => {
    if (parcelSearchText) {
      const filtered = parcelOrders.filter(
        (order) =>
          order.parcel_number?.toLowerCase().includes(parcelSearchText.toLowerCase()) ||
          order.customer_name?.toLowerCase().includes(parcelSearchText.toLowerCase()) ||
          order.customer_phone?.includes(parcelSearchText)
      );
      setFilteredParcelOrders(filtered);
    } else {
      setFilteredParcelOrders(parcelOrders);
    }
  }, [parcelSearchText, parcelOrders]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [itemsRes, categoriesRes, subcategoriesRes] = await Promise.all([
          fetch(`${BQ_BASE_URL}/bq/api/items/`, { headers: getAuthHeaders() }),
          fetch(`${BQ_BASE_URL}/bq/api/categories/`, { headers: getAuthHeaders() }),
          fetch(`${BQ_BASE_URL}/bq/api/subcategories/`, { headers: getAuthHeaders() }),
        ]);

        const itemsData = await itemsRes.json();
        const categoriesData = await categoriesRes.json();
        const subcategoriesData = await subcategoriesRes.json();

        setMenuItems(itemsData.items || []);
        setCategories(categoriesData || []);
        setSubcategories(subcategoriesData || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load menu');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (tableSearchText) {
      const filtered = tables.filter(
        (table) =>
          table.table_number.toLowerCase().includes(tableSearchText.toLowerCase()) ||
          table.table_name?.toLowerCase().includes(tableSearchText.toLowerCase())
      );
      setFilteredTables(filtered);
    } else {
      setFilteredTables(tables);
    }
  }, [tableSearchText, tables]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'text-green-500';
      case 'occupied':
        return 'text-red-500';
      case 'reserved':
        return 'text-yellow-500';
      default:
        return 'text-gray-500';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'available':
        return 'Available';
      case 'occupied':
        return 'Occupied';
      case 'reserved':
        return 'Reserved';
      default:
        return status;
    }
  };

  const handleTableSelect = async (table) => {
    setTableNumber(table.table_number);
    setTableSearchText(`${table.table_number} (${getStatusText(table.status)})`);
    setShowTableDropdown(false);

    if (table.status === 'occupied') {
      await fetchOrderByTable(table.table_number);
    } else if (table.status === 'available') {
      setCurrentOrder(null);
      setOrderId(null);
      setOrderItems([]);
      setBillingInfo(null);
      setCart([]);
      toast.info(`Table ${table.table_number} is available. Create a new order.`);
    }
  };

  const fetchOrderByTable = async (tableNum) => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/tables/${tableNum}/order`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentOrder(data);
        setOrderId(data.order_id);

        if (data.is_in_house) {
          setIsInHouse(true);
          setInHouseReason(data.in_house_reason || '');
          setInHouseNotes(data.in_house_notes || '');
        } else {
          setIsInHouse(false);
          setInHouseReason('');
          setInHouseNotes('');
        }

        const allItems = [];
        if (data.kitchen_batches) {
          data.kitchen_batches.forEach((batch) => {
            if (batch.items) {
              batch.items.forEach((item) => {
                allItems.push({
                  order_item_id: item.item_id,
                  food_id: item.food_id,
                  food_name: item.name,
                  quantity: item.quantity,
                  unit_price: item.unit_price,
                  total_amount: item.line_total,
                  special_request: item.special_request,
                  item_status: item.item_status,
                  is_cancelled:
                    (item.item_status || '').toString().toLowerCase() === 'cancelled' ||
                    item.is_cancelled === true,
                  is_complimentary: item.is_complimentary,
                  kot_number: batch.kot_number,
                  description: `${item.name} x ${item.quantity}`,
                });
              });
            }
          });
        }

        setOrderItems(allItems);
        setBillingInfo(data.billing);
        setCustomerName(data.customer_name || '');
        setCustomerEmail(data.customer_email || '');
        setPhoneNumber(data.customer_phone || '');
        setNumberOfGuests(data.number_of_guests || 1);
        setSpecialRequest(data.special_request || '');

        if (data.gst_number) {
          setGstNumber(data.gst_number);
        }

        toast.success(`Order found for table ${tableNum}`);
        setCart([]);
      } else if (response.status === 404) {
        toast.info(`No active order found for table ${tableNum}. You can create a new order.`);
        setCurrentOrder(null);
        setOrderId(null);
        setOrderItems([]);
        setBillingInfo(null);
      } else {
        throw new Error('Failed to fetch order');
      }
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('Failed to fetch order details');
    }
  };

  const fetchParcelOrderDetails = async (orderId) => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders/${orderId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setParcelOrderDetails(data);

        if (data.is_in_house) {
          setIsInHouse(true);
          setInHouseReason(data.in_house_reason || '');
          setInHouseNotes(data.in_house_notes || '');
        } else {
          setIsInHouse(false);
          setInHouseReason('');
          setInHouseNotes('');
        }

        const allItems = [];
        if (data.kitchen_batches) {
          data.kitchen_batches.forEach((batch) => {
            if (batch.items) {
              batch.items.forEach((item) => {
                allItems.push({
                  order_item_id: item.item_id,
                  food_id: item.food_id,
                  food_name: item.name,
                  quantity: item.quantity,
                  unit_price: item.unit_price,
                  total_amount: item.line_total,
                  special_request: item.special_request,
                  item_status: item.item_status,
                  is_cancelled:
                    (item.item_status || '').toString().toLowerCase() === 'cancelled' ||
                    item.is_cancelled === true,
                  is_complimentary: item.is_complimentary,
                  kot_number: batch.kot_number,
                  description: `${item.name} x ${item.quantity}`,
                });
              });
            }
          });
        }

        setOrderItems(allItems);
        setBillingInfo(data.billing);
        setCustomerName(data.customer_name || '');
        setCustomerEmail(data.customer_email || '');
        setPhoneNumber(data.customer_phone || '');
        setSpecialRequest(data.special_request || '');
        setDeliveryAddress(data.delivery_address || '');

        if (data.gst_number) {
          setGstNumber(data.gst_number);
        }

        toast.success(`Order details loaded`);
      } else {
        throw new Error('Failed to fetch order details');
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast.error('Failed to fetch order details');
    }
  };

  const fetchRoomOrderDetails = async (order) => {
    let detail = null;
    try {
      const detailRes = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/${order.id}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (detailRes.ok) {
        detail = await detailRes.json();
      } else {
        console.warn('Room order detail fetch failed, status', detailRes.status);
      }
    } catch (detailErr) {
      console.warn('Room order detail fetch error:', detailErr);
    }

    const orderData = {
      ...order,
      ...(detail || {}),
      items: detail && detail.items && detail.items.length ? detail.items : order.items || [],
      billing: order.billing || (detail && detail.billing) || null,
      payments: order.payments || (detail && detail.payments) || [],
      booking_id: (detail && detail.booking_id) || order.booking_id,
    };

    setSelectedRoomOrder(orderData);
    setRoomOrderId(order.id);

    if (orderData.is_in_house) {
      setIsInHouse(true);
      setInHouseReason(orderData.in_house_reason || '');
      setInHouseNotes(orderData.in_house_notes || '');
    } else {
      setIsInHouse(false);
      setInHouseReason('');
      setInHouseNotes('');
    }

    const mappedItems = (orderData.items || []).map((item) => ({
      order_item_id: item.id,
      food_id: item.food_id,
      food_name: item.food_title || item.food_name || 'Item',
      quantity: item.quantity,
      unit_price: item.food_price || item.unit_price || 0,
      total_amount: item.total_price || item.line_total || item.food_price * item.quantity,
      special_request: item.special_request,
      item_status: item.item_status || item.status,
      is_cancelled: isItemCancelled(item),
      description: `${item.food_title || 'Item'} x ${item.quantity}`,
    }));

    setRoomOrderItems(mappedItems);
    setCustomerName(orderData.customer_name || '');
    setPhoneNumber(orderData.phone_number || '');
    setRoomNumber(orderData.room_number || '');
    setSpecialRequest(orderData.special_request || '');
    setTableNumber(orderData.table_number || '');
    setRoomTableSearchText(orderData.table_number ? `${orderData.table_number}` : '');
    setGstNumber(orderData.gst_number || '');

    setShowOrderItems(true);

    const orderStatus = (orderData.status || orderData.order_status || '').toLowerCase();
    const isClosedOrder =
      orderStatus === 'delivered' || orderStatus === 'completed' || orderStatus === 'cancelled';

    if (orderData.billing && orderData.billing.billing_id) {
      setRoomBillingInfo({
        billing_id: orderData.billing.billing_id,
        final_amount: orderData.billing.final_amount || orderData.totalprice,
        is_paid: orderData.billing.is_paid || false,
      });
      toast.success(`Room order loaded for room ${orderData.room_number}`);
    } else if (isClosedOrder) {
      setRoomBillingInfo(null);
      toast.info('Opened a past order for viewing. No new bill was created.');
    } else if (orderData.booking_id) {
      await createRoomOrderBilling(orderData.id, orderData.booking_id);
    } else {
      console.error('Booking ID not found in order object');
      toast.error('Cannot create billing: Booking ID missing');
    }

    setCart([]);
  };

  const handleParcelSelect = async (order) => {
    setSelectedParcelOrder(order);
    setParcelSearchText(`${order.parcel_number} - ${order.customer_name || 'Guest'}`);
    setShowParcelDropdown(false);
    setOrderId(order.order_id);
    setParcelOrderDetails(null);
    await fetchParcelOrderDetails(order.order_id);
  };

  const handleRoomSelect = async (order) => {
    setRoomSearchText(`${order.room_number} - ${order.customer_name || 'Guest'}`);
    setShowRoomDropdown(false);
    await fetchRoomOrderDetails(order);
  };

  const handleNewRoomOrder = () => {
    setSelectedRoomOrder(null);
    setRoomSearchText('');
    setRoomOrderId(null);
    setRoomOrderItems([]);
    setRoomBillingInfo(null);
    setRoomNumber('');
    setTableNumber('');
    setRoomTableSearchText('');
    setCustomerName('');
    setCustomerEmail('');
    setPhoneNumber('');
    setGstNumber('');
    setSpecialRequest('');
    setCart([]);
    setShowRoomDropdown(false);
    setIsInHouse(false);
    setInHouseReason('');
    setInHouseNotes('');
    toast.info('Creating new room order. Add items to cart and place order.');
  };

  const handleNewParcelOrder = () => {
    setSelectedParcelOrder(null);
    setParcelSearchText('');
    setOrderId(null);
    setOrderItems([]);
    setBillingInfo(null);
    setCustomerName('');
    setCustomerEmail('');
    setPhoneNumber('');
    setDeliveryAddress('');
    setGstNumber('');
    setSpecialRequest('');
    setCart([]);
    setShowParcelDropdown(false);
    setIsInHouse(false);
    setInHouseReason('');
    setInHouseNotes('');
    toast.info('Creating new parcel order. Add items to cart and place order.');
  };

  const getCategoryIdFromSubCategoryId = (subCategoryId) => {
    const subcategory = subcategories.find((sub) => sub.id === subCategoryId);
    return subcategory ? subcategory.categoryid : null;
  };

  const handleAddToCart = (item) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((cartItem) => cartItem.id === item.id);
      if (existingItem) {
        return prevCart.map((cartItem) =>
          cartItem.id === item.id ? { ...cartItem, quantity: cartItem.quantity + 1 } : cartItem
        );
      }
      return [...prevCart, { ...item, quantity: 1, specialRequest: '' }];
    });

    setJustAddedItemId(item.id);
    setTimeout(() => setJustAddedItemId(null), 1000);
    toast.success(`${item.title} added to cart`);
    clearSearch();
  };

  const handleRemoveFromCart = (id) => {
    setCart(cart.filter((item) => item.id !== id));
    toast.info('Item removed from cart');
  };

  const handleIncreaseQuantity = (id) => {
    setCart((prevCart) =>
      prevCart.map((item) => (item.id === id ? { ...item, quantity: item.quantity + 1 } : item))
    );
  };

  const handleDecreaseQuantity = (id) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === id && item.quantity > 1 ? { ...item, quantity: item.quantity - 1 } : item
      )
    );
  };

  const handleIncreaseGuests = () => {
    setNumberOfGuests((prev) => prev + 1);
  };

  const handleDecreaseGuests = () => {
    setNumberOfGuests((prev) => (prev > 1 ? prev - 1 : 1));
  };

  const clearCart = () => {
    setCart([]);
    setSpecialRequest('');
    setSearch('');
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setShowAdditionalDetails(false);
    toast.info('Cart cleared');
  };

  const clearAllFields = () => {
    setCart([]);
    setSpecialRequest('');
    setSearch('');
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setShowAdditionalDetails(false);
    setDiscountPercent(0);
    setDiscountAmount(0);
    setDiscountError('');
    setDiscount(0);

    setCustomerName('');
    setCustomerEmail('');
    setPhoneNumber('');
    setGstNumber('');
    setDeliveryAddress('');
    setSelectedCompany(null);
    setCompanySearchText('');

    setIsInHouse(false);
    setInHouseReason('');
    setInHouseNotes('');
    setShowInHouseModal(false);

    if (orderType === 'dining') {
      setTableNumber('');
      setTableSearchText('');
      setNumberOfGuests(1);
      setOrderId(null);
      setCurrentOrder(null);
      setOrderItems([]);
      setBillingInfo(null);
      setShowTableDropdown(false);
    } else if (orderType === 'parcel') {
      setOrderId(null);
      setOrderItems([]);
      setBillingInfo(null);
      setSelectedParcelOrder(null);
      setParcelSearchText('');
      setParcelOrderDetails(null);
      setShowParcelDropdown(false);
    } else if (orderType === 'room') {
      setRoomNumber('');
      setRoomSearchText('');
      setRoomOrderId(null);
      setRoomOrderItems([]);
      setRoomBillingInfo(null);
      setSelectedRoomOrder(null);
      setTableNumber('');
      setRoomTableSearchText('');
      setShowRoomDropdown(false);
      setShowRoomTableDropdown(false);
    }

    toast.info('All fields cleared');
  };

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setCompanySearchText(company.companyname);
    setGstNumber(company.gstnumber || '');
    setCustomerName(company.companyname);
    setShowCompanyDropdown(false);
  };

  const handleUpdateItemQuantity = async (item, newQuantity) => {
    if (isItemCancelled(item)) {
      toast.error('This item is cancelled and cannot be updated');
      return;
    }
    if (!item.order_item_id) {
      toast.error('Item ID not found');
      return;
    }

    const itemId = item.order_item_id;
    const orderIdToUse = orderType === 'room' ? roomOrderId : orderId;

    if (!orderIdToUse) {
      toast.error('No order found');
      return;
    }

    const toastId = toast.loading('Updating quantity...');

    try {
      let response;
      let url;

      if (orderType === 'room') {
        url = `${BQ_BASE_URL}/bq/api/orders/room/${roomNumber}/items/${itemId}`;
        response = await fetch(url, {
          method: 'PATCH',
          headers: getAuthJsonHeaders(),
          body: JSON.stringify({
            quantity: newQuantity,
            specialRequest: item.special_request || '',
          }),
        });
      } else if (orderType === 'dining' || orderType === 'parcel') {
        url = `${BQ_BASE_URL}/bq/api/restaurant/orders/${orderIdToUse}/items/${itemId}`;
        response = await fetch(url, {
          method: 'PATCH',
          headers: getAuthJsonHeaders(),
          body: JSON.stringify({
            quantity: newQuantity,
            special_request: item.special_request || '',
          }),
        });
      }

      const data = await response.json();

      toast.dismiss(toastId);

      if (response.ok) {
        toast.success(`${item.food_name || 'Item'} quantity updated to ${newQuantity}`);

        if (orderType === 'dining' && tableNumber) {
          await fetchOrderByTable(tableNumber);
          await fetchTables();
        } else if (orderType === 'parcel' && orderId) {
          await fetchParcelOrderDetails(orderId);
          await fetchParcelOrders();
        } else if (orderType === 'room' && roomNumber) {
          const roomOrder = await fetchRoomOrderByNumber(roomNumber);
          if (roomOrder) {
            const mappedItems = (roomOrder.items || []).map((item) => ({
              order_item_id: item.id,
              food_id: item.food_id,
              food_name: item.food_title || item.food_name || 'Item',
              quantity: item.quantity,
              unit_price: item.food_price || item.unit_price || 0,
              total_amount: item.total_price || item.food_price * item.quantity,
              special_request: item.special_request,
              item_status: item.item_status || item.status,
              is_cancelled: isItemCancelled(item),
            }));

            setRoomOrderId(roomOrder.id);
            setRoomOrderItems(mappedItems);
            setSelectedRoomOrder((prev) => ({
              ...prev,
              ...roomOrder,
              items: mappedItems,
            }));

            if (roomOrder.billing && roomOrder.billing.billing_id) {
              setRoomBillingInfo({
                billing_id: roomOrder.billing.billing_id,
                final_amount: roomOrder.billing.final_amount || roomOrder.totalprice,
                is_paid: roomOrder.billing.is_paid || false,
              });
            }
          }
          await fetchRoomOrders();
        }
      } else {
        throw new Error(data.detail || 'Failed to update quantity');
      }
    } catch (error) {
      console.error('Error updating item quantity:', error);
      toast.dismiss(toastId);
      toast.error(error.message || 'Failed to update quantity');
    }
  };

  // ============== KITCHEN INSTRUCTION HANDLERS ==============

  const handleOpenKitchenInstruction = (item) => {
    if (isItemCancelled(item)) {
      toast.info('Cannot add instructions to a cancelled item');
      return;
    }
    setInstructionItem({ ...item, __isCartItem: false });
    setInstructionText(item.special_request || '');
    setActiveInstructionSpeech(false);
    setShowKitchenInstructionModal(true);
  };

  const handleOpenCartItemInstruction = (item) => {
    setInstructionItem({ ...item, __isCartItem: true });
    setInstructionText(item.specialRequest || '');
    setActiveInstructionSpeech(false);
    setShowKitchenInstructionModal(true);
  };

  const handleSaveCartItemInstruction = () => {
    const trimmed = (instructionText || '').trim();
    setCart((prev) =>
      prev.map((ci) => (ci.id === instructionItem.id ? { ...ci, specialRequest: trimmed } : ci))
    );
    toast.success(`Instruction saved for ${instructionItem.title || 'item'}`);
    if (isInstructionListening) SpeechRecognition.stopListening();
    setActiveInstructionSpeech(false);
    setShowKitchenInstructionModal(false);
    setInstructionItem(null);
    setInstructionText('');
  };

  const toggleInstructionSpeech = () => {
    if (!isInstructionSpeechSupported) {
      toast.error('Speech recognition is not supported in this browser');
      return;
    }
    if (isInstructionListening) {
      SpeechRecognition.stopListening();
      setActiveInstructionSpeech(false);
      toast.info('Stopped listening');
    } else {
      resetInstructionTranscript();
      setActiveInstructionSpeech(true);
      SpeechRecognition.startListening({
        continuous: false,
        language: 'en-IN',
        interimResults: true,
      });
      toast.info('🎤 Listening for kitchen instruction... Speak now');
    }
  };

  const handleSendKitchenInstruction = async () => {
    if (!instructionItem) return;

    const trimmed = (instructionText || '').trim();
    if (!trimmed) {
      toast.error('Please enter an instruction');
      return;
    }

    const itemId = instructionItem.order_item_id;
    if (!itemId) {
      toast.error('Item ID not found');
      return;
    }

    const orderIdToUse = orderType === 'room' ? roomOrderId : orderId;

    if (!orderIdToUse) {
      toast.error('No active order found');
      return;
    }

    setIsSendingInstruction(true);
    const toastId = toast.loading('Sending instruction to kitchen...');

    try {
      let url;
      let body;

      if (orderType === 'room') {
        url = `${BQ_BASE_URL}/bq/api/orders/room/${roomNumber}/items/${itemId}`;
        body = {
          quantity: instructionItem.quantity || 1,
          specialRequest: trimmed,
        };
      } else {
        url = `${BQ_BASE_URL}/bq/api/restaurant/orders/${orderIdToUse}/items/${itemId}`;
        body = {
          quantity: instructionItem.quantity || 1,
          special_request: trimmed,
        };
      }

      const response = await fetch(url, {
        method: 'PATCH',
        headers: getAuthJsonHeaders(),
        body: JSON.stringify(body),
      });

      const data = await response.json().catch(() => ({}));

      toast.dismiss(toastId);

      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Failed to send kitchen instruction');
      }

      toast.success(`Instruction sent for ${instructionItem.food_name || 'item'}`);

      if (orderType === 'dining' && tableNumber) {
        await fetchOrderByTable(tableNumber);
        await fetchTables();
      } else if (orderType === 'parcel' && orderId) {
        await fetchParcelOrderDetails(orderId);
        await fetchParcelOrders();
      } else if (orderType === 'room' && roomNumber) {
        const roomOrder = await fetchRoomOrderByNumber(roomNumber);
        if (roomOrder) {
          const mappedItems = (roomOrder.items || []).map((item) => ({
            order_item_id: item.id,
            food_id: item.food_id,
            food_name: item.food_title || item.food_name || 'Item',
            quantity: item.quantity,
            unit_price: item.food_price || item.unit_price || 0,
            total_amount: item.total_price || item.food_price * item.quantity,
            special_request: item.special_request,
            item_status: item.item_status || item.status,
            is_cancelled: isItemCancelled(item),
          }));
          setRoomOrderId(roomOrder.id);
          setRoomOrderItems(mappedItems);
          setSelectedRoomOrder((prev) => ({
            ...prev,
            ...roomOrder,
            items: mappedItems,
          }));
        }
        await fetchRoomOrders();
      }

      if (isInstructionListening) {
        SpeechRecognition.stopListening();
      }
      setActiveInstructionSpeech(false);
      setShowKitchenInstructionModal(false);
      setInstructionItem(null);
      setInstructionText('');
    } catch (error) {
      console.error('Error sending kitchen instruction:', error);
      toast.dismiss(toastId);
      toast.error(error.message || 'Failed to send kitchen instruction');
    } finally {
      setIsSendingInstruction(false);
    }
  };

  // ============== CANCEL ITEM ==============
  const handleRemoveItemFromOrder = async (itemId, orderIdToUse, itemName) => {
    const toastId = toast.loading('Cancelling item...');

    try {
      let response;
      let url;

      if (orderType === 'room') {
        url = `${BQ_BASE_URL}/bq/api/orders/${orderIdToUse}/items/${itemId}/cancel`;
        response = await fetch(url, {
          method: 'POST',
          headers: getAuthJsonHeaders(),
          body: JSON.stringify({ reason: '' }),
        });
      } else {
        url = `${BQ_BASE_URL}/bq/api/restaurant/orders/${orderIdToUse}/items/${itemId}/cancel`;
        response = await fetch(url, {
          method: 'POST',
          headers: getAuthJsonHeaders(),
          body: JSON.stringify({ reason: '' }),
        });
      }

      const data = await response.json();

      toast.dismiss(toastId);

      if (response.ok) {
        const cancelledName = data.food_name || itemName || 'Item';
        toast.success(data.message || `${cancelledName} cancelled from order`);

        if (data.parent_order_cancelled) {
          toast.info('All items were cancelled — parent order is now cancelled.');
        }

        if (orderType === 'dining' && tableNumber) {
          await fetchOrderByTable(tableNumber);
          await fetchTables();
        } else if (orderType === 'parcel' && orderId) {
          await fetchParcelOrderDetails(orderId);
          await fetchParcelOrders();
        } else if (orderType === 'room' && roomNumber) {
          if (data.parent_order_cancelled) {
            setRoomOrderId(null);
            setRoomOrderItems([]);
            setRoomBillingInfo(null);
            setSelectedRoomOrder(null);
            setRoomSearchText('');
            setRoomNumber('');
            setTableNumber('');
            setRoomTableSearchText('');
            setCustomerName('');
            setPhoneNumber('');
            setGstNumber('');
            setSpecialRequest('');
            setCart([]);
          } else {
            const roomOrder = await fetchRoomOrderByNumber(roomNumber);
            if (roomOrder) {
              const mappedItems = (roomOrder.items || []).map((item) => ({
                order_item_id: item.id,
                food_id: item.food_id,
                food_name: item.food_title || item.food_name || 'Item',
                quantity: item.quantity,
                unit_price: item.food_price || item.unit_price || 0,
                total_amount: item.total_price || item.food_price * item.quantity,
                special_request: item.special_request,
                item_status: item.item_status || item.status,
                is_cancelled: isItemCancelled(item),
              }));

              setRoomOrderId(roomOrder.id);
              setRoomOrderItems(mappedItems);
              setSelectedRoomOrder((prev) => ({
                ...prev,
                ...roomOrder,
                items: mappedItems,
              }));

              if (roomOrder.billing && roomOrder.billing.billing_id) {
                setRoomBillingInfo({
                  billing_id: roomOrder.billing.billing_id,
                  final_amount: roomOrder.billing.final_amount || roomOrder.totalprice,
                  is_paid: roomOrder.billing.is_paid || false,
                });
              }
            }
          }
          await fetchRoomOrders();
        }
      } else {
        throw new Error(data.detail || data.message || 'Failed to cancel item');
      }
    } catch (error) {
      console.error('Error cancelling item:', error);
      toast.dismiss(toastId);
      toast.error(error.message || 'Failed to cancel item');
    }
  };

  // ============== CANCEL ENTIRE ORDER ==============
  const handleClearAllItems = async () => {
    if (!window.confirm('Are you sure you want to cancel this entire order?')) {
      return;
    }

    const itemsToRemove = orderType === 'room' ? roomOrderItems : orderItems;

    if (itemsToRemove.length === 0) {
      toast.info('No items to cancel');
      return;
    }

    const toastId = toast.loading('Cancelling order...');

    try {
      if (orderType === 'room') {
        const orderIdToCancel = roomOrderId;

        if (!orderIdToCancel) {
          throw new Error('Order ID not found');
        }

        const response = await fetch(`${BQ_BASE_URL}/bq/api/orders/${orderIdToCancel}/cancel`, {
          method: 'POST',
          headers: getAuthJsonHeaders(),
          body: JSON.stringify({ reason: '' }),
        });

        const data = await response.json();

        if (response.ok) {
          toast.dismiss(toastId);
          toast.success(data.message || 'Room food order cancelled');

          setRoomOrderId(null);
          setRoomOrderItems([]);
          setRoomBillingInfo(null);
          setSelectedRoomOrder(null);
          setRoomSearchText('');
          setRoomNumber('');
          setRoomTableSearchText('');
          setTableNumber('');
          setCustomerName('');
          setPhoneNumber('');
          setGstNumber('');
          setSpecialRequest('');
          setCart([]);
          setIsInHouse(false);
          setInHouseReason('');
          setInHouseNotes('');

          await fetchRoomOrders();
          await fetchAvailableTablesForRoom();
          await fetchTables();
        } else {
          throw new Error(data.detail || data.message || 'Failed to cancel order');
        }
      } else {
        const orderIdToCancel = orderId;

        if (!orderIdToCancel) {
          throw new Error('Order ID not found');
        }

        const response = await fetch(
          `${BQ_BASE_URL}/bq/api/restaurant/orders/${orderIdToCancel}/cancel`,
          {
            method: 'POST',
            headers: getAuthJsonHeaders(),
            body: JSON.stringify({ reason: '' }),
          }
        );

        const data = await response.json();

        if (response.ok) {
          toast.dismiss(toastId);
          toast.success(data.message || 'Order cancelled');

          if (data.table_status === 'available') {
            toast.info(`Table ${data.table_number || ''} is now available.`);
          }

          setOrderId(null);
          setOrderItems([]);
          setBillingInfo(null);
          setCurrentOrder(null);
          setCart([]);
          setSpecialRequest('');
          setCustomerName('');
          setCustomerEmail('');
          setPhoneNumber('');
          setGstNumber('');
          setNumberOfGuests(1);
          setIsInHouse(false);
          setInHouseReason('');
          setInHouseNotes('');

          if (orderType === 'dining') {
            setTableNumber('');
            setTableSearchText('');
            await fetchTables();
          } else if (orderType === 'parcel') {
            setSelectedParcelOrder(null);
            setParcelSearchText('');
            setDeliveryAddress('');
            await fetchParcelOrders();
          }
        } else {
          throw new Error(data.detail || data.message || 'Failed to cancel order');
        }
      }
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.dismiss(toastId);
      toast.error(error.message || 'Failed to cancel order');
    }
  };

  const handleRemoveItem = (item) => {
    if (isItemCancelled(item)) {
      toast.info('This item is already cancelled');
      return;
    }

    const itemId = item.order_item_id;
    const orderIdToUse = orderType === 'room' ? roomOrderId : orderId;
    const itemName = item.food_name || 'this item';

    if (!itemId) {
      toast.error('Item ID not found');
      return;
    }

    if (!orderIdToUse) {
      toast.error('Order ID not found');
      return;
    }

    handleRemoveItemFromOrder(itemId, orderIdToUse, itemName);
  };

  const handleAddItemsToOrder = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    if (isOrderCancelled(currentOrder)) {
      toast.error('This order has been cancelled. Cannot add items.');
      return;
    }

    const items = cart.map((item) => ({
      food_id: item.id,
      quantity: item.quantity,
      special_request: item.specialRequest || '',
    }));

    const addItemsData = {
      items: items,
      kitchen_notes: specialRequest || null,
    };

    try {
      let response;
      let data;

      if (orderType === 'dining') {
        if (!tableNumber) {
          toast.error('Table number is required');
          return;
        }

        response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/tables/${tableNumber}/add-items`, {
          method: 'POST',
          headers: getAuthJsonHeaders(),
          body: JSON.stringify(addItemsData),
        });
      } else {
        if (!orderId) {
          toast.error('No order found. Please select a parcel order.');
          return;
        }

        response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders/${orderId}/add-items`, {
          method: 'POST',
          headers: getAuthJsonHeaders(),
          body: JSON.stringify(addItemsData),
        });
      }

      data = await response.json();

      if (response.ok) {
        toast.success('Items added successfully!');

        if (orderType === 'dining') {
          await fetchOrderByTable(tableNumber);
          await fetchTables();
          if (typeof fetchAvailableTablesForRoom === 'function') {
            await fetchAvailableTablesForRoom();
          }
        } else {
          await fetchParcelOrderDetails(orderId);
          await fetchParcelOrders();
        }

        setCart([]);
        setSpecialRequest('');
        setCustomerName('');
        setCustomerEmail('');
        setPhoneNumber('');
        setGstNumber('');
        setDeliveryAddress('');
        setSelectedCompany(null);
        setCompanySearchText('');
        setShowAdditionalDetails(false);
        clearSearch();
      } else {
        throw new Error(data.detail || 'Failed to add items');
      }
    } catch (error) {
      console.error('Error adding items:', error);
      toast.error(error.message || 'Failed to add items');
    }
  };

  const buildInHousePayload = () => {
    if (!isInHouse) return {};
    return {
      is_in_house: true,
      in_house_reason: inHouseReason,
      in_house_notes: inHouseNotes,
    };
  };

  const placeNewOrder = async () => {
    if (!propertyId) {
      toast.error('Property information not loaded. Please try again.');
      return;
    }

    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    if (isInHouse && (!inHouseReason || !inHouseNotes)) {
      toast.error('Please provide in-house reason and notes');
      return;
    }

    const items = cart.map((item) => ({
      food_id: item.id,
      quantity: item.quantity,
      special_request: item.specialRequest,
    }));

    const orderData = {
      order_type: 'dining',
      customer_name: customerName || null,
      customer_phone: phoneNumber || null,
      customer_email: customerEmail || null,
      table_number: tableNumber,
      number_of_guests: numberOfGuests,
      gst_number: gstNumber || null,
      delivery_address: null,
      items: items,
      special_request: specialRequest || null,
      kitchen_notes: null,
      discount_percent: discountPercent,
      service_charge_percent: 0,
      property_id: propertyId,
      ...buildInHousePayload(),
    };

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders`, {
        method: 'POST',
        headers: getAuthJsonHeaders(),
        body: JSON.stringify(orderData),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(
          isInHouse
            ? `In-house order placed (No GST) — Bill ${data.invoice_number || ''}`
            : `Order placed successfully!`
        );
        await fetchOrderByTable(tableNumber);
        await fetchTables();
        setCart([]);
        setSpecialRequest('');
        setDiscountPercent(0);
        setSelectedCompany(null);
        setCustomerName('');
        setCustomerEmail('');
        setPhoneNumber('');
        setGstNumber('');
        setSelectedCompany(null);
        setCompanySearchText('');
        setNumberOfGuests(1);
        setIsInHouse(false);
        setInHouseReason('');
        setInHouseNotes('');
        clearSearch();
      } else {
        throw new Error(data.detail || 'Failed to place order');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Failed to place order');
    }
  };

  const placeParcelOrder = async () => {
    if (!propertyId) {
      toast.error('Property information not loaded. Please try again.');
      return;
    }

    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    if (isInHouse && (!inHouseReason || !inHouseNotes)) {
      toast.error('Please provide in-house reason and notes');
      return;
    }

    const items = cart.map((item) => ({
      food_id: item.id,
      quantity: item.quantity,
      special_request: item.specialRequest,
    }));

    const orderData = {
      order_type: 'parcel',
      customer_name: customerName || null,
      customer_phone: phoneNumber || null,
      customer_email: customerEmail || null,
      table_number: null,
      number_of_guests: 1,
      gst_number: gstNumber || null,
      delivery_address: deliveryAddress || null,
      items: items,
      special_request: specialRequest || null,
      kitchen_notes: null,
      discount_percent: discountPercent,
      service_charge_percent: 0,
      property_id: propertyId,
      ...buildInHousePayload(),
    };

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders`, {
        method: 'POST',
        headers: getAuthJsonHeaders(),
        body: JSON.stringify(orderData),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(
          isInHouse
            ? `In-house parcel placed (No GST) — Bill ${data.invoice_number || ''}`
            : `Parcel order placed successfully!`
        );
        setOrderId(data.order_id);
        await fetchParcelOrderDetails(data.order_id);
        setCart([]);
        setSpecialRequest('');
        setDiscountPercent(0);
        setShowParcelDropdown(false);
        await fetchParcelOrders();
        setCustomerName('');
        setCustomerEmail('');
        setPhoneNumber('');
        setGstNumber('');
        setDeliveryAddress('');
        setSelectedCompany(null);
        setCompanySearchText('');
        setShowAdditionalDetails(false);
        setIsInHouse(false);
        setInHouseReason('');
        setInHouseNotes('');
        clearSearch();
      } else {
        throw new Error(data.detail || 'Failed to place parcel order');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Failed to place parcel order');
    }
  };

  const placeRoomOrder = async () => {
    if (!propertyId) {
      toast.error('Property information not loaded. Please try again.');
      return;
    }

    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    if (!roomNumber) {
      toast.error('Please enter room number');
      return;
    }

    if (isInHouse && (!inHouseReason || !inHouseNotes)) {
      toast.error('Please provide in-house reason and notes');
      return;
    }

    const items = cart.map((item) => ({
      foodid: item.id,
      quantity: item.quantity,
      specialRequest: item.specialRequest || null,
    }));

    const orderData = {
      items: items,
      orderType: 'room',
      customerName: customerName || 'Guest',
      phoneNumber: phoneNumber || null,
      tableNumber: tableNumber || null,
      roomNumber: roomNumber,
      parcelNumber: null,
      specialRequest: specialRequest || null,
      gstNumber: gstNumber || null,
      discount: discountPercent,
      ...buildInHousePayload(),
    };

    setIsPlacingOrder(true);
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/`, {
        method: 'POST',
        headers: getAuthJsonHeaders(),
        body: JSON.stringify(orderData),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(
          isInHouse ? `In-house room order placed (No GST)` : `Room order placed successfully!`
        );

        setRoomOrderId(null);
        setRoomOrderItems([]);
        setRoomBillingInfo(null);
        setSelectedRoomOrder(null);
        setRoomSearchText('');
        setRoomNumber('');
        setTableNumber('');
        setRoomTableSearchText('');
        setCustomerName('');
        setCustomerEmail('');
        setPhoneNumber('');
        setGstNumber('');
        setSpecialRequest('');
        setDiscountPercent(0);
        setCart([]);
        setShowRoomDropdown(false);
        setIsInHouse(false);
        setInHouseReason('');
        setInHouseNotes('');

        toast.success(`Order placed! Order ID: ${data.order_id || 'N/A'}`);

        await fetchRoomOrders();
        await fetchAvailableTablesForRoom();
        await fetchTables();
        clearSearch();
      } else {
        throw new Error(data.detail || 'Failed to place room order');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Failed to place room order');
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const createRoomOrderBilling = async (foodOrderId, bookingId) => {
    try {
      if (!bookingId) {
        console.error('Booking ID is required for billing');
        toast.error('Cannot create billing: Booking ID missing');
        return;
      }

      const url = `${BQ_BASE_URL}/bq/api/create_food_order_billing?bookingid=${bookingId}&food_order_id=${foodOrderId}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthJsonHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setRoomBillingInfo({
          billing_id: data.billingid,
          final_amount: parseFloat(data.finalamount),
          is_paid: false,
        });
        console.log('Room billing info set:', data);
        toast.success('Billing created successfully!');
      } else {
        const error = await response.json();
        console.error('Failed to create billing for room order:', error);
        toast.error(error.detail || 'Failed to create billing');
      }
    } catch (error) {
      console.error('Error creating room order billing:', error);
      toast.error('Failed to create billing');
    }
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    if (!propertyId) {
      toast.error('Loading property information. Please wait...');
      return;
    }

    if (discountPercent > 25) {
      toast.error('Discount cannot exceed 25%');
      return;
    }

    if (isInHouse && (!inHouseReason || !inHouseNotes)) {
      setDraftReason(inHouseReason || '');
      setDraftNotes(inHouseNotes || '');
      setShowInHouseModal(true);
      return;
    }

    if (orderType === 'dining') {
      if (!tableNumber) {
        toast.error('Please select a table');
        return;
      }

      if (orderId) {
        if (isOrderCancelled(currentOrder)) {
          toast.error('This order is cancelled. Cannot add more items.');
          return;
        }
        setIsAddingItems(true);
        try {
          await handleAddItemsToOrder();
        } finally {
          setIsAddingItems(false);
        }
      } else {
        setIsPlacingOrder(true);
        try {
          await placeNewOrder();
        } finally {
          setIsPlacingOrder(false);
        }
      }
      return;
    }

    if (orderType === 'parcel') {
      if (orderId && selectedParcelOrder) {
        if (isOrderCancelled(selectedParcelOrder)) {
          toast.error('This order is cancelled. Cannot add more items.');
          return;
        }
        setIsAddingItems(true);
        try {
          await handleAddItemsToOrder();
        } finally {
          setIsAddingItems(false);
        }
      } else {
        setIsPlacingOrder(true);
        try {
          await placeParcelOrder();
        } finally {
          setIsPlacingOrder(false);
        }
      }
      return;
    }

    if (orderType === 'room') {
      if (roomOrderId && selectedRoomOrder) {
        if (isOrderCancelled(selectedRoomOrder)) {
          toast.error('This order is cancelled. Cannot add more items.');
          return;
        }
        await handleAddItemsToRoomOrder();
      } else {
        setIsPlacingOrder(true);
        try {
          await placeRoomOrder();
        } finally {
          setIsPlacingOrder(false);
        }
      }
      return;
    }
  };

  const handleCashPayment = async () => {
    if (!orderId || !billingInfo) {
      toast.error('No order or billing information found');
      return;
    }

    try {
      const paymentData = {
        order_id: orderId,
        billing_id: billingInfo.billing_id,
        amount: billingInfo.final_amount,
        notes:
          orderType === 'dining'
            ? `Cash payment for table ${tableNumber}`
            : 'Cash payment for parcel order',
      };

      const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/payments/cash`, {
        method: 'POST',
        headers: getAuthJsonHeaders(),
        body: JSON.stringify(paymentData),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`Payment successful! Change due: ₹${data.change_due}`);
        setShowPaymentModal(false);
        if (orderType === 'dining') {
          setCurrentOrder(null);
          setOrderId(null);
          setOrderItems([]);
          setBillingInfo(null);
          setTableNumber('');
          setTableSearchText('');
          setCart([]);
          setSpecialRequest('');
          setCustomerName('');
          setCustomerEmail('');
          setPhoneNumber('');
          setGstNumber('');
          setNumberOfGuests(1);
          await fetchTables();
        } else {
          await fetchParcelOrderDetails(orderId);
          await fetchParcelOrders();
        }
      } else {
        throw new Error(data.detail || 'Payment failed');
      }
    } catch (error) {
      console.error('Cash payment error:', error);
      toast.error(error.message || 'Failed to process cash payment');
    }
  };

  const handleRazorpayPayment = async () => {
    if (!razorpayLoaded) {
      toast.error('Razorpay script is not loaded yet.');
      return;
    }

    if (!orderId || !billingInfo) {
      toast.error('No order or billing information found');
      return;
    }

    try {
      const apiUrl = `${BQ_BASE_URL}/bq/api/restaurant/payments/razorpay/create-order`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: getAuthJsonHeaders(),
        body: JSON.stringify({
          order_id: orderId,
          billing_id: billingInfo.billing_id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create payment order');
      }

      const orderData = await response.json();

      if (orderData && orderData.razorpay_order_id) {
        const options = {
          key: REACT_APP_RAZORPAY_KEY,
          amount: orderData.amount,
          currency: 'INR',
          name: 'Pagoda Hotel',
          description:
            orderType === 'dining' ? `Restaurant Order - Table ${tableNumber}` : 'Parcel Order',
          order_id: orderData.razorpay_order_id,
          handler: async function (response) {
            const paymentDetails = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            };

            try {
              const verificationResponse = await fetch(
                `${BQ_BASE_URL}/bq/api/restaurant/payments/razorpay/verify`,
                {
                  method: 'POST',
                  headers: getAuthJsonHeaders(),
                  body: JSON.stringify(paymentDetails),
                }
              );

              if (verificationResponse.ok) {
                toast.success('Payment successful and verified!');
                setShowPaymentModal(false);
                if (orderType === 'dining') {
                  setCurrentOrder(null);
                  setOrderId(null);
                  setOrderItems([]);
                  setBillingInfo(null);
                  setTableNumber('');
                  setTableSearchText('');
                  setCart([]);
                  setSpecialRequest('');
                  await fetchTables();
                } else {
                  await fetchParcelOrderDetails(orderId);
                  await fetchParcelOrders();
                }
              } else {
                toast.error('Payment verification failed.');
              }
            } catch (error) {
              console.error('Verification error:', error);
              toast.error('Error verifying payment. Please try again.');
            }
          },
          prefill: {
            name: customerName || 'Guest',
            email: customerEmail || 'guest@example.com',
            contact: phoneNumber || '9999999999',
          },
          theme: { color: '#3399cc' },
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Error processing payment. Please try again.');
    }
  };

  const handleRoomCashPayment = async () => {
    if (!roomOrderId || !roomBillingInfo) {
      toast.error('No room order or billing information found');
      return;
    }

    if (!selectedRoomOrder?.booking_id) {
      toast.error('Booking ID not found for this room order');
      return;
    }

    setIsProcessingPayment(true);
    try {
      const paymentData = {
        booking_id: selectedRoomOrder.booking_id,
        billing_id: roomBillingInfo.billing_id,
        amount: roomBillingInfo.final_amount,
      };

      const response = await fetch(`${BQ_BASE_URL}/bq/api/payments/cash`, {
        method: 'POST',
        headers: getAuthJsonHeaders(),
        body: JSON.stringify(paymentData),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`Payment successful!`);
        setShowPaymentModal(false);
        setRoomOrderId(null);
        setRoomBillingInfo(null);
        setRoomOrderItems([]);
        setSelectedRoomOrder(null);
        setRoomSearchText('');
        setRoomTableSearchText('');
        await fetchRoomOrders();
        await fetchAvailableTablesForRoom();
        await fetchTables();
        setCart([]);
        setSpecialRequest('');
      } else {
        throw new Error(data.detail || 'Payment failed');
      }
    } catch (error) {
      console.error('Cash payment error:', error);
      toast.error(error.message || 'Failed to process cash payment');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleRoomRazorpayPayment = async () => {
    if (!razorpayLoaded) {
      toast.error('Razorpay script is not loaded yet.');
      return;
    }

    if (!roomOrderId || !roomBillingInfo) {
      toast.error('No room order or billing information found');
      return;
    }

    if (!selectedRoomOrder?.booking_id) {
      toast.error('Booking ID not found for this room order');
      return;
    }

    setIsProcessingPayment(true);

    try {
      const apiUrl = `${BQ_BASE_URL}/bq/api/razorpay/create_payment_order?billingid=${roomBillingInfo.billing_id}&bookingid=${selectedRoomOrder.booking_id}&strict_target=true`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: getAuthJsonHeaders(),
        body: '',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create payment order');
      }

      const orderData = await response.json();

      if (orderData && orderData.razorpay_order_id) {
        const options = {
          key: REACT_APP_RAZORPAY_KEY,
          amount: orderData.amount,
          currency: 'INR',
          name: 'Pagoda Hotel',
          description: `Room Order - Room ${roomNumber}`,
          order_id: orderData.razorpay_order_id,
          handler: async function (response) {
            const paymentDetails = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            };

            try {
              const verificationResponse = await fetch(
                `${BQ_BASE_URL}/bq/api/razorpay/verify_payment`,
                {
                  method: 'POST',
                  headers: getAuthJsonHeaders(),
                  body: JSON.stringify(paymentDetails),
                }
              );

              if (verificationResponse.ok) {
                toast.success('Payment successful and verified!');
                setShowPaymentModal(false);
                setRoomOrderId(null);
                setRoomBillingInfo(null);
                setRoomOrderItems([]);
                setSelectedRoomOrder(null);
                setRoomSearchText('');
                setRoomTableSearchText('');
                await fetchRoomOrders();
                await fetchAvailableTablesForRoom();
                await fetchTables();
                setCart([]);
                setSpecialRequest('');
              } else {
                toast.error('Payment verification failed.');
              }
            } catch (error) {
              console.error('Verification error:', error);
              toast.error('Error verifying payment. Please try again.');
            } finally {
              setIsProcessingPayment(false);
            }
          },
          prefill: {
            name: customerName || 'Guest',
            email: customerEmail || 'guest@example.com',
            contact: phoneNumber || '9999999999',
          },
          theme: { color: '#3399cc' },
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        setIsProcessingPayment(false);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Error processing payment. Please try again.');
      setIsProcessingPayment(false);
    }
  };

  const handlePaymentOption = (method) => {
    setSelectedPaymentMethod(method);
    setShowPaymentModal(true);
  };

  const confirmPayment = async () => {
    try {
      if (orderType === 'dining' || orderType === 'parcel') {
        if (selectedPaymentMethod === 'cash') {
          await handleCashPayment();
        } else if (selectedPaymentMethod === 'razorpay') {
          await handleRazorpayPayment();
        }
      } else if (orderType === 'room') {
        if (selectedPaymentMethod === 'cash') {
          await handleRoomCashPayment();
        } else if (selectedPaymentMethod === 'razorpay') {
          await handleRoomRazorpayPayment();
        }
      }
    } finally {
      setSelectedPaymentMethod(null);
    }
  };

  const handlePrintInvoice = async () => {
    if (orderType === 'dining' || orderType === 'parcel') {
      if (!orderId) {
        toast.error('No order found');
        return;
      }

      setIsPrinting(true);
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/print/${orderId}`, {
          method: 'POST',
          headers: getAuthJsonHeaders(),
        });

        const data = await response.json();

        if (response.ok) {
          toast.success('Invoice printed successfully!');
        } else {
          toast.error(data.detail || 'Failed to print invoice');
        }
      } catch (error) {
        console.error('Print error:', error);
        toast.error('Failed to print invoice');
      } finally {
        setIsPrinting(false);
      }
    } else if (orderType === 'room') {
      if (!roomOrderId) {
        toast.error('No room order found');
        return;
      }

      setIsPrinting(true);
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/food/print/${roomOrderId}`, {
          method: 'POST',
          headers: getAuthJsonHeaders(),
        });

        const data = await response.json();

        if (response.ok) {
          toast.success(`Room invoice printed successfully!`);
        } else {
          toast.error(data.detail || 'Failed to print room invoice');
        }
      } catch (error) {
        console.error('Print error:', error);
        toast.error('Failed to print room invoice');
      } finally {
        setIsPrinting(false);
      }
    } else {
      toast.error('No order found');
    }
  };

  const filteredItems = menuItems.filter((item) => {
    if (item.status && item.status.toLowerCase() === 'delivered') {
      return false;
    }

    const categoryid = getCategoryIdFromSubCategoryId(item.subCategoryId);
    const matchesCategory = selectedCategory ? categoryid === selectedCategory : true;
    const matchesSubcategory = selectedSubcategory
      ? item.subCategoryId === selectedSubcategory
      : true;

    const searchLower = search.toLowerCase();
    const matchesTitle = item.title.toLowerCase().includes(searchLower);
    const matchesItemcode =
      !isNaN(search) && search.trim() !== '' ? item.itemcode === parseInt(search) : false;
    const matchesSearch = matchesTitle || matchesItemcode;

    return matchesCategory && matchesSubcategory && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading menu...</p>
        </div>
      </div>
    );
  }

  const activeOrderIsCancelled =
    orderType === 'dining' || orderType === 'parcel'
      ? isOrderCancelled(currentOrder) || isOrderCancelled(selectedParcelOrder)
      : orderType === 'room'
        ? isOrderCancelled(selectedRoomOrder)
        : false;

  const hasItemsInOrder = orderType === 'room' ? roomOrderItems.length > 0 : orderItems.length > 0;

  const allOrderItems = orderType === 'room' ? roomOrderItems : orderItems;
  const activeItemCount = allOrderItems.filter((it) => !isItemCancelled(it)).length;
  const cancelledItemCount = allOrderItems.filter((it) => isItemCancelled(it)).length;

  // ── IN-HOUSE ── true only when creating a NEW order (no existing order loaded)
  const isNewOrder =
    orderType === 'dining'
      ? !orderId
      : orderType === 'parcel'
        ? !orderId
        : orderType === 'room'
          ? !roomOrderId
          : true;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">Food & Beverages Management</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/core-modules/f&b-management/track-order-frontdesk')}
            className="btn "
          >
            Order Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/f&b-management/order-food')}
            className="btn font-bold text-black"
          >
            Order Food
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/f&b-management/dining-tables')}
            className="btn "
          >
            Dining Tables
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/f&b-management/track-order-kitchen')}
            className="btn"
          >
            Kitchen Display System(KDS)
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/f&b-management/order-invoicing')}
            className="btn text-gray-600 hover:text-black"
          >
            Billing & Invoicing
          </button>
        </div>
      </header>

      <div className="flex flex-col lg:flex-row">
        <div className="flex-1 overflow-y-auto" style={{ height: 'calc(100vh - 80px)' }}>
          <div className="p-2">
            <div className="sticky top-0 z-10 bg-white pt-2 pb-2">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name or item code.."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full px-5 py-3 pl-11 pr-12 border border-gray-200 rounded-lg transition-all bg-white shadow-sm"
                />
                <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-300 text-sm" />

                {isSupported && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isListening && activeSpeechTarget === 'search') {
                        stopSearchListening();
                      } else {
                        startSearchListening();
                      }
                    }}
                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-2 rounded-full transition-all z-10 ${
                      isListening && activeSpeechTarget === 'search'
                        ? 'text-red-500 bg-red-50 animate-pulse scale-110'
                        : 'text-gray-400 hover:text-black hover:bg-gray-100'
                    }`}
                    title={
                      isListening && activeSpeechTarget === 'search'
                        ? 'Stop listening'
                        : 'Search by voice'
                    }
                    type="button"
                  >
                    <FaMicrophone size={18} />
                  </button>
                )}

                {isListening && activeSpeechTarget === 'search' && (
                  <div className="absolute -bottom-6 left-0 right-0 text-xs text-red-500 font-medium text-center animate-pulse">
                    🎤 Listening for search... Speak now
                  </div>
                )}
              </div>
            </div>

            <div className="overflow-x-auto mb-2 pb-2 mt-2">
              <div className="flex gap-1 min-w-min">
                <button
                  onClick={() => {
                    setSelectedCategory(null);
                    setSelectedSubcategory(null);
                  }}
                  className={`px-3 py-2 text-[13px] lg:text-[13px] rounded-full transition-all whitespace-nowrap ${
                    selectedCategory === null
                      ? 'bg-black text-white'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-black'
                  }`}
                >
                  All
                </button>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => {
                      setSelectedCategory(category.id);
                      setSelectedSubcategory(null);
                    }}
                    className={`px-3 sm:px-3 py-2 text-[13px] lg:text-[14px] font-medium rounded-full transition-all whitespace-nowrap ${
                      selectedCategory === category.id
                        ? 'bg-black text-white'
                        : 'bg-white text-gray-600 border border-gray-200 hover:border-black'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>

            {selectedCategory &&
              subcategories.filter((s) => s.categoryid === selectedCategory).length > 0 && (
                <div className="overflow-x-auto mb-5 pb-2">
                  <div className="flex gap-2 min-w-min">
                    <button
                      onClick={() => setSelectedSubcategory(null)}
                      className={`px-3 sm:px-4 py-1.5 text-xs rounded-full transition-all whitespace-nowrap ${
                        selectedSubcategory === null
                          ? 'bg-black text-white'
                          : 'bg-white text-gray-500 border border-gray-200 hover:border-black'
                      }`}
                    >
                      All
                    </button>
                    {subcategories
                      .filter((sub) => sub.categoryid === selectedCategory)
                      .map((sub) => (
                        <button
                          key={sub.id}
                          onClick={() => setSelectedSubcategory(sub.id)}
                          className={`px-3 sm:px-4 py-1.5 text-xs rounded-full transition-all whitespace-nowrap ${
                            selectedSubcategory === sub.id
                              ? 'bg-black text-white'
                              : 'bg-white text-gray-500 border border-gray-200 hover:border-black'
                          }`}
                        >
                          {sub.name}
                        </button>
                      ))}
                  </div>
                </div>
              )}

            <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 gap-2">
              {filteredItems.map((item) => (
                <div
                  key={item.id}
                  className="group border border-gray-100 rounded-xl hover:shadow-md transition-all duration-300 bg-white overflow-hidden"
                >
                  <div className="relative h-40 bg-gray-50">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `
          <div class="flex items-center justify-center h-full bg-gray-100">
            <div class="text-center">
              <FaImage class="text-4xl text-gray-300 mx-auto mb-2" />
              <span class="text-gray-400 text-xs">No Image Uploaded</span>
            </div>
          </div>
        `;
                        }}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full bg-gray-100">
                        <div className="text-center">
                          <FaImage className="text-4xl text-gray-300 mx-auto mb-2" />
                          <span className="text-gray-400 text-xs">No Image Uploaded</span>
                        </div>
                      </div>
                    )}

                    <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-1 sm:gap-2">
                      {item.foodType === 'Veg' ? (
                        <FaLeaf className="text-green-600 bg-white p-1 rounded-full text-base sm:text-lg" />
                      ) : (
                        <FaDrumstickBite className="text-orange-600 bg-white p-1 rounded-full text-base sm:text-lg" />
                      )}

                      {item.itemcode > 0 && (
                        <span className="bg-purple-100 text-purple-800 text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full flex items-center gap-1">
                          <FaBarcode className="text-xs" />
                          Code: {item.itemcode}
                        </span>
                      )}
                    </div>

                    {!item.isActive && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="bg-white text-black px-3 py-1 rounded-full text-xs font-medium">
                          Unavailable
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="p-3 sm:p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-black text-sm">{item.title}</h3>

                      {item.value > 0 && item.value_type && (
                        <span className="bg-yellow-50 text-yellow-700 text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full flex items-center gap-1">
                          <FaTag className="text-xs" />
                          Tax:{item.value}
                          {item.value_type === 'P' ? '%' : ' ₹'}
                        </span>
                      )}
                    </div>

                    <p className="text-gray-400 text-xs sm:text-sm mb-2 line-clamp-2">
                      {item.description}
                    </p>

                    <div className="flex flex-wrap gap-1 sm:gap-2 mb-3">
                      {item.price_type && (
                        <span className="bg-blue-50 text-blue-700 text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full flex items-center gap-1">
                          <span className="font-bold text-black">₹{item.price}</span>/
                          {item.price_type}
                        </span>
                      )}
                    </div>

                    <button
                      onClick={() => handleAddToCart(item)}
                      disabled={!item.isActive}
                      className={`w-full py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                        item.isActive
                          ? justAddedItemId === item.id
                            ? 'bg-green-500 text-white'
                            : 'bg-black text-white hover:bg-gray-800'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {justAddedItemId === item.id ? 'Added!' : 'Add to Cart'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {filteredItems.length === 0 && (
              <div className="text-center py-20">
                <p className="text-gray-400">No items found</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Order Panel */}
        <div
          className="w-full lg:w-[480px] border-l border-gray-100 bg-white flex flex-col"
          style={{ height: 'calc(100vh - 80px)' }}
        >
          <div className="px-6 py-2 border-b border-gray-100">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <FaShoppingCart className="text-black text-lg" />
                <h2 className="font-semibold text-black">Place Order</h2>
              </div>
              {cart.length > 0 && (
                <button
                  onClick={clearAllFields}
                  className="text-white text-sm transition-all flex items-center gap-1 px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600"
                >
                  <FaTrash size={12} /> Clear All
                </button>
              )}
            </div>

            {activeOrderIsCancelled && (
              <div className="mb-3 rounded-xl border-2 border-red-200 bg-red-50 p-3 flex items-start gap-2">
                <FaBan className="text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-red-800">
                    This order has been cancelled
                  </p>
                  <p className="text-xs text-red-600 mt-0.5">
                    You can view the items below, but no changes are allowed.
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => {
                  setOrderType('dining');
                }}
                className={`py-2 text-sm rounded-lg transition-all ${
                  orderType === 'dining'
                    ? 'bg-black text-white'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <FaUsers className="inline mr-1 text-xs" /> Dine In
              </button>
              <button
                onClick={() => {
                  setOrderType('room');
                  setRoomOrderId(null);
                  setRoomBillingInfo(null);
                  setRoomOrderItems([]);
                  setSelectedRoomOrder(null);
                  setRoomSearchText('');
                }}
                className={`py-2 text-sm rounded-lg transition-all ${
                  orderType === 'room'
                    ? 'bg-black text-white'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <FaHotel className="inline mr-1 text-xs" /> Room
              </button>
              <button
                onClick={() => {
                  setOrderType('parcel');
                  setOrderId(null);
                  setBillingInfo(null);
                  setOrderItems([]);
                  setSelectedParcelOrder(null);
                  setParcelSearchText('');
                }}
                className={`py-2 text-sm rounded-lg transition-all ${
                  orderType === 'parcel'
                    ? 'bg-black text-white'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <FaBox className="inline mr-1 text-xs" /> Parcel
              </button>
            </div>

            {/* ── IN-HOUSE ── interactive checkbox only when creating a NEW order */}
            {isNewOrder && (
              <div className="mt-3">
                <label
                  className={`flex items-center justify-between px-3 py-2.5 rounded-lg border cursor-pointer transition-all ${
                    isInHouse
                      ? 'bg-amber-50 border-amber-300'
                      : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <FaUserShield
                      className={isInHouse ? 'text-amber-600' : 'text-gray-400'}
                      size={14}
                    />
                    <span
                      className={`text-sm font-medium ${
                        isInHouse ? 'text-amber-900' : 'text-gray-700'
                      }`}
                    >
                      In-house guest (no GST)
                    </span>
                  </div>
                  <input
                    type="checkbox"
                    checked={isInHouse}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setDraftReason(inHouseReason || '');
                        setDraftNotes(inHouseNotes || '');
                        setShowInHouseModal(true);
                      } else {
                        setIsInHouse(false);
                        setInHouseReason('');
                        setInHouseNotes('');
                      }
                    }}
                    className="w-4 h-4 accent-amber-600 cursor-pointer"
                  />
                </label>
              </div>
            )}

            {/* ── IN-HOUSE ── read-only badge only when the LOADED order is in-house */}
            {!isNewOrder && isInHouse && (
              <div className="mt-3 flex items-center gap-2 px-3 py-2.5 rounded-lg border bg-amber-50 border-amber-300">
                <FaUserShield className="text-amber-600" size={14} />
                <span className="text-sm font-medium text-amber-900">In-house guest (no GST)</span>
                <span className="ml-auto text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full font-medium">
                  Locked
                </span>
              </div>
            )}

            {orderType === 'dining' && (
              <div className="mt-4 relative">
                <div className="relative">
                  <FaTable className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm z-10" />
                  <input
                    type="text"
                    placeholder="Select or search table..."
                    value={tableSearchText}
                    onChange={(e) => {
                      setTableSearchText(e.target.value);
                      setShowTableDropdown(true);
                    }}
                    onFocus={() => setShowTableDropdown(true)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
                  />
                </div>

                {showTableDropdown && filteredTables.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredTables.map((table) => (
                      <button
                        key={table.id}
                        onClick={() => handleTableSelect(table)}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between border-b border-gray-100 last:border-0"
                      >
                        <div className="flex items-center gap-2">
                          <FaCircle className={`${getStatusColor(table.status)} text-xs`} />
                          <span className="font-medium">{table.table_number}</span>
                          <span className="text-sm text-gray-500">({table.table_name})</span>
                          <span className="text-xs text-gray-400">Cap: {table.capacity}</span>
                        </div>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${
                            table.status === 'available'
                              ? 'bg-green-100 text-green-700'
                              : table.status === 'occupied'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {getStatusText(table.status)}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {orderType === 'room' && (
              <div className="mt-4">
                <div className="relative">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <FaHotel className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm z-10" />
                      <input
                        type="text"
                        placeholder="Search room number or enter new room number..."
                        value={roomSearchText}
                        onChange={(e) => {
                          const value = e.target.value;
                          setRoomSearchText(value);
                          setShowRoomDropdown(true);

                          if (/^\d+$/.test(value)) {
                            setRoomNumber(value);
                            setSelectedRoomOrder(null);
                            setRoomOrderId(null);
                            setRoomOrderItems([]);
                            setRoomBillingInfo(null);
                            setCustomerName('');
                            setPhoneNumber('');
                            setSpecialRequest('');
                          }
                        }}
                        onFocus={() => {
                          setShowRoomDropdown(true);
                        }}
                        onBlur={() => {
                          setTimeout(() => {
                            setShowRoomDropdown(false);
                          }, 200);
                        }}
                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
                      />
                    </div>
                    <button
                      onClick={handleNewRoomOrder}
                      className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-all text-sm whitespace-nowrap"
                    >
                      + New Order
                    </button>
                  </div>

                  {showRoomDropdown && filteredRoomOrders.length > 0 && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredRoomOrders.map((order) => (
                        <button
                          key={order.id || order.order_id}
                          onClick={() => {
                            handleRoomSelect(order);
                            setShowRoomDropdown(false);
                          }}
                          className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="font-medium text-sm">Room {order.room_number}</div>
                              <div className="text-xs text-gray-500">
                                {order.customer_name || 'Guest'}
                                {order.table_number && ` • Table ${order.table_number}`}
                                {order.is_in_house && (
                                  <span className="ml-2 inline-block text-amber-600 font-medium">
                                    • IN-HOUSE
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-sm">
                                ₹{order.totalprice || order.total_amount || 0}
                              </div>
                              <div
                                className={`text-xs capitalize ${
                                  order.status === 'Pending' || order.order_status === 'Pending'
                                    ? 'text-yellow-600'
                                    : order.status === 'Served' || order.order_status === 'Served'
                                      ? 'text-blue-600'
                                      : 'text-green-600'
                                }`}
                              >
                                {order.status || order.order_status || 'Pending'}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-3 space-y-2">
                  <button
                    onClick={() => setShowAdditionalDetails(!showAdditionalDetails)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all"
                  >
                    <span className="text-sm font-medium text-gray-800">
                      Additional Details (Optional)
                    </span>
                    {showAdditionalDetails ? (
                      <FaChevronUp size={12} />
                    ) : (
                      <FaChevronDown size={12} />
                    )}
                  </button>

                  {showAdditionalDetails && (
                    <div className="mt-2 space-y-2 animate-fadeIn">
                      <div className="relative">
                        <FaTable className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm z-10" />
                        <input
                          type="text"
                          placeholder="Select or search table number..."
                          value={roomTableSearchText}
                          onChange={(e) => {
                            setRoomTableSearchText(e.target.value);
                            setShowRoomTableDropdown(true);
                            if (e.target.value) {
                              setTableNumber(e.target.value);
                            }
                          }}
                          onFocus={() => setShowRoomTableDropdown(true)}
                          onBlur={() => {
                            setTimeout(() => {
                              setShowRoomTableDropdown(false);
                            }, 200);
                          }}
                          className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
                        />

                        {showRoomTableDropdown && filteredRoomTables.length > 0 && (
                          <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                            {filteredRoomTables.map((table) => (
                              <button
                                key={table.id}
                                onClick={() => {
                                  setTableNumber(table.table_number);
                                  setRoomTableSearchText(
                                    `${table.table_number} (${table.table_name || 'Table'})`
                                  );
                                  setShowRoomTableDropdown(false);
                                }}
                                className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-center justify-between"
                              >
                                <div>
                                  <span className="font-medium text-sm">{table.table_number}</span>
                                  <span className="text-xs text-gray-500 ml-2">
                                    {table.table_name}
                                  </span>
                                </div>
                                <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                  Available
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="relative">
                        <FaIdCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                        <input
                          type="text"
                          placeholder="GST Number"
                          value={gstNumber}
                          onChange={(e) => setGstNumber(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {orderType === 'parcel' && (
              <div className="mt-4 relative">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <FaList className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm z-10" />
                    <input
                      type="text"
                      placeholder="Search parcel order by number, name or phone..."
                      value={parcelSearchText}
                      onChange={(e) => {
                        setParcelSearchText(e.target.value);
                        setShowParcelDropdown(true);
                      }}
                      onFocus={() => setShowParcelDropdown(true)}
                      className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
                    />
                  </div>
                  <button
                    onClick={handleNewParcelOrder}
                    className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-all text-sm whitespace-nowrap"
                  >
                    + New Order
                  </button>
                </div>

                {showParcelDropdown && filteredParcelOrders.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredParcelOrders.map((order) => (
                      <button
                        key={order.order_id}
                        onClick={() => handleParcelSelect(order)}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-medium text-sm">{order.parcel_number}</div>
                            <div className="text-xs text-gray-500">
                              {order.customer_name || 'Guest'}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-sm">₹{order.total_amount}</div>
                            <div
                              className={`text-xs ${
                                order.order_status === 'pending'
                                  ? 'text-yellow-600'
                                  : 'text-blue-600'
                              }`}
                            >
                              {order.order_status}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Order Items Summary */}
            {((orderItems.length > 0 && (orderType === 'dining' || orderType === 'parcel')) ||
              ((roomOrderItems.length > 0 || selectedRoomOrder) && orderType === 'room')) && (
              <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <button
                  onClick={() => setShowOrderItems(!showOrderItems)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <FaList className="text-gray-400" />
                    <span className="font-semibold text-sm">Order Items</span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                      {activeItemCount} active
                    </span>
                    {cancelledItemCount > 0 && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <FaBan size={9} /> {cancelledItemCount} cancelled
                      </span>
                    )}
                    {showOrderItems ? (
                      <FaChevronUp size={12} className="text-gray-400" />
                    ) : (
                      <FaChevronDown size={12} className="text-gray-400" />
                    )}
                  </div>
                  {(billingInfo || roomBillingInfo) && (
                    <span className="font-bold text-sm text-black">
                      ₹{billingInfo?.final_amount || roomBillingInfo?.final_amount || 0}
                    </span>
                  )}
                </button>

                {showOrderItems && (
                  <div className="border-t border-gray-100">
                    <div className="p-3 space-y-3 max-h-64 overflow-y-auto">
                      {orderType === 'room' && roomOrderItems.length === 0 && (
                        <div className="text-xs text-gray-500 text-center py-4">
                          This order has no items to show. They may all have been delivered already.
                        </div>
                      )}
                      {(orderType === 'room' ? roomOrderItems : orderItems).map((item, idx) => {
                        const cancelled = isItemCancelled(item);
                        const pendingQty =
                          pendingQuantities[item.order_item_id] !== undefined
                            ? pendingQuantities[item.order_item_id]
                            : item.quantity || 1;

                        const isQuantityChanged =
                          !cancelled &&
                          pendingQuantities[item.order_item_id] !== undefined &&
                          pendingQuantities[item.order_item_id] !== item.quantity;

                        return (
                          <div
                            key={idx}
                            className={`rounded-lg p-3 border transition-all ${
                              cancelled
                                ? 'bg-red-50/40 border-red-200 opacity-75'
                                : 'bg-gray-50 border-gray-100 hover:border-gray-200'
                            }`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span
                                    className={`font-medium text-sm ${
                                      cancelled ? 'text-gray-400 line-through' : 'text-gray-800'
                                    }`}
                                  >
                                    {item.food_name || item.description || 'Item'}
                                  </span>
                                  {cancelled && (
                                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full flex items-center gap-1 font-semibold">
                                      <FaBan size={9} /> CANCELLED
                                    </span>
                                  )}
                                  {item.is_complimentary && (
                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                      Complimentary
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-3 flex-wrap">
                                <span
                                  className={`font-semibold text-sm ${
                                    cancelled ? 'text-gray-400 line-through' : ''
                                  }`}
                                >
                                  ₹{((item.unit_price || 0) * (item.quantity || 1)).toFixed(2)}
                                </span>
                                <button
                                  onClick={() => handleUpdateQuantityWithButton(item)}
                                  disabled={cancelled || !isQuantityChanged}
                                  className={`px-3 py-1 rounded-lg transition-all text-xs font-medium flex items-center gap-1 ${
                                    cancelled
                                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                      : isQuantityChanged
                                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                  }`}
                                  title={
                                    cancelled ? 'Cannot update a cancelled item' : 'Update quantity'
                                  }
                                >
                                  <FaCheckCircle size={12} />
                                  Update
                                </button>
                                {/* ── KITCHEN INSTRUCTIONS ── button for ORDER items */}
                                <button
                                  onClick={() => handleOpenKitchenInstruction(item)}
                                  disabled={cancelled}
                                  className={`px-3 py-1 rounded-lg transition-all text-xs font-medium flex items-center gap-1 ${
                                    cancelled
                                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                      : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                                  }`}
                                  title={
                                    cancelled
                                      ? 'Cannot add instructions to a cancelled item'
                                      : 'Send kitchen instruction for this item'
                                  }
                                >
                                  <FaUtensils size={12} />
                                  Instructions
                                </button>
                              </div>
                            </div>

                            {item.special_request && (
                              <div className="mb-2 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-2 py-1 flex items-start gap-1">
                                <FaUtensils size={10} className="mt-0.5 flex-shrink-0" />
                                <span>
                                  <strong>Instruction:</strong> {item.special_request}
                                </span>
                              </div>
                            )}

                            <div className="flex items-center justify-between mt-2">
                              <div className="flex items-center gap-3">
                                <div
                                  className={`flex items-center gap-2 rounded-lg border px-2 py-1 ${
                                    cancelled
                                      ? 'bg-gray-100 border-gray-200 opacity-60'
                                      : 'bg-white border-gray-200'
                                  }`}
                                >
                                  <button
                                    onClick={() => {
                                      if (cancelled) {
                                        toast.info('This item is cancelled');
                                        return;
                                      }
                                      const currentQty =
                                        pendingQuantities[item.order_item_id] !== undefined
                                          ? pendingQuantities[item.order_item_id]
                                          : item.quantity || 1;
                                      if (currentQty > 1) {
                                        handlePendingQuantityChange(
                                          item.order_item_id,
                                          currentQty - 1
                                        );
                                      } else {
                                        if (
                                          window.confirm(
                                            `Cancel ${item.food_name || 'this item'} from order?`
                                          )
                                        ) {
                                          handleRemoveItem(item);
                                        }
                                      }
                                    }}
                                    disabled={cancelled}
                                    className={`w-7 h-7 flex items-center justify-center rounded transition-all ${
                                      cancelled
                                        ? 'text-gray-300 cursor-not-allowed'
                                        : 'hover:bg-gray-100 text-gray-600'
                                    }`}
                                    title="Decrease quantity"
                                  >
                                    <FaMinus size={11} />
                                  </button>
                                  <input
                                    type="number"
                                    min="1"
                                    max="99"
                                    value={pendingQty}
                                    onChange={(e) => {
                                      if (cancelled) return;
                                      const val = parseInt(e.target.value);
                                      if (!isNaN(val) && val > 0) {
                                        handlePendingQuantityChange(item.order_item_id, val);
                                      }
                                    }}
                                    disabled={cancelled}
                                    className={`w-10 text-center text-sm font-semibold border-0 focus:outline-none bg-transparent ${
                                      cancelled ? 'text-gray-400' : ''
                                    }`}
                                  />
                                  <button
                                    onClick={() => {
                                      if (cancelled) {
                                        toast.info('This item is cancelled');
                                        return;
                                      }
                                      const currentQty =
                                        pendingQuantities[item.order_item_id] !== undefined
                                          ? pendingQuantities[item.order_item_id]
                                          : item.quantity || 1;
                                      handlePendingQuantityChange(
                                        item.order_item_id,
                                        currentQty + 1
                                      );
                                    }}
                                    disabled={cancelled}
                                    className={`w-7 h-7 flex items-center justify-center rounded transition-all ${
                                      cancelled
                                        ? 'text-gray-300 cursor-not-allowed'
                                        : 'hover:bg-gray-100 text-gray-600'
                                    }`}
                                    title="Increase quantity"
                                  >
                                    <FaPlus size={11} />
                                  </button>
                                </div>

                                <button
                                  onClick={() => {
                                    if (cancelled) {
                                      toast.info('This item is already cancelled');
                                      return;
                                    }
                                    if (
                                      window.confirm(
                                        `Cancel ${item.food_name || 'this item'} from order?`
                                      )
                                    ) {
                                      handleRemoveItem(item);
                                    }
                                  }}
                                  disabled={cancelled}
                                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all text-xs font-medium ${
                                    cancelled
                                      ? 'text-gray-400 cursor-not-allowed'
                                      : 'text-red-600 hover:bg-red-50'
                                  }`}
                                >
                                  {cancelled ? <FaBan size={11} /> : <FaTrash size={11} />}
                                  {cancelled ? 'Cancelled' : 'Cancel Item'}
                                </button>
                              </div>

                              <div className="flex items-center gap-2">
                                <span
                                  className={`text-xs ${
                                    cancelled ? 'text-gray-400' : 'text-gray-500'
                                  }`}
                                >
                                  Qty: {item.quantity || 1}
                                </span>
                                {isQuantityChanged && (
                                  <span className="text-xs text-blue-600 font-medium">
                                    → {pendingQty}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {activeOrderIsCancelled ? (
                      <div className="border-t border-red-200 px-3 py-2 bg-red-50 flex items-center justify-center gap-2">
                        <FaBan className="text-red-600" size={12} />
                        <span className="text-xs text-red-700 font-semibold">
                          Order cancelled — no further actions allowed
                        </span>
                      </div>
                    ) : (
                      <>
                        {(orderType === 'dining' || orderType === 'parcel') &&
                          orderItems.length > 0 && (
                            <div className="border-t border-gray-100 px-3 py-2 bg-gray-50 flex justify-between items-center">
                              <button
                                onClick={handleClearAllItems}
                                className="text-xs text-red-600 hover:text-red-800 font-medium flex items-center gap-1 transition-colors"
                              >
                                <FaTrash size={10} />
                                Cancel Entire Order
                              </button>
                              <span className="text-xs text-gray-500">
                                {orderItems.length} items in order
                              </span>
                            </div>
                          )}

                        {orderType === 'room' && roomOrderItems.length > 0 && (
                          <div className="border-t border-gray-100 px-3 py-2 bg-gray-50 flex justify-between items-center">
                            <button
                              onClick={handleClearAllItems}
                              className="text-xs text-red-600 hover:text-red-800 font-medium flex items-center gap-1 transition-colors"
                            >
                              <FaTrash size={10} />
                              Cancel Entire Order
                            </button>
                            <span className="text-xs text-gray-500">
                              {roomOrderItems.length} items in order
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Customer Details for Dining & Parcel with Company Search INSIDE */}
            {(orderType === 'dining' || orderType === 'parcel') && !orderId && (
              <div className="mt-2">
                <button
                  onClick={() => setShowAdditionalDetails(!showAdditionalDetails)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all"
                >
                  <span className="text-sm font-medium text-gray-800">
                    Customer Details (Optional)
                  </span>
                  {showAdditionalDetails ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                </button>

                {showAdditionalDetails && (
                  <div className="mt-2 space-y-2 animate-fadeIn">
                    <div className="relative" ref={companyDropdownRef}>
                      <div className="relative">
                        <FaBuilding className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm z-10" />
                        <input
                          type="text"
                          placeholder="Search company name..."
                          value={companySearchText}
                          onChange={(e) => {
                            setCompanySearchText(e.target.value);
                            setSelectedCompany(null);
                            if (!e.target.value) {
                              setGstNumber('');
                            }
                          }}
                          onFocus={() => {
                            if (companySearchText.length > 1) {
                              setShowCompanyDropdown(true);
                            }
                          }}
                          className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
                        />
                      </div>

                      {showCompanyDropdown && filteredCompanies.length > 0 && (
                        <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {filteredCompanies.map((company) => (
                            <button
                              key={company.id}
                              onClick={() => handleCompanySelect(company)}
                              className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="font-medium text-sm">{company.companyname}</div>
                                  {company.companyid && (
                                    <div className="text-xs text-gray-400">
                                      ID: {company.companyid}
                                    </div>
                                  )}
                                </div>
                                {company.gstnumber && (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                    GST: {company.gstnumber}
                                  </span>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="relative">
                      <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                      <input
                        type="text"
                        placeholder="Customer name"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
                      />
                    </div>

                    <div className="relative">
                      <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                      <input
                        type="email"
                        placeholder="Email address"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
                      />
                    </div>

                    <div className="relative">
                      <FaPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                      <input
                        type="tel"
                        placeholder="Phone number"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
                      />
                    </div>

                    <div className="relative">
                      <FaIdCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                      <input
                        type="text"
                        placeholder="GST number"
                        value={gstNumber}
                        onChange={(e) => setGstNumber(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Number of Guests for Dining */}
            {orderType === 'dining' && !orderId && (
              <div className="mt-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FaUsers className="text-gray-400 text-sm" />
                    <span className="text-sm text-gray-600">Number of guests</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleDecreaseGuests}
                      className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center hover:border-black transition-all"
                    >
                      <FaMinus size={10} />
                    </button>
                    <span className="text-sm font-medium w-6 text-center">{numberOfGuests}</span>
                    <button
                      onClick={handleIncreaseGuests}
                      className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center hover:border-black transition-all"
                    >
                      <FaPlus size={10} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Cart Items with per-item Instructions button */}
          <div className="flex-1 overflow-y-auto px-6 py-2">
            {cart.length === 0 ? (
              <div className="text-center py-5">
                <FaShoppingCart className="text-4xl text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">Cart is empty</p>
              </div>
            ) : (
              <div className="space-y-4">
                {cart.map((item) => (
                  <div key={item.id} className="border-b border-gray-100 pb-4">
                    <div className="flex justify-between mb-2">
                      <div>
                        <h4 className="font-medium text-black text-sm">{item.title}</h4>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <span className="text-gray-400 text-xs">₹{item.price}</span>
                          {item.price_type && (
                            <span className="text-gray-400 text-xs">/{item.price_type}</span>
                          )}
                          {isInHouse ? (
                            <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">
                              NO GST
                            </span>
                          ) : (
                            item.value > 0 &&
                            item.value_type && (
                              <span className="text-gray-400 text-xs">
                                Tax: {item.value}
                                {item.value_type === 'P' ? '%' : ' ₹'}
                              </span>
                            )
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveFromCart(item.id)}
                        className="text-gray-300 hover:text-black"
                      >
                        <FaTimes size={12} />
                      </button>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleDecreaseQuantity(item.id)}
                          className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center hover:border-black transition-all"
                        >
                          <FaMinus size={10} />
                        </button>
                        <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                        <button
                          onClick={() => handleIncreaseQuantity(item.id)}
                          className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center hover:border-black transition-all"
                        >
                          <FaPlus size={10} />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">₹{item.price * item.quantity}</span>
                        {/* ── KITCHEN INSTRUCTIONS ── button for CART items */}
                        <button
                          onClick={() => handleOpenCartItemInstruction(item)}
                          className={`px-2 py-1 rounded-lg transition-all text-xs font-medium flex items-center gap-1 ${
                            item.specialRequest
                              ? 'bg-amber-500 text-white hover:bg-amber-600'
                              : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                          }`}
                          title={
                            item.specialRequest
                              ? 'Edit kitchen instruction'
                              : 'Add kitchen instruction'
                          }
                        >
                          <FaUtensils size={11} />
                          {item.specialRequest ? 'Edit' : 'Instructions'}
                        </button>
                      </div>
                    </div>

                    {/* ── KITCHEN INSTRUCTIONS ── show saved instruction under cart item */}
                    {item.specialRequest && (
                      <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-2 py-1 flex items-start gap-1">
                        <FaUtensils size={10} className="mt-0.5 flex-shrink-0" />
                        <span>
                          <strong>Instruction:</strong> {item.specialRequest}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {cart.length > 0 && (
            <div className="border-t border-gray-100 px-6 py-2">
              <div className="relative">
                <textarea
                  placeholder="Special instructions (optional)..."
                  value={specialRequest}
                  onChange={(e) => setSpecialRequest(e.target.value)}
                  rows={1}
                  className="w-full px-3 py-2 pr-12 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black resize-none"
                />
                {isSupported && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isListening && activeSpeechTarget === 'special') {
                        stopSpecialListening();
                      } else {
                        startSpecialListening();
                      }
                    }}
                    className={`absolute right-2 bottom-2 p-1.5 rounded-full transition-all z-10 ${
                      isListening && activeSpeechTarget === 'special'
                        ? 'text-red-500 bg-red-50 animate-pulse scale-110'
                        : 'text-gray-400 hover:text-black hover:bg-gray-100'
                    }`}
                    title={
                      isListening && activeSpeechTarget === 'special'
                        ? 'Stop listening'
                        : 'Add special instructions by voice'
                    }
                    type="button"
                  >
                    <FaMicrophone size={16} />
                  </button>
                )}
                {isListening && activeSpeechTarget === 'special' && (
                  <div className="absolute -bottom-5 left-0 right-0 text-xs text-red-500 font-medium text-center animate-pulse">
                    🎤 Listening for instructions... Speak now
                  </div>
                )}
              </div>
              {!orderId && !roomOrderId && (
                <div className="mt-1">
                  <div className="flex items-center gap-3">
                    <div className="flex-[2]">
                      <label className="text-xs text-gray-500 font-medium block mb-1">
                        Discount (%)
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={discountPercent || ''}
                          onChange={(e) => {
                            const rawValue = e.target.value;
                            const cleanValue = rawValue.replace(/[^0-9.]/g, '');

                            let processedValue = cleanValue;
                            if (
                              processedValue.length > 1 &&
                              processedValue.startsWith('0') &&
                              processedValue[1] !== '.'
                            ) {
                              processedValue = processedValue.replace(/^0+/, '');
                            }

                            if (processedValue === '') {
                              setDiscountPercent(0);
                              return;
                            }

                            const value = parseFloat(processedValue);
                            if (isNaN(value)) return;

                            if (value > 25) {
                              toast.error('Discount cannot exceed 25%');
                              setDiscountPercent(25);
                              return;
                            }
                            if (value < 0) {
                              setDiscountPercent(0);
                              return;
                            }
                            setDiscountPercent(value);
                          }}
                          className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-black ${
                            discountError ? 'border-red-500' : 'border-gray-200'
                          }`}
                          placeholder="Enter discount %"
                          inputMode="decimal"
                        />
                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">
                          Max 25%
                        </span>
                      </div>
                      {discountError && (
                        <p className="text-red-500 text-xs mt-1">{discountError}</p>
                      )}
                    </div>
                    {discountAmount > 0 && (
                      <div className="flex-1">
                        <label className="text-xs text-gray-500 font-medium block mb-1">
                          Discount Amount
                        </label>
                        <div className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm font-semibold text-green-700">
                          -₹{discountAmount.toFixed(2)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-2 mt-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal (Food)</span>
                  <span>₹{totalAmount.toFixed(2)}</span>
                </div>

                {!orderId && !roomOrderId && discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount ({discountPercent}%)</span>
                    <span>-₹{discountAmount.toFixed(2)}</span>
                  </div>
                )}

                {!orderId && !roomOrderId ? (
                  <div className="flex justify-between text-sm border-t border-dashed border-gray-200 pt-1">
                    <span className="text-gray-600 font-medium">Taxable Amount</span>
                    <span className="font-medium">₹{taxableAmount.toFixed(2)}</span>
                  </div>
                ) : (
                  <div className="flex justify-between text-sm border-t border-dashed border-gray-200 pt-1">
                    <span className="text-gray-600 font-medium">Taxable Amount</span>
                    <span className="font-medium">₹{totalAmount.toFixed(2)}</span>
                  </div>
                )}

                {isInHouse ? (
                  <div className="flex justify-between text-sm items-center bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
                    <span className="text-amber-900 font-medium flex items-center gap-1">
                      <FaUserShield size={11} />
                      Taxes
                    </span>
                    <span className="text-amber-900 font-semibold">Exempt (In-House)</span>
                  </div>
                ) : orderType === 'dining' && taxes > 0 ? (
                  <>
                    <div className="flex justify-between text-sm items-center">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">Taxes (incl. GST)</span>
                        <button
                          onClick={() => setShowTaxBreakdown(!showTaxBreakdown)}
                          className="text-gray-400 hover:text-black transition-colors"
                          title="View tax breakdown"
                        >
                          {showTaxBreakdown ? (
                            <FaChevronUp size={10} />
                          ) : (
                            <FaChevronDown size={10} />
                          )}
                        </button>
                      </div>
                      <span>₹{taxes.toFixed(2)}</span>
                    </div>

                    {showTaxBreakdown && (
                      <div className="ml-4 pl-2 border-l-2 border-gray-200 space-y-1 mt-1 animate-fadeIn">
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>CGST</span>
                          <span>₹{cgstAmount.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>SGST</span>
                          <span>₹{sgstAmount.toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Taxes</span>
                    <span>₹{taxes.toFixed(2)}</span>
                  </div>
                )}

                <div className="border-t border-gray-200 pt-2 mt-2">
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Total</span>
                    <span
                      className={
                        !orderId && !roomOrderId && discountAmount > 0 ? 'text-green-700' : ''
                      }
                    >
                      {!orderId && !roomOrderId
                        ? `₹${finalTotalWithDiscount.toFixed(2)}`
                        : `₹${(totalAmount + taxes).toFixed(2)}`}
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={handlePlaceOrder}
                disabled={
                  isPlacingOrder || isAddingItems || !!discountError || activeOrderIsCancelled
                }
                className={`w-full py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 mt-3 ${
                  isInHouse
                    ? 'bg-amber-600 text-white hover:bg-amber-700'
                    : 'bg-black text-white hover:bg-gray-800'
                } ${
                  isPlacingOrder || isAddingItems || discountError || activeOrderIsCancelled
                    ? 'opacity-50 cursor-not-allowed'
                    : ''
                }`}
              >
                {isPlacingOrder || isAddingItems ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : activeOrderIsCancelled ? (
                  <>
                    <FaBan size={14} /> Order Cancelled
                  </>
                ) : (
                  <>
                    {(orderId && (orderType === 'parcel' ? selectedParcelOrder : orderId)) ||
                    roomOrderId
                      ? 'Add Items to Order'
                      : isInHouse
                        ? 'Place In-House Order'
                        : 'Place Order'}
                    <FaArrowRight size={14} />
                  </>
                )}
              </button>
            </div>
          )}

          {/* Payment Options Section */}
          {((orderType === 'dining' || orderType === 'parcel') &&
            orderId &&
            billingInfo &&
            !billingInfo.is_paid) ||
          (orderType === 'room' &&
            roomOrderId &&
            roomBillingInfo &&
            !roomBillingInfo.is_paid &&
            selectedRoomOrder?.booking_id) ? (
            <div className="border-t border-gray-100 px-6 py-3 bg-gray-50">
              <h3 className="font-semibold text-sm mb-3 text-gray-700">Complete Payment</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePaymentOption('cash')}
                  disabled={isProcessingPayment || activeOrderIsCancelled}
                  className={`flex-1 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-all flex items-center justify-center gap-2 text-sm ${
                    isProcessingPayment || activeOrderIsCancelled
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }`}
                >
                  {isProcessingPayment ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <FaMoneyBill /> Cash
                    </>
                  )}
                </button>
                <button
                  onClick={() => handlePaymentOption('razorpay')}
                  disabled={isProcessingPayment || activeOrderIsCancelled}
                  className={`flex-1 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all flex items-center justify-center gap-2 text-sm ${
                    isProcessingPayment || activeOrderIsCancelled
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }`}
                >
                  {isProcessingPayment ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <FaDigitalOcean /> Digital
                    </>
                  )}
                </button>
                <button
                  onClick={handlePrintInvoice}
                  disabled={isPrinting}
                  className={`flex-1 py-2 bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-800 transition-all flex items-center justify-center gap-2 text-sm ${
                    isPrinting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isPrinting ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <FaFileInvoice /> Print
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : null}

          {/* Payment Confirmation Modal */}
          {showPaymentModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
                <h2 className="text-xl font-bold mb-4">Confirm Payment</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Order ID</label>
                    <input
                      type="text"
                      value={(orderId || roomOrderId || '').slice(0, 8)}
                      disabled
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Total Amount</label>
                    <input
                      type="text"
                      value={`₹${billingInfo?.final_amount || roomBillingInfo?.final_amount || 0}`}
                      disabled
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Payment Method</label>
                    <input
                      type="text"
                      value={selectedPaymentMethod === 'cash' ? 'Cash' : 'Razorpay'}
                      disabled
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                    />
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => setShowPaymentModal(false)}
                      className="flex-1 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmPayment}
                      disabled={isProcessingPayment}
                      className={`flex-1 py-2 bg-black text-white rounded-lg hover:bg-gray-800 ${
                        isProcessingPayment ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {isProcessingPayment ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                      ) : (
                        'Confirm Payment'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── IN-HOUSE ── Reason + Notes Modal */}
          {showInHouseModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <FaExclamationTriangle className="text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Mark as In-House Guest</h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      This order will be exempt from GST and printed as an "Internal Bill — Not a
                      Tax Invoice".
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1.5 text-gray-700">
                      Reason <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={draftReason}
                      onChange={(e) => setDraftReason(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-amber-500"
                    >
                      <option value="">Select a reason…</option>
                      {IN_HOUSE_REASONS.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1.5 text-gray-700">
                      For Whom / Notes <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={draftNotes}
                      onChange={(e) => setDraftNotes(e.target.value)}
                      placeholder="e.g. Owner's mother — birthday lunch"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-amber-500 resize-none"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      This is stored in the audit log for accountability.
                    </p>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-800 leading-relaxed">
                      <strong>What happens when you confirm:</strong>
                    </p>
                    <ul className="text-xs text-amber-700 mt-1 space-y-0.5 list-disc list-inside">
                      <li>No GST will be applied</li>
                      <li>Bill uses the INH// invoice series</li>
                      <li>Not included in GST reports</li>
                      <li>Audit log records who, why, and for whom</li>
                    </ul>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => {
                        setShowInHouseModal(false);
                        setDraftReason('');
                        setDraftNotes('');
                      }}
                      className="flex-1 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        if (!draftReason) {
                          toast.error('Please select a reason');
                          return;
                        }
                        if (!draftNotes || !draftNotes.trim()) {
                          toast.error('Please enter for whom / notes');
                          return;
                        }
                        setIsInHouse(true);
                        setInHouseReason(draftReason);
                        setInHouseNotes(draftNotes.trim());
                        setShowInHouseModal(false);
                        toast.success('Order marked as in-house (no GST)');
                      }}
                      className="flex-1 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── KITCHEN INSTRUCTIONS ── Modal */}
          {showKitchenInstructionModal && instructionItem && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <FaUtensils className="text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-bold text-gray-900">
                      {instructionItem.__isCartItem
                        ? 'Add Kitchen Instruction'
                        : 'Kitchen Instruction'}
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {instructionItem.food_name ||
                        instructionItem.title ||
                        instructionItem.description ||
                        'Item'}{' '}
                      · Qty {instructionItem.quantity || 1}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (isInstructionListening) {
                        SpeechRecognition.stopListening();
                      }
                      setActiveInstructionSpeech(false);
                      setShowKitchenInstructionModal(false);
                      setInstructionItem(null);
                      setInstructionText('');
                    }}
                    className="text-gray-400 hover:text-black"
                  >
                    <FaTimes size={16} />
                  </button>
                </div>

                <div className="space-y-3">
                  <div className="relative">
                    <label className="block text-sm font-medium mb-1.5 text-gray-700">
                      Instruction for kitchen
                    </label>
                    <textarea
                      value={instructionText}
                      onChange={(e) => setInstructionText(e.target.value)}
                      placeholder="e.g. Make it extra spicy, no onions, cook well..."
                      rows={4}
                      className="w-full px-3 py-2 pr-12 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-amber-500 resize-none"
                    />
                    {isInstructionSpeechSupported && (
                      <button
                        type="button"
                        onClick={toggleInstructionSpeech}
                        className={`absolute right-2 bottom-2 p-2 rounded-full transition-all ${
                          isInstructionListening
                            ? 'text-red-500 bg-red-50 animate-pulse scale-110'
                            : 'text-gray-400 hover:text-black hover:bg-gray-100'
                        }`}
                        title={isInstructionListening ? 'Stop listening' : 'Speak instruction'}
                      >
                        <FaMicrophone size={16} />
                      </button>
                    )}
                    {isInstructionListening && (
                      <div className="absolute -bottom-5 left-0 right-0 text-xs text-red-500 font-medium text-center animate-pulse">
                        🎤 Listening... Speak now
                      </div>
                    )}
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-800 leading-relaxed">
                      {instructionItem.__isCartItem ? (
                        <>
                          This instruction will be attached to{' '}
                          <strong>
                            {instructionItem.title || instructionItem.food_name || 'this item'}
                          </strong>{' '}
                          and sent to the kitchen when you place the order.
                        </>
                      ) : (
                        <>
                          This instruction will be attached to{' '}
                          <strong>{instructionItem.food_name || 'this item'}</strong> and sent to
                          the kitchen display immediately.
                        </>
                      )}
                    </p>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => {
                        if (isInstructionListening) {
                          SpeechRecognition.stopListening();
                        }
                        setActiveInstructionSpeech(false);
                        setShowKitchenInstructionModal(false);
                        setInstructionItem(null);
                        setInstructionText('');
                      }}
                      className="flex-1 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-sm"
                      disabled={isSendingInstruction}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={
                        instructionItem?.__isCartItem
                          ? handleSaveCartItemInstruction
                          : handleSendKitchenInstruction
                      }
                      disabled={
                        !instructionItem?.__isCartItem &&
                        (isSendingInstruction || !instructionText.trim())
                      }
                      className={`flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 ${
                        !instructionItem?.__isCartItem &&
                        (isSendingInstruction || !instructionText.trim())
                          ? 'bg-amber-300 text-white cursor-not-allowed'
                          : 'bg-amber-600 text-white hover:bg-amber-700'
                      }`}
                    >
                      {!instructionItem?.__isCartItem && isSendingInstruction ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <FaCheckCircle size={12} />
                          {instructionItem?.__isCartItem ? 'Save Instruction' : 'Send to Kitchen'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        position="bottom-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}

export default FoodOrder;

// import React, { useState, useEffect, useRef } from 'react';
// import {
//   FaShoppingCart,
//   FaSearch,
//   FaLeaf,
//   FaDrumstickBite,
//   FaPlus,
//   FaMinus,
//   FaTimes,
//   FaTrash,
//   FaHotel,
//   FaUsers,
//   FaBox,
//   FaArrowRight,
//   FaChevronDown,
//   FaChevronUp,
//   FaUser,
//   FaPhone,
//   FaIdCard,
//   FaFileInvoice,
//   FaCreditCard,
//   FaTable,
//   FaCircle,
//   FaMoneyBill,
//   FaDigitalTiling,
//   FaList,
//   FaPlusCircle,
//   FaDigitalOcean,
//   FaInfoCircle,
//   FaBarcode,
//   FaCheckCircle,
//   FaBuilding,
//   FaSpinner,
//   FaRupeeSign,
//   FaClock,
//   FaUtensils,
//   FaTag,
//   FaImage,
//   FaMicrophone,
//   FaBan,
// } from 'react-icons/fa';
// import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
// import { useNavigate } from 'react-router-dom';
// import { toast, ToastContainer } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
// const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;
// const REACT_APP_RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

// // ---------- helpers ----------
// const isItemCancelled = (item) => {
//   if (!item) return false;
//   if (item.is_cancelled === true) return true;
//   const s = (item.item_status || item.status || '').toString().toLowerCase();
//   return s === 'cancelled' || s === 'canceled';
// };

// const isOrderCancelled = (order) => {
//   if (!order) return false;
//   const s = (order.status || order.order_status || '').toString().toLowerCase();
//   return s === 'cancelled' || s === 'canceled';
// };

// function FoodOrder() {
//   const [menuItems, setMenuItems] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [search, setSearch] = useState('');
//   const [cart, setCart] = useState([]);
//   const [categories, setCategories] = useState([]);
//   const [subcategories, setSubcategories] = useState([]);
//   const [selectedCategory, setSelectedCategory] = useState(null);
//   const [selectedSubcategory, setSelectedSubcategory] = useState(null);
//   const [justAddedItemId, setJustAddedItemId] = useState(null);
//   const [specialRequest, setSpecialRequest] = useState('');
//   const [discount, setDiscount] = useState(0);
//   const [orderType, setOrderType] = useState('dining');
//   const [tableNumber, setTableNumber] = useState('');
//   const [roomNumber, setRoomNumber] = useState('');
//   const [customerName, setCustomerName] = useState('');
//   const [customerEmail, setCustomerEmail] = useState('');
//   const [phoneNumber, setPhoneNumber] = useState('');
//   const [numberOfGuests, setNumberOfGuests] = useState(1);
//   const [gstNumber, setGstNumber] = useState('');
//   const [deliveryAddress, setDeliveryAddress] = useState('');
//   const [showAdditionalDetails, setShowAdditionalDetails] = useState(false);
//   const [currentUser, setCurrentUser] = useState(null);
//   const [propertyId, setPropertyId] = useState(null);
//   const [currentOrder, setCurrentOrder] = useState(null);
//   const [orderItems, setOrderItems] = useState([]);
//   const [orderId, setOrderId] = useState(null);
//   const [billingInfo, setBillingInfo] = useState(null);
//   const [tables, setTables] = useState([]);
//   const [filteredTables, setFilteredTables] = useState([]);
//   const [tableSearchText, setTableSearchText] = useState('');
//   const [showTableDropdown, setShowTableDropdown] = useState(false);
//   const [showOrderItems, setShowOrderItems] = useState(false);
//   const [razorpayLoaded, setRazorpayLoaded] = useState(false);
//   const [showPaymentModal, setShowPaymentModal] = useState(false);
//   const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
//   const [parcelOrders, setParcelOrders] = useState([]);
//   const [filteredParcelOrders, setFilteredParcelOrders] = useState([]);
//   const [parcelSearchText, setParcelSearchText] = useState('');
//   const [showParcelDropdown, setShowParcelDropdown] = useState(false);
//   const [selectedParcelOrder, setSelectedParcelOrder] = useState(null);
//   const [parcelOrderDetails, setParcelOrderDetails] = useState(null);
//   const [showParcelItems, setShowParcelItems] = useState(false);
//   const [roomOrders, setRoomOrders] = useState([]);
//   const [filteredRoomOrders, setFilteredRoomOrders] = useState([]);
//   const [roomSearchText, setRoomSearchText] = useState('');
//   const [showRoomDropdown, setShowRoomDropdown] = useState(false);
//   const [selectedRoomOrder, setSelectedRoomOrder] = useState(null);
//   const [roomOrderId, setRoomOrderId] = useState(null);
//   const [roomBillingInfo, setRoomBillingInfo] = useState(null);
//   const [roomOrderItems, setRoomOrderItems] = useState([]);
//   const [roomTableSearchText, setRoomTableSearchText] = useState('');
//   const [showRoomTableDropdown, setShowRoomTableDropdown] = useState(false);
//   const [availableTables, setAvailableTables] = useState([]);
//   const [filteredRoomTables, setFilteredRoomTables] = useState([]);
//   const [showTaxBreakdown, setShowTaxBreakdown] = useState(false);
//   const [cgstAmount, setCgstAmount] = useState(0);
//   const [sgstAmount, setSgstAmount] = useState(0);
//   const [isPrinting, setIsPrinting] = useState(false);
//   const [isPlacingOrder, setIsPlacingOrder] = useState(false);
//   const [isAddingItems, setIsAddingItems] = useState(false);
//   const [isProcessingPayment, setIsProcessingPayment] = useState(false);
//   const [companies, setCompanies] = useState([]);
//   const [companySearchText, setCompanySearchText] = useState('');
//   const [filteredCompanies, setFilteredCompanies] = useState([]);
//   const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
//   const [selectedCompany, setSelectedCompany] = useState(null);
//   const companyDropdownRef = useRef(null);
//   const [pendingQuantities, setPendingQuantities] = useState({});
//   const [activeSpeechTarget, setActiveSpeechTarget] = useState(null);

//   // Discount States
//   const [discountPercent, setDiscountPercent] = useState(0);
//   const [discountAmount, setDiscountAmount] = useState(0);
//   const [discountError, setDiscountError] = useState('');

//   const navigate = useNavigate();

//   const {
//     transcript,
//     listening: isListening,
//     resetTranscript,
//     browserSupportsSpeechRecognition: isSupported,
//   } = useSpeechRecognition({
//     clearTranscriptOnListen: true,
//   });

//   // ============== DISCOUNT & TAX CALCULATION LOGIC ==============

//   const totalAmount = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

//   useEffect(() => {
//     const discountValue = (totalAmount * discountPercent) / 100;
//     setDiscountAmount(discountValue);

//     if (discountPercent > 25) {
//       setDiscountError('Discount cannot exceed 25%');
//     } else {
//       setDiscountError('');
//     }
//   }, [discountPercent, totalAmount]);

//   const taxableAmount = totalAmount - discountAmount;

//   const calculateTaxes = () => {
//     let totalTax = 0;

//     if (cart.length === 0) return 0;

//     cart.forEach((item) => {
//       const itemSubtotal = item.price * item.quantity;
//       const itemPercentage = totalAmount > 0 ? itemSubtotal / totalAmount : 0;
//       const discountedItemAmount = itemSubtotal - discountAmount * itemPercentage;

//       if (item.value_type === 'P') {
//         totalTax += discountedItemAmount * (item.value / 100);
//       } else {
//         totalTax += item.value * item.quantity;
//       }
//     });

//     return totalTax;
//   };

//   const taxes = calculateTaxes();

//   useEffect(() => {
//     const halfTax = taxes / 2;
//     setCgstAmount(halfTax);
//     setSgstAmount(halfTax);
//   }, [taxes]);

//   const finalTotalWithDiscount = taxableAmount + taxes;

//   useEffect(() => {
//     if (transcript && activeSpeechTarget === 'search') {
//       setSearch(transcript);
//     }
//   }, [transcript, activeSpeechTarget]);

//   useEffect(() => {
//     if (transcript && activeSpeechTarget === 'special') {
//       setSpecialRequest(transcript);
//     }
//   }, [transcript, activeSpeechTarget]);

//   const startSearchListening = () => {
//     setActiveSpeechTarget('search');
//     resetTranscript();
//     if (isListening) {
//       SpeechRecognition.stopListening();
//     }
//     SpeechRecognition.startListening({
//       continuous: false,
//       language: 'en-IN',
//       interimResults: true,
//     });
//     toast.info('🎤 Listening for search... Speak now');
//   };

//   const stopSearchListening = () => {
//     setActiveSpeechTarget(null);
//     SpeechRecognition.stopListening();
//     toast.info('👂 Stopped listening');
//   };

//   const startSpecialListening = () => {
//     setActiveSpeechTarget('special');
//     resetTranscript();
//     if (isListening) {
//       SpeechRecognition.stopListening();
//     }
//     SpeechRecognition.startListening({
//       continuous: false,
//       language: 'en-IN',
//       interimResults: true,
//     });
//     toast.info('🎤 Listening for special instructions... Speak now');
//   };

//   const stopSpecialListening = () => {
//     setActiveSpeechTarget(null);
//     SpeechRecognition.stopListening();
//     toast.info('👂 Stopped listening');
//   };

//   useEffect(() => {
//     return () => {
//       if (isListening) {
//         SpeechRecognition.stopListening();
//       }
//     };
//   }, []);

//   const clearSearch = () => {
//     setSearch('');
//     setSelectedCategory(null);
//     setSelectedSubcategory(null);
//   };

//   const handlePendingQuantityChange = (itemId, newQuantity) => {
//     if (newQuantity < 1) return;
//     setPendingQuantities((prev) => ({
//       ...prev,
//       [itemId]: newQuantity,
//     }));
//   };

//   const handleUpdateQuantityWithButton = async (item) => {
//     if (isItemCancelled(item)) {
//       toast.info('This item is cancelled and cannot be updated');
//       return;
//     }

//     const newQuantity = pendingQuantities[item.order_item_id] || item.quantity || 1;

//     if (newQuantity === item.quantity) {
//       toast.info('Quantity is already set to this value');
//       return;
//     }

//     await handleUpdateItemQuantity(item, newQuantity);

//     setPendingQuantities((prev) => ({
//       ...prev,
//       [item.order_item_id]: undefined,
//     }));
//   };

//   const getTodayDate = () => {
//     const today = new Date();
//     const day = String(today.getDate()).padStart(2, '0');
//     const month = String(today.getMonth() + 1).padStart(2, '0');
//     const year = String(today.getFullYear()).slice(-2);
//     return `${day}${month}${year}`;
//   };

//   useEffect(() => {
//     const loadRazorpayScript = () => {
//       const script = document.createElement('script');
//       script.src = 'https://checkout.razorpay.com/v1/checkout.js';
//       script.async = true;
//       script.onload = () => setRazorpayLoaded(true);
//       script.onerror = () => {
//         toast.error('Error loading Razorpay script. Please try again.');
//       };
//       document.body.appendChild(script);
//     };

//     loadRazorpayScript();
//   }, []);

//   useEffect(() => {
//     const fetchCompanies = async () => {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/companies`, {
//           headers: { accept: 'application/json' },
//         });

//         if (response.ok) {
//           const data = await response.json();
//           setCompanies(data.companies || []);
//         } else {
//           console.error('Failed to fetch companies');
//         }
//       } catch (error) {
//         console.error('Error fetching companies:', error);
//       }
//     };

//     fetchCompanies();
//   }, []);

//   useEffect(() => {
//     if (cart.length > 0) {
//       setShowAdditionalDetails(false);
//     }
//   }, [cart]);

//   useEffect(() => {
//     if (companySearchText && companySearchText.length > 1) {
//       const filtered = companies.filter((company) =>
//         company.companyname.toLowerCase().includes(companySearchText.toLowerCase())
//       );
//       setFilteredCompanies(filtered);
//       setShowCompanyDropdown(true);
//     } else {
//       setFilteredCompanies([]);
//       setShowCompanyDropdown(false);
//     }
//   }, [companySearchText, companies]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
//         setShowCompanyDropdown(false);
//       }
//     };

//     document.addEventListener('mousedown', handleClickOutside);
//     return () => {
//       document.removeEventListener('mousedown', handleClickOutside);
//     };
//   }, []);

//   useEffect(() => {
//     const fetchCurrentUser = async () => {
//       try {
//         const response = await fetch(`${AQ_BASE_URL}/aq/api/currentuser`, {
//           headers: { accept: 'application/json' },
//           credentials: 'include',
//         });

//         if (response.ok) {
//           const userData = await response.json();
//           setCurrentUser(userData);
//           setPropertyId(userData.property_id);
//           console.log('Current user loaded:', userData);
//         } else {
//           console.error('Failed to fetch current user');
//           setPropertyId(1);
//         }
//       } catch (error) {
//         console.error('Error fetching current user:', error);
//         setPropertyId(1);
//       }
//     };

//     fetchCurrentUser();
//   }, []);

//   const fetchTables = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/dining-tables/`, {
//         headers: { accept: 'application/json' },
//       });

//       if (response.ok) {
//         const data = await response.json();
//         const activeTables = data.tables.filter((table) => table.status !== 'out_of_service');
//         setTables(activeTables);
//         setFilteredTables(activeTables);
//       } else {
//         console.error('Failed to fetch tables');
//       }
//     } catch (error) {
//       console.error('Error fetching tables:', error);
//       toast.error('Failed to load tables');
//     }
//   };

//   useEffect(() => {
//     fetchTables();
//   }, []);

//   useEffect(() => {
//     if (orderType === 'room') {
//       fetchRoomOrders();
//     }
//   }, [orderType]);

//   const fetchAvailableTablesForRoom = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/dining-tables/`, {
//         headers: { accept: 'application/json' },
//       });

//       if (response.ok) {
//         const data = await response.json();
//         const available = data.tables.filter((table) => table.status === 'available');
//         setAvailableTables(available);
//         setFilteredRoomTables(available);
//       } else {
//         console.error('Failed to fetch available tables');
//       }
//     } catch (error) {
//       console.error('Error fetching available tables:', error);
//     }
//   };

//   useEffect(() => {
//     if (orderType === 'room') {
//       fetchAvailableTablesForRoom();
//     }
//   }, [orderType]);

//   const fetchRoomOrders = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/`, {
//         headers: { accept: 'application/json' },
//       });

//       if (response.ok) {
//         const data = await response.json();
//         console.log('Room orders response:', data);

//         const transformedOrders = data
//           .filter((order) => {
//             if (order.order_type !== 'room') return false;
//             if (order.status?.toLowerCase() === 'delivered') return false;
//             if (order.status === 'Completed' || order.status === 'Cancelled') return false;
//             if (order.billing && order.billing.is_paid === true) return false;
//             return true;
//           })
//           .map((order) => {
//             const hasBilling = order.billing !== null;
//             const isPaid = hasBilling ? order.billing.is_paid : false;
//             const billingId = hasBilling ? order.billing.billing_id : null;

//             let paymentMethod = 'Not Paid';
//             if (order.payments && order.payments.length > 0) {
//               const payment = order.payments[0];
//               paymentMethod = payment.payment_method || 'Not Paid';
//               paymentMethod = paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1);
//             } else if (isPaid) {
//               paymentMethod = 'Paid';
//             }

//             const filteredItems = (order.items || []).filter(
//               (item) => item.status?.toLowerCase() !== 'delivered'
//             );

//             return {
//               id: order.id,
//               order_id: order.id,
//               order_type: order.order_type,
//               order_status: order.status || 'pending',
//               customer_name: order.customer_name || 'Guest',
//               customer_phone: order.phone_number || null,
//               customer_email: null,
//               table_number: order.table_number || null,
//               parcel_number: order.parcel_number || null,
//               room_number: order.room_number || null,
//               kot_number: null,
//               totalprice: order.totalprice || 0,
//               total_amount: order.totalprice || 0,
//               is_paid: isPaid,
//               ordered_at: order.created_at,
//               created_at: order.created_at,
//               items: filteredItems,
//               billing: order.billing || null,
//               billing_id: billingId,
//               booking_id: order.booking_id || null,
//               special_request: order.special_request || null,
//               gst_number: null,
//               has_billing: hasBilling,
//               payment_method: paymentMethod,
//               billings: order.billing ? [order.billing] : [],
//               payments: order.payments || [],
//               status: order.status || 'pending',
//             };
//           });

//         transformedOrders.sort((a, b) => {
//           return new Date(b.created_at) - new Date(a.created_at);
//         });

//         setRoomOrders(transformedOrders);
//         setFilteredRoomOrders(transformedOrders);

//         return transformedOrders;
//       }
//       return [];
//     } catch (error) {
//       console.error('Error fetching room orders:', error);
//       return [];
//     }
//   };

//   const fetchRoomOrderByNumber = async (roomNum) => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/`, {
//         method: 'GET',
//         headers: { accept: 'application/json' },
//       });

//       if (response.ok) {
//         const data = await response.json();
//         const roomOrder = data.find(
//           (order) =>
//             order.order_type === 'room' &&
//             order.room_number === roomNum &&
//             order.status !== 'Completed' &&
//             order.status !== 'Cancelled' &&
//             !(order.billing && order.billing.is_paid === true)
//         );
//         if (roomOrder) {
//           roomOrder.billing = roomOrder.billing || null;
//           roomOrder.booking_id = roomOrder.booking_id;
//         }
//         return roomOrder;
//       }
//       return null;
//     } catch (error) {
//       console.error('Error fetching room order:', error);
//       return null;
//     }
//   };

//   const handleAddItemsToRoomOrder = async () => {
//     if (cart.length === 0) {
//       toast.error('Cart is empty');
//       return;
//     }

//     if (!roomNumber) {
//       toast.error('Room number is required');
//       return;
//     }

//     if (isOrderCancelled(selectedRoomOrder)) {
//       toast.error('This order has been cancelled. Cannot add items.');
//       return;
//     }

//     const items = cart.map((item) => ({
//       foodid: item.id,
//       quantity: item.quantity,
//       specialRequest: item.specialRequest || null,
//     }));

//     const addItemsData = {
//       items: items,
//       specialRequest: specialRequest || null,
//     };

//     setIsAddingItems(true);
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/orders/room/${roomNumber}/add-items`, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify(addItemsData),
//       });

//       const data = await response.json();

//       if (response.ok) {
//         toast.success('Items added successfully!');
//         const roomOrder = await fetchRoomOrderByNumber(roomNumber);
//         if (roomOrder) {
//           const mappedItems = (roomOrder.items || []).map((item) => ({
//             order_item_id: item.id,
//             food_id: item.food_id,
//             food_name: item.food_title || item.food_name || 'Item',
//             quantity: item.quantity,
//             unit_price: item.food_price || item.unit_price || 0,
//             total_amount: item.total_price || item.food_price * item.quantity,
//             special_request: item.special_request,
//             item_status: item.item_status || item.status,
//             is_cancelled: isItemCancelled(item),
//           }));

//           setRoomOrderId(roomOrder.id);
//           setRoomOrderItems(mappedItems);
//           setSelectedRoomOrder((prev) => ({
//             ...prev,
//             ...roomOrder,
//             items: mappedItems,
//             booking_id: roomOrder.booking_id || prev?.booking_id,
//           }));

//           if (roomOrder.billing && roomOrder.billing.billing_id) {
//             setRoomBillingInfo({
//               billing_id: roomOrder.billing.billing_id,
//               final_amount: roomOrder.billing.final_amount || roomOrder.totalprice,
//               is_paid: roomOrder.billing.is_paid || false,
//             });
//           } else if (roomOrder.booking_id) {
//             await createRoomOrderBilling(roomOrder.id, roomOrder.booking_id);
//           }
//         }
//         setCart([]);
//         setSpecialRequest('');
//         clearSearch();
//       } else {
//         throw new Error(data.detail || 'Failed to add items');
//       }
//     } catch (error) {
//       console.error('Error adding items to room order:', error);
//       toast.error(error.message || 'Failed to add items');
//     } finally {
//       setIsAddingItems(false);
//     }
//   };

//   useEffect(() => {
//     if (roomSearchText) {
//       const filtered = roomOrders.filter(
//         (order) =>
//           order.room_number?.toLowerCase().includes(roomSearchText.toLowerCase()) ||
//           order.customer_name?.toLowerCase().includes(roomSearchText.toLowerCase()) ||
//           order.id?.toLowerCase().includes(roomSearchText.toLowerCase())
//       );
//       setFilteredRoomOrders(filtered);
//     } else {
//       setFilteredRoomOrders(roomOrders);
//     }
//   }, [roomSearchText, roomOrders]);

//   useEffect(() => {
//     if (orderType === 'parcel') {
//       fetchParcelOrders();
//     }
//   }, [orderType]);

//   const fetchParcelOrders = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders?order_type=parcel`, {
//         method: 'GET',
//         headers: { accept: 'application/json' },
//       });

//       if (response.ok) {
//         const data = await response.json();
//         const todayDate = getTodayDate();
//         const todayOrders = data.orders.filter((order) => {
//           const parcelNumber = order.parcel_number;
//           if (parcelNumber && parcelNumber.includes(todayDate)) {
//             return order.order_status === 'pending' || order.order_status === 'ready';
//           }
//           return false;
//         });
//         setParcelOrders(todayOrders);
//         setFilteredParcelOrders(todayOrders);
//       } else {
//         console.error('Failed to fetch parcel orders');
//       }
//     } catch (error) {
//       console.error('Error fetching parcel orders:', error);
//       toast.error('Failed to load parcel orders');
//     }
//   };

//   useEffect(() => {
//     if (parcelSearchText) {
//       const filtered = parcelOrders.filter(
//         (order) =>
//           order.parcel_number?.toLowerCase().includes(parcelSearchText.toLowerCase()) ||
//           order.customer_name?.toLowerCase().includes(parcelSearchText.toLowerCase()) ||
//           order.customer_phone?.includes(parcelSearchText)
//       );
//       setFilteredParcelOrders(filtered);
//     } else {
//       setFilteredParcelOrders(parcelOrders);
//     }
//   }, [parcelSearchText, parcelOrders]);

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const [itemsRes, categoriesRes, subcategoriesRes] = await Promise.all([
//           fetch(`${BQ_BASE_URL}/bq/api/items/`),
//           fetch(`${BQ_BASE_URL}/bq/api/categories/`),
//           fetch(`${BQ_BASE_URL}/bq/api/subcategories/`),
//         ]);

//         const itemsData = await itemsRes.json();
//         const categoriesData = await categoriesRes.json();
//         const subcategoriesData = await subcategoriesRes.json();

//         setMenuItems(itemsData.items || []);
//         setCategories(categoriesData || []);
//         setSubcategories(subcategoriesData || []);
//       } catch (error) {
//         console.error('Error fetching data:', error);
//         toast.error('Failed to load menu');
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, []);

//   useEffect(() => {
//     if (tableSearchText) {
//       const filtered = tables.filter(
//         (table) =>
//           table.table_number.toLowerCase().includes(tableSearchText.toLowerCase()) ||
//           table.table_name?.toLowerCase().includes(tableSearchText.toLowerCase())
//       );
//       setFilteredTables(filtered);
//     } else {
//       setFilteredTables(tables);
//     }
//   }, [tableSearchText, tables]);

//   const getStatusColor = (status) => {
//     switch (status) {
//       case 'available':
//         return 'text-green-500';
//       case 'occupied':
//         return 'text-red-500';
//       case 'reserved':
//         return 'text-yellow-500';
//       default:
//         return 'text-gray-500';
//     }
//   };

//   const getStatusText = (status) => {
//     switch (status) {
//       case 'available':
//         return 'Available';
//       case 'occupied':
//         return 'Occupied';
//       case 'reserved':
//         return 'Reserved';
//       default:
//         return status;
//     }
//   };

//   const handleTableSelect = async (table) => {
//     setTableNumber(table.table_number);
//     setTableSearchText(`${table.table_number} (${getStatusText(table.status)})`);
//     setShowTableDropdown(false);

//     if (table.status === 'occupied') {
//       await fetchOrderByTable(table.table_number);
//     } else if (table.status === 'available') {
//       setCurrentOrder(null);
//       setOrderId(null);
//       setOrderItems([]);
//       setBillingInfo(null);
//       setCart([]);
//       toast.info(`Table ${table.table_number} is available. Create a new order.`);
//     }
//   };

//   const fetchOrderByTable = async (tableNum) => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/tables/${tableNum}/order`, {
//         method: 'GET',
//         headers: { accept: 'application/json' },
//       });

//       if (response.ok) {
//         const data = await response.json();
//         setCurrentOrder(data);
//         setOrderId(data.order_id);

//         const allItems = [];
//         if (data.kitchen_batches) {
//           data.kitchen_batches.forEach((batch) => {
//             if (batch.items) {
//               batch.items.forEach((item) => {
//                 allItems.push({
//                   order_item_id: item.item_id,
//                   food_id: item.food_id,
//                   food_name: item.name,
//                   quantity: item.quantity,
//                   unit_price: item.unit_price,
//                   total_amount: item.line_total,
//                   special_request: item.special_request,
//                   item_status: item.item_status,
//                   is_cancelled:
//                     (item.item_status || '').toString().toLowerCase() === 'cancelled' ||
//                     item.is_cancelled === true,
//                   is_complimentary: item.is_complimentary,
//                   kot_number: batch.kot_number,
//                   description: `${item.name} x ${item.quantity}`,
//                 });
//               });
//             }
//           });
//         }

//         setOrderItems(allItems);
//         setBillingInfo(data.billing);
//         setCustomerName(data.customer_name || '');
//         setCustomerEmail(data.customer_email || '');
//         setPhoneNumber(data.customer_phone || '');
//         setNumberOfGuests(data.number_of_guests || 1);
//         setSpecialRequest(data.special_request || '');

//         if (data.gst_number) {
//           setGstNumber(data.gst_number);
//         }

//         toast.success(`Order found for table ${tableNum}`);
//         setCart([]);
//       } else if (response.status === 404) {
//         toast.info(`No active order found for table ${tableNum}. You can create a new order.`);
//         setCurrentOrder(null);
//         setOrderId(null);
//         setOrderItems([]);
//         setBillingInfo(null);
//       } else {
//         throw new Error('Failed to fetch order');
//       }
//     } catch (error) {
//       console.error('Error fetching order:', error);
//       toast.error('Failed to fetch order details');
//     }
//   };

//   const fetchParcelOrderDetails = async (orderId) => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders/${orderId}`, {
//         method: 'GET',
//         headers: { accept: 'application/json' },
//       });

//       if (response.ok) {
//         const data = await response.json();
//         setParcelOrderDetails(data);

//         const allItems = [];
//         if (data.kitchen_batches) {
//           data.kitchen_batches.forEach((batch) => {
//             if (batch.items) {
//               batch.items.forEach((item) => {
//                 allItems.push({
//                   order_item_id: item.item_id,
//                   food_id: item.food_id,
//                   food_name: item.name,
//                   quantity: item.quantity,
//                   unit_price: item.unit_price,
//                   total_amount: item.line_total,
//                   special_request: item.special_request,
//                   item_status: item.item_status,
//                   is_cancelled:
//                     (item.item_status || '').toString().toLowerCase() === 'cancelled' ||
//                     item.is_cancelled === true,
//                   is_complimentary: item.is_complimentary,
//                   kot_number: batch.kot_number,
//                   description: `${item.name} x ${item.quantity}`,
//                 });
//               });
//             }
//           });
//         }

//         setOrderItems(allItems);
//         setBillingInfo(data.billing);
//         setCustomerName(data.customer_name || '');
//         setCustomerEmail(data.customer_email || '');
//         setPhoneNumber(data.customer_phone || '');
//         setSpecialRequest(data.special_request || '');
//         setDeliveryAddress(data.delivery_address || '');

//         if (data.gst_number) {
//           setGstNumber(data.gst_number);
//         }

//         toast.success(`Order details loaded`);
//       } else {
//         throw new Error('Failed to fetch order details');
//       }
//     } catch (error) {
//       console.error('Error fetching order details:', error);
//       toast.error('Failed to fetch order details');
//     }
//   };

//   const fetchRoomOrderDetails = async (order) => {
//     let detail = null;
//     try {
//       const detailRes = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/${order.id}`, {
//         method: 'GET',
//         headers: { accept: 'application/json' },
//       });
//       if (detailRes.ok) {
//         detail = await detailRes.json();
//       } else {
//         console.warn('Room order detail fetch failed, status', detailRes.status);
//       }
//     } catch (detailErr) {
//       console.warn('Room order detail fetch error:', detailErr);
//     }

//     const orderData = {
//       ...order,
//       ...(detail || {}),
//       items: detail && detail.items && detail.items.length ? detail.items : order.items || [],
//       billing: order.billing || (detail && detail.billing) || null,
//       payments: order.payments || (detail && detail.payments) || [],
//       booking_id: (detail && detail.booking_id) || order.booking_id,
//     };

//     setSelectedRoomOrder(orderData);
//     setRoomOrderId(order.id);

//     const mappedItems = (orderData.items || []).map((item) => ({
//       order_item_id: item.id,
//       food_id: item.food_id,
//       food_name: item.food_title || item.food_name || 'Item',
//       quantity: item.quantity,
//       unit_price: item.food_price || item.unit_price || 0,
//       total_amount: item.total_price || item.line_total || item.food_price * item.quantity,
//       special_request: item.special_request,
//       item_status: item.item_status || item.status,
//       is_cancelled: isItemCancelled(item),
//       description: `${item.food_title || 'Item'} x ${item.quantity}`,
//     }));

//     setRoomOrderItems(mappedItems);
//     setCustomerName(orderData.customer_name || '');
//     setPhoneNumber(orderData.phone_number || '');
//     setRoomNumber(orderData.room_number || '');
//     setSpecialRequest(orderData.special_request || '');
//     setTableNumber(orderData.table_number || '');
//     setRoomTableSearchText(orderData.table_number ? `${orderData.table_number}` : '');
//     setGstNumber(orderData.gst_number || '');

//     setShowOrderItems(true);

//     const orderStatus = (orderData.status || orderData.order_status || '').toLowerCase();
//     const isClosedOrder =
//       orderStatus === 'delivered' || orderStatus === 'completed' || orderStatus === 'cancelled';

//     if (orderData.billing && orderData.billing.billing_id) {
//       setRoomBillingInfo({
//         billing_id: orderData.billing.billing_id,
//         final_amount: orderData.billing.final_amount || orderData.totalprice,
//         is_paid: orderData.billing.is_paid || false,
//       });
//       toast.success(`Room order loaded for room ${orderData.room_number}`);
//     } else if (isClosedOrder) {
//       setRoomBillingInfo(null);
//       toast.info('Opened a past order for viewing. No new bill was created.');
//     } else if (orderData.booking_id) {
//       await createRoomOrderBilling(orderData.id, orderData.booking_id);
//     } else {
//       console.error('Booking ID not found in order object');
//       toast.error('Cannot create billing: Booking ID missing');
//     }

//     setCart([]);
//   };

//   const handleParcelSelect = async (order) => {
//     setSelectedParcelOrder(order);
//     setParcelSearchText(`${order.parcel_number} - ${order.customer_name || 'Guest'}`);
//     setShowParcelDropdown(false);
//     setOrderId(order.order_id);
//     setParcelOrderDetails(null);
//     await fetchParcelOrderDetails(order.order_id);
//   };

//   const handleRoomSelect = async (order) => {
//     setRoomSearchText(`${order.room_number} - ${order.customer_name || 'Guest'}`);
//     setShowRoomDropdown(false);
//     await fetchRoomOrderDetails(order);
//   };

//   const handleNewRoomOrder = () => {
//     setSelectedRoomOrder(null);
//     setRoomSearchText('');
//     setRoomOrderId(null);
//     setRoomOrderItems([]);
//     setRoomBillingInfo(null);
//     setRoomNumber('');
//     setTableNumber('');
//     setRoomTableSearchText('');
//     setCustomerName('');
//     setCustomerEmail('');
//     setPhoneNumber('');
//     setGstNumber('');
//     setSpecialRequest('');
//     setCart([]);
//     setShowRoomDropdown(false);
//     toast.info('Creating new room order. Add items to cart and place order.');
//   };

//   const handleNewParcelOrder = () => {
//     setSelectedParcelOrder(null);
//     setParcelSearchText('');
//     setOrderId(null);
//     setOrderItems([]);
//     setBillingInfo(null);
//     setCustomerName('');
//     setCustomerEmail('');
//     setPhoneNumber('');
//     setDeliveryAddress('');
//     setGstNumber('');
//     setSpecialRequest('');
//     setCart([]);
//     setShowParcelDropdown(false);
//     toast.info('Creating new parcel order. Add items to cart and place order.');
//   };

//   const getCategoryIdFromSubCategoryId = (subCategoryId) => {
//     const subcategory = subcategories.find((sub) => sub.id === subCategoryId);
//     return subcategory ? subcategory.categoryid : null;
//   };

//   const handleAddToCart = (item) => {
//     setCart((prevCart) => {
//       const existingItem = prevCart.find((cartItem) => cartItem.id === item.id);
//       if (existingItem) {
//         return prevCart.map((cartItem) =>
//           cartItem.id === item.id ? { ...cartItem, quantity: cartItem.quantity + 1 } : cartItem
//         );
//       }
//       return [...prevCart, { ...item, quantity: 1, specialRequest: '' }];
//     });

//     setJustAddedItemId(item.id);
//     setTimeout(() => setJustAddedItemId(null), 1000);
//     toast.success(`${item.title} added to cart`);
//     clearSearch();
//   };

//   const handleRemoveFromCart = (id) => {
//     setCart(cart.filter((item) => item.id !== id));
//     toast.info('Item removed from cart');
//   };

//   const handleIncreaseQuantity = (id) => {
//     setCart((prevCart) =>
//       prevCart.map((item) => (item.id === id ? { ...item, quantity: item.quantity + 1 } : item))
//     );
//   };

//   const handleDecreaseQuantity = (id) => {
//     setCart((prevCart) =>
//       prevCart.map((item) =>
//         item.id === id && item.quantity > 1 ? { ...item, quantity: item.quantity - 1 } : item
//       )
//     );
//   };

//   const handleIncreaseGuests = () => {
//     setNumberOfGuests((prev) => prev + 1);
//   };

//   const handleDecreaseGuests = () => {
//     setNumberOfGuests((prev) => (prev > 1 ? prev - 1 : 1));
//   };

//   const clearCart = () => {
//     setCart([]);
//     setSpecialRequest('');
//     setSearch('');
//     setSelectedCategory(null);
//     setSelectedSubcategory(null);
//     setShowAdditionalDetails(false);
//     toast.info('Cart cleared');
//   };

//   const clearAllFields = () => {
//     setCart([]);
//     setSpecialRequest('');
//     setSearch('');
//     setSelectedCategory(null);
//     setSelectedSubcategory(null);
//     setShowAdditionalDetails(false);
//     setDiscountPercent(0);
//     setDiscountAmount(0);
//     setDiscountError('');
//     setDiscount(0);

//     setCustomerName('');
//     setCustomerEmail('');
//     setPhoneNumber('');
//     setGstNumber('');
//     setDeliveryAddress('');
//     setSelectedCompany(null);
//     setCompanySearchText('');

//     if (orderType === 'dining') {
//       setTableNumber('');
//       setTableSearchText('');
//       setNumberOfGuests(1);
//       setOrderId(null);
//       setCurrentOrder(null);
//       setOrderItems([]);
//       setBillingInfo(null);
//       setShowTableDropdown(false);
//     } else if (orderType === 'parcel') {
//       setOrderId(null);
//       setOrderItems([]);
//       setBillingInfo(null);
//       setSelectedParcelOrder(null);
//       setParcelSearchText('');
//       setParcelOrderDetails(null);
//       setShowParcelDropdown(false);
//     } else if (orderType === 'room') {
//       setRoomNumber('');
//       setRoomSearchText('');
//       setRoomOrderId(null);
//       setRoomOrderItems([]);
//       setRoomBillingInfo(null);
//       setSelectedRoomOrder(null);
//       setTableNumber('');
//       setRoomTableSearchText('');
//       setShowRoomDropdown(false);
//       setShowRoomTableDropdown(false);
//     }

//     toast.info('All fields cleared');
//   };

//   const handleCompanySelect = (company) => {
//     setSelectedCompany(company);
//     setCompanySearchText(company.companyname);
//     setGstNumber(company.gstnumber || '');
//     setCustomerName(company.companyname);
//     setShowCompanyDropdown(false);
//   };

//   const handleUpdateItemQuantity = async (item, newQuantity) => {
//     if (isItemCancelled(item)) {
//       toast.error('This item is cancelled and cannot be updated');
//       return;
//     }
//     if (!item.order_item_id) {
//       toast.error('Item ID not found');
//       return;
//     }

//     const itemId = item.order_item_id;
//     const orderIdToUse = orderType === 'room' ? roomOrderId : orderId;

//     if (!orderIdToUse) {
//       toast.error('No order found');
//       return;
//     }

//     const toastId = toast.loading('Updating quantity...');

//     try {
//       let response;
//       let url;

//       if (orderType === 'room') {
//         url = `${BQ_BASE_URL}/bq/api/orders/room/${roomNumber}/items/${itemId}`;
//         response = await fetch(url, {
//           method: 'PATCH',
//           headers: {
//             'Content-Type': 'application/json',
//             accept: 'application/json',
//           },
//           body: JSON.stringify({
//             quantity: newQuantity,
//             specialRequest: item.special_request || '',
//           }),
//         });
//       } else if (orderType === 'dining' || orderType === 'parcel') {
//         url = `${BQ_BASE_URL}/bq/api/restaurant/orders/${orderIdToUse}/items/${itemId}`;
//         response = await fetch(url, {
//           method: 'PATCH',
//           headers: {
//             accept: 'application/json',
//             'Content-Type': 'application/json',
//           },
//           body: JSON.stringify({
//             quantity: newQuantity,
//             special_request: item.special_request || '',
//           }),
//         });
//       }

//       const data = await response.json();

//       toast.dismiss(toastId);

//       if (response.ok) {
//         toast.success(`${item.food_name || 'Item'} quantity updated to ${newQuantity}`);

//         if (orderType === 'dining' && tableNumber) {
//           await fetchOrderByTable(tableNumber);
//           await fetchTables();
//         } else if (orderType === 'parcel' && orderId) {
//           await fetchParcelOrderDetails(orderId);
//           await fetchParcelOrders();
//         } else if (orderType === 'room' && roomNumber) {
//           const roomOrder = await fetchRoomOrderByNumber(roomNumber);
//           if (roomOrder) {
//             const mappedItems = (roomOrder.items || []).map((item) => ({
//               order_item_id: item.id,
//               food_id: item.food_id,
//               food_name: item.food_title || item.food_name || 'Item',
//               quantity: item.quantity,
//               unit_price: item.food_price || item.unit_price || 0,
//               total_amount: item.total_price || item.food_price * item.quantity,
//               special_request: item.special_request,
//               item_status: item.item_status || item.status,
//               is_cancelled: isItemCancelled(item),
//             }));

//             setRoomOrderId(roomOrder.id);
//             setRoomOrderItems(mappedItems);
//             setSelectedRoomOrder((prev) => ({
//               ...prev,
//               ...roomOrder,
//               items: mappedItems,
//             }));

//             if (roomOrder.billing && roomOrder.billing.billing_id) {
//               setRoomBillingInfo({
//                 billing_id: roomOrder.billing.billing_id,
//                 final_amount: roomOrder.billing.final_amount || roomOrder.totalprice,
//                 is_paid: roomOrder.billing.is_paid || false,
//               });
//             }
//           }
//           await fetchRoomOrders();
//         }
//       } else {
//         throw new Error(data.detail || 'Failed to update quantity');
//       }
//     } catch (error) {
//       console.error('Error updating item quantity:', error);
//       toast.dismiss(toastId);
//       toast.error(error.message || 'Failed to update quantity');
//     }
//   };

//   // ============== CANCEL ITEM (NEW APIs) ==============
//   const handleRemoveItemFromOrder = async (itemId, orderIdToUse, itemName) => {
//     const toastId = toast.loading('Cancelling item...');

//     try {
//       let response;
//       let url;

//       if (orderType === 'room') {
//         url = `${BQ_BASE_URL}/bq/api/orders/${orderIdToUse}/items/${itemId}/cancel`;
//         response = await fetch(url, {
//           method: 'POST',
//           headers: {
//             accept: '*/*',
//             'Content-Type': 'application/json',
//           },
//           body: JSON.stringify({ reason: '' }),
//         });
//       } else {
//         url = `${BQ_BASE_URL}/bq/api/restaurant/orders/${orderIdToUse}/items/${itemId}/cancel`;
//         response = await fetch(url, {
//           method: 'POST',
//           headers: {
//             accept: '*/*',
//             'Content-Type': 'application/json',
//           },
//           body: JSON.stringify({ reason: '' }),
//         });
//       }

//       const data = await response.json();

//       toast.dismiss(toastId);

//       if (response.ok) {
//         const cancelledName = data.food_name || itemName || 'Item';
//         toast.success(data.message || `${cancelledName} cancelled from order`);

//         if (data.parent_order_cancelled) {
//           toast.info('All items were cancelled — parent order is now cancelled.');
//         }

//         if (orderType === 'dining' && tableNumber) {
//           await fetchOrderByTable(tableNumber);
//           await fetchTables();
//         } else if (orderType === 'parcel' && orderId) {
//           await fetchParcelOrderDetails(orderId);
//           await fetchParcelOrders();
//         } else if (orderType === 'room' && roomNumber) {
//           if (data.parent_order_cancelled) {
//             setRoomOrderId(null);
//             setRoomOrderItems([]);
//             setRoomBillingInfo(null);
//             setSelectedRoomOrder(null);
//             setRoomSearchText('');
//             setRoomNumber('');
//             setTableNumber('');
//             setRoomTableSearchText('');
//             setCustomerName('');
//             setPhoneNumber('');
//             setGstNumber('');
//             setSpecialRequest('');
//             setCart([]);
//           } else {
//             const roomOrder = await fetchRoomOrderByNumber(roomNumber);
//             if (roomOrder) {
//               const mappedItems = (roomOrder.items || []).map((item) => ({
//                 order_item_id: item.id,
//                 food_id: item.food_id,
//                 food_name: item.food_title || item.food_name || 'Item',
//                 quantity: item.quantity,
//                 unit_price: item.food_price || item.unit_price || 0,
//                 total_amount: item.total_price || item.food_price * item.quantity,
//                 special_request: item.special_request,
//                 item_status: item.item_status || item.status,
//                 is_cancelled: isItemCancelled(item),
//               }));

//               setRoomOrderId(roomOrder.id);
//               setRoomOrderItems(mappedItems);
//               setSelectedRoomOrder((prev) => ({
//                 ...prev,
//                 ...roomOrder,
//                 items: mappedItems,
//               }));

//               if (roomOrder.billing && roomOrder.billing.billing_id) {
//                 setRoomBillingInfo({
//                   billing_id: roomOrder.billing.billing_id,
//                   final_amount: roomOrder.billing.final_amount || roomOrder.totalprice,
//                   is_paid: roomOrder.billing.is_paid || false,
//                 });
//               }
//             }
//           }
//           await fetchRoomOrders();
//         }
//       } else {
//         throw new Error(data.detail || data.message || 'Failed to cancel item');
//       }
//     } catch (error) {
//       console.error('Error cancelling item:', error);
//       toast.dismiss(toastId);
//       toast.error(error.message || 'Failed to cancel item');
//     }
//   };

//   // ============== CANCEL ENTIRE ORDER (NEW APIs) ==============
//   const handleClearAllItems = async () => {
//     if (!window.confirm('Are you sure you want to cancel this entire order?')) {
//       return;
//     }

//     const itemsToRemove = orderType === 'room' ? roomOrderItems : orderItems;

//     if (itemsToRemove.length === 0) {
//       toast.info('No items to cancel');
//       return;
//     }

//     const toastId = toast.loading('Cancelling order...');

//     try {
//       if (orderType === 'room') {
//         const orderIdToCancel = roomOrderId;

//         if (!orderIdToCancel) {
//           throw new Error('Order ID not found');
//         }

//         const response = await fetch(`${BQ_BASE_URL}/bq/api/orders/${orderIdToCancel}/cancel`, {
//           method: 'POST',
//           headers: {
//             accept: '*/*',
//             'Content-Type': 'application/json',
//           },
//           body: JSON.stringify({ reason: '' }),
//         });

//         const data = await response.json();

//         if (response.ok) {
//           toast.dismiss(toastId);
//           toast.success(data.message || 'Room food order cancelled');

//           setRoomOrderId(null);
//           setRoomOrderItems([]);
//           setRoomBillingInfo(null);
//           setSelectedRoomOrder(null);
//           setRoomSearchText('');
//           setRoomNumber('');
//           setRoomTableSearchText('');
//           setTableNumber('');
//           setCustomerName('');
//           setPhoneNumber('');
//           setGstNumber('');
//           setSpecialRequest('');
//           setCart([]);

//           await fetchRoomOrders();
//           await fetchAvailableTablesForRoom();
//           await fetchTables();
//         } else {
//           throw new Error(data.detail || data.message || 'Failed to cancel order');
//         }
//       } else {
//         const orderIdToCancel = orderId;

//         if (!orderIdToCancel) {
//           throw new Error('Order ID not found');
//         }

//         const response = await fetch(
//           `${BQ_BASE_URL}/bq/api/restaurant/orders/${orderIdToCancel}/cancel`,
//           {
//             method: 'POST',
//             headers: {
//               accept: '*/*',
//               'Content-Type': 'application/json',
//             },
//             body: JSON.stringify({ reason: '' }),
//           }
//         );

//         const data = await response.json();

//         if (response.ok) {
//           toast.dismiss(toastId);
//           toast.success(data.message || 'Order cancelled');

//           if (data.table_status === 'available') {
//             toast.info(`Table ${data.table_number || ''} is now available.`);
//           }

//           setOrderId(null);
//           setOrderItems([]);
//           setBillingInfo(null);
//           setCurrentOrder(null);
//           setCart([]);
//           setSpecialRequest('');
//           setCustomerName('');
//           setCustomerEmail('');
//           setPhoneNumber('');
//           setGstNumber('');
//           setNumberOfGuests(1);

//           if (orderType === 'dining') {
//             setTableNumber('');
//             setTableSearchText('');
//             await fetchTables();
//           } else if (orderType === 'parcel') {
//             setSelectedParcelOrder(null);
//             setParcelSearchText('');
//             setDeliveryAddress('');
//             await fetchParcelOrders();
//           }
//         } else {
//           throw new Error(data.detail || data.message || 'Failed to cancel order');
//         }
//       }
//     } catch (error) {
//       console.error('Error cancelling order:', error);
//       toast.dismiss(toastId);
//       toast.error(error.message || 'Failed to cancel order');
//     }
//   };

//   const handleRemoveItem = (item) => {
//     if (isItemCancelled(item)) {
//       toast.info('This item is already cancelled');
//       return;
//     }

//     const itemId = item.order_item_id;
//     const orderIdToUse = orderType === 'room' ? roomOrderId : orderId;
//     const itemName = item.food_name || 'this item';

//     if (!itemId) {
//       toast.error('Item ID not found');
//       return;
//     }

//     if (!orderIdToUse) {
//       toast.error('Order ID not found');
//       return;
//     }

//     handleRemoveItemFromOrder(itemId, orderIdToUse, itemName);
//   };

//   const handleAddItemsToOrder = async () => {
//     if (cart.length === 0) {
//       toast.error('Cart is empty');
//       return;
//     }

//     if (isOrderCancelled(currentOrder)) {
//       toast.error('This order has been cancelled. Cannot add items.');
//       return;
//     }

//     const items = cart.map((item) => ({
//       food_id: item.id,
//       quantity: item.quantity,
//       special_request: item.specialRequest || '',
//     }));

//     const addItemsData = {
//       items: items,
//       kitchen_notes: specialRequest || null,
//     };

//     try {
//       let response;
//       let data;

//       if (orderType === 'dining') {
//         if (!tableNumber) {
//           toast.error('Table number is required');
//           return;
//         }

//         response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/tables/${tableNumber}/add-items`, {
//           method: 'POST',
//           headers: { 'Content-Type': 'application/json' },
//           body: JSON.stringify(addItemsData),
//         });
//       } else {
//         if (!orderId) {
//           toast.error('No order found. Please select a parcel order.');
//           return;
//         }

//         response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders/${orderId}/add-items`, {
//           method: 'POST',
//           headers: { 'Content-Type': 'application/json' },
//           body: JSON.stringify(addItemsData),
//         });
//       }

//       data = await response.json();

//       if (response.ok) {
//         toast.success('Items added successfully!');

//         if (orderType === 'dining') {
//           await fetchOrderByTable(tableNumber);
//           await fetchTables();
//           if (typeof fetchAvailableTablesForRoom === 'function') {
//             await fetchAvailableTablesForRoom();
//           }
//         } else {
//           await fetchParcelOrderDetails(orderId);
//           await fetchParcelOrders();
//         }

//         setCart([]);
//         setSpecialRequest('');
//         setCustomerName('');
//         setCustomerEmail('');
//         setPhoneNumber('');
//         setGstNumber('');
//         setDeliveryAddress('');
//         setSelectedCompany(null);
//         setCompanySearchText('');
//         setShowAdditionalDetails(false);
//         clearSearch();
//       } else {
//         throw new Error(data.detail || 'Failed to add items');
//       }
//     } catch (error) {
//       console.error('Error adding items:', error);
//       toast.error(error.message || 'Failed to add items');
//     }
//   };

//   const placeNewOrder = async () => {
//     if (!propertyId) {
//       toast.error('Property information not loaded. Please try again.');
//       return;
//     }

//     if (cart.length === 0) {
//       toast.error('Cart is empty');
//       return;
//     }

//     const items = cart.map((item) => ({
//       food_id: item.id,
//       quantity: item.quantity,
//       special_request: item.specialRequest,
//     }));

//     const orderData = {
//       order_type: 'dining',
//       customer_name: customerName || null,
//       customer_phone: phoneNumber || null,
//       customer_email: customerEmail || null,
//       table_number: tableNumber,
//       number_of_guests: numberOfGuests,
//       gst_number: gstNumber || null,
//       delivery_address: null,
//       items: items,
//       special_request: specialRequest || null,
//       kitchen_notes: null,
//       discount_percent: discountPercent,
//       service_charge_percent: 0,
//       property_id: propertyId,
//     };

//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders`, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify(orderData),
//       });

//       const data = await response.json();

//       if (response.ok) {
//         toast.success(`Order placed successfully!`);
//         await fetchOrderByTable(tableNumber);
//         await fetchTables();
//         setCart([]);
//         setSpecialRequest('');
//         setDiscountPercent(0);
//         setSelectedCompany(null);
//         setCustomerName('');
//         setCustomerEmail('');
//         setPhoneNumber('');
//         setGstNumber('');
//         setSelectedCompany(null);
//         setCompanySearchText('');
//         setNumberOfGuests(1);
//         clearSearch();
//       } else {
//         throw new Error(data.detail || 'Failed to place order');
//       }
//     } catch (error) {
//       console.error('Error:', error);
//       toast.error(error.message || 'Failed to place order');
//     }
//   };

//   const placeParcelOrder = async () => {
//     if (!propertyId) {
//       toast.error('Property information not loaded. Please try again.');
//       return;
//     }

//     if (cart.length === 0) {
//       toast.error('Cart is empty');
//       return;
//     }

//     const items = cart.map((item) => ({
//       food_id: item.id,
//       quantity: item.quantity,
//       special_request: item.specialRequest,
//     }));

//     const orderData = {
//       order_type: 'parcel',
//       customer_name: customerName || null,
//       customer_phone: phoneNumber || null,
//       customer_email: customerEmail || null,
//       table_number: null,
//       number_of_guests: 1,
//       gst_number: gstNumber || null,
//       delivery_address: deliveryAddress || null,
//       items: items,
//       special_request: specialRequest || null,
//       kitchen_notes: null,
//       discount_percent: discountPercent,
//       service_charge_percent: 0,
//       property_id: propertyId,
//     };

//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders`, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify(orderData),
//       });

//       const data = await response.json();

//       if (response.ok) {
//         toast.success(`Parcel order placed successfully!`);
//         setOrderId(data.order_id);
//         await fetchParcelOrderDetails(data.order_id);
//         setCart([]);
//         setSpecialRequest('');
//         setDiscountPercent(0);
//         setShowParcelDropdown(false);
//         await fetchParcelOrders();
//         setCustomerName('');
//         setCustomerEmail('');
//         setPhoneNumber('');
//         setGstNumber('');
//         setDeliveryAddress('');
//         setSelectedCompany(null);
//         setCompanySearchText('');
//         setShowAdditionalDetails(false);
//         clearSearch();
//       } else {
//         throw new Error(data.detail || 'Failed to place parcel order');
//       }
//     } catch (error) {
//       console.error('Error:', error);
//       toast.error(error.message || 'Failed to place parcel order');
//     }
//   };

//   const placeRoomOrder = async () => {
//     if (!propertyId) {
//       toast.error('Property information not loaded. Please try again.');
//       return;
//     }

//     if (cart.length === 0) {
//       toast.error('Cart is empty');
//       return;
//     }

//     if (!roomNumber) {
//       toast.error('Please enter room number');
//       return;
//     }

//     const items = cart.map((item) => ({
//       foodid: item.id,
//       quantity: item.quantity,
//       specialRequest: item.specialRequest || null,
//     }));

//     const orderData = {
//       items: items,
//       orderType: 'room',
//       customerName: customerName || 'Guest',
//       phoneNumber: phoneNumber || null,
//       tableNumber: tableNumber || null,
//       roomNumber: roomNumber,
//       parcelNumber: null,
//       specialRequest: specialRequest || null,
//       gstNumber: gstNumber || null,
//       discount: discountPercent,
//     };

//     setIsPlacingOrder(true);
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/`, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify(orderData),
//       });

//       const data = await response.json();

//       if (response.ok) {
//         toast.success(`Room order placed successfully!`);

//         setRoomOrderId(null);
//         setRoomOrderItems([]);
//         setRoomBillingInfo(null);
//         setSelectedRoomOrder(null);
//         setRoomSearchText('');
//         setRoomNumber('');
//         setTableNumber('');
//         setRoomTableSearchText('');
//         setCustomerName('');
//         setCustomerEmail('');
//         setPhoneNumber('');
//         setGstNumber('');
//         setSpecialRequest('');
//         setDiscountPercent(0);
//         setCart([]);
//         setShowRoomDropdown(false);

//         toast.success(`Order placed! Order ID: ${data.order_id || 'N/A'}`);

//         await fetchRoomOrders();
//         await fetchAvailableTablesForRoom();
//         await fetchTables();
//         clearSearch();
//       } else {
//         throw new Error(data.detail || 'Failed to place room order');
//       }
//     } catch (error) {
//       console.error('Error:', error);
//       toast.error(error.message || 'Failed to place room order');
//     } finally {
//       setIsPlacingOrder(false);
//     }
//   };

//   const createRoomOrderBilling = async (foodOrderId, bookingId) => {
//     try {
//       if (!bookingId) {
//         console.error('Booking ID is required for billing');
//         toast.error('Cannot create billing: Booking ID missing');
//         return;
//       }

//       const url = `${BQ_BASE_URL}/bq/api/create_food_order_billing?bookingid=${bookingId}&food_order_id=${foodOrderId}`;
//       const response = await fetch(url, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//       });

//       if (response.ok) {
//         const data = await response.json();
//         setRoomBillingInfo({
//           billing_id: data.billingid,
//           final_amount: parseFloat(data.finalamount),
//           is_paid: false,
//         });
//         console.log('Room billing info set:', data);
//         toast.success('Billing created successfully!');
//       } else {
//         const error = await response.json();
//         console.error('Failed to create billing for room order:', error);
//         toast.error(error.detail || 'Failed to create billing');
//       }
//     } catch (error) {
//       console.error('Error creating room order billing:', error);
//       toast.error('Failed to create billing');
//     }
//   };

//   const handlePlaceOrder = async () => {
//     if (cart.length === 0) {
//       toast.error('Cart is empty');
//       return;
//     }

//     if (!propertyId) {
//       toast.error('Loading property information. Please wait...');
//       return;
//     }

//     if (discountPercent > 25) {
//       toast.error('Discount cannot exceed 25%');
//       return;
//     }

//     if (orderType === 'dining') {
//       if (!tableNumber) {
//         toast.error('Please select a table');
//         return;
//       }

//       if (orderId) {
//         if (isOrderCancelled(currentOrder)) {
//           toast.error('This order is cancelled. Cannot add more items.');
//           return;
//         }
//         setIsAddingItems(true);
//         try {
//           await handleAddItemsToOrder();
//         } finally {
//           setIsAddingItems(false);
//         }
//       } else {
//         setIsPlacingOrder(true);
//         try {
//           await placeNewOrder();
//         } finally {
//           setIsPlacingOrder(false);
//         }
//       }
//       return;
//     }

//     if (orderType === 'parcel') {
//       if (orderId && selectedParcelOrder) {
//         if (isOrderCancelled(selectedParcelOrder)) {
//           toast.error('This order is cancelled. Cannot add more items.');
//           return;
//         }
//         setIsAddingItems(true);
//         try {
//           await handleAddItemsToOrder();
//         } finally {
//           setIsAddingItems(false);
//         }
//       } else {
//         setIsPlacingOrder(true);
//         try {
//           await placeParcelOrder();
//         } finally {
//           setIsPlacingOrder(false);
//         }
//       }
//       return;
//     }

//     if (orderType === 'room') {
//       if (roomOrderId && selectedRoomOrder) {
//         if (isOrderCancelled(selectedRoomOrder)) {
//           toast.error('This order is cancelled. Cannot add more items.');
//           return;
//         }
//         await handleAddItemsToRoomOrder();
//       } else {
//         setIsPlacingOrder(true);
//         try {
//           await placeRoomOrder();
//         } finally {
//           setIsPlacingOrder(false);
//         }
//       }
//       return;
//     }
//   };

//   const handleCashPayment = async () => {
//     if (!orderId || !billingInfo) {
//       toast.error('No order or billing information found');
//       return;
//     }

//     try {
//       const paymentData = {
//         order_id: orderId,
//         billing_id: billingInfo.billing_id,
//         amount: billingInfo.final_amount,
//         notes:
//           orderType === 'dining'
//             ? `Cash payment for table ${tableNumber}`
//             : 'Cash payment for parcel order',
//       };

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/payments/cash`, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify(paymentData),
//       });

//       const data = await response.json();

//       if (response.ok) {
//         toast.success(`Payment successful! Change due: ₹${data.change_due}`);
//         setShowPaymentModal(false);
//         if (orderType === 'dining') {
//           setCurrentOrder(null);
//           setOrderId(null);
//           setOrderItems([]);
//           setBillingInfo(null);
//           setTableNumber('');
//           setTableSearchText('');
//           setCart([]);
//           setSpecialRequest('');
//           setCustomerName('');
//           setCustomerEmail('');
//           setPhoneNumber('');
//           setGstNumber('');
//           setNumberOfGuests(1);
//           await fetchTables();
//         } else {
//           await fetchParcelOrderDetails(orderId);
//           await fetchParcelOrders();
//         }
//       } else {
//         throw new Error(data.detail || 'Payment failed');
//       }
//     } catch (error) {
//       console.error('Cash payment error:', error);
//       toast.error(error.message || 'Failed to process cash payment');
//     }
//   };

//   const handleRazorpayPayment = async () => {
//     if (!razorpayLoaded) {
//       toast.error('Razorpay script is not loaded yet.');
//       return;
//     }

//     if (!orderId || !billingInfo) {
//       toast.error('No order or billing information found');
//       return;
//     }

//     try {
//       const apiUrl = `${BQ_BASE_URL}/bq/api/restaurant/payments/razorpay/create-order`;

//       const response = await fetch(apiUrl, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Accept: 'application/json',
//         },
//         body: JSON.stringify({
//           order_id: orderId,
//           billing_id: billingInfo.billing_id,
//         }),
//       });

//       if (!response.ok) {
//         const errorData = await response.json();
//         throw new Error(errorData.detail || 'Failed to create payment order');
//       }

//       const orderData = await response.json();

//       if (orderData && orderData.razorpay_order_id) {
//         const options = {
//           key: REACT_APP_RAZORPAY_KEY,
//           amount: orderData.amount,
//           currency: 'INR',
//           name: 'Pagoda Hotel',
//           description:
//             orderType === 'dining' ? `Restaurant Order - Table ${tableNumber}` : 'Parcel Order',
//           order_id: orderData.razorpay_order_id,
//           handler: async function (response) {
//             const paymentDetails = {
//               razorpay_order_id: response.razorpay_order_id,
//               razorpay_payment_id: response.razorpay_payment_id,
//               razorpay_signature: response.razorpay_signature,
//             };

//             try {
//               const verificationResponse = await fetch(
//                 `${BQ_BASE_URL}/bq/api/restaurant/payments/razorpay/verify`,
//                 {
//                   method: 'POST',
//                   headers: {
//                     'Content-Type': 'application/json',
//                     Accept: 'application/json',
//                   },
//                   body: JSON.stringify(paymentDetails),
//                 }
//               );

//               if (verificationResponse.ok) {
//                 toast.success('Payment successful and verified!');
//                 setShowPaymentModal(false);
//                 if (orderType === 'dining') {
//                   setCurrentOrder(null);
//                   setOrderId(null);
//                   setOrderItems([]);
//                   setBillingInfo(null);
//                   setTableNumber('');
//                   setTableSearchText('');
//                   setCart([]);
//                   setSpecialRequest('');
//                   await fetchTables();
//                 } else {
//                   await fetchParcelOrderDetails(orderId);
//                   await fetchParcelOrders();
//                 }
//               } else {
//                 toast.error('Payment verification failed.');
//               }
//             } catch (error) {
//               console.error('Verification error:', error);
//               toast.error('Error verifying payment. Please try again.');
//             }
//           },
//           prefill: {
//             name: customerName || 'Guest',
//             email: customerEmail || 'guest@example.com',
//             contact: phoneNumber || '9999999999',
//           },
//           theme: { color: '#3399cc' },
//         };

//         const rzp = new window.Razorpay(options);
//         rzp.open();
//       }
//     } catch (error) {
//       console.error('Payment error:', error);
//       toast.error(error.message || 'Error processing payment. Please try again.');
//     }
//   };

//   const handleRoomCashPayment = async () => {
//     if (!roomOrderId || !roomBillingInfo) {
//       toast.error('No room order or billing information found');
//       return;
//     }

//     if (!selectedRoomOrder?.booking_id) {
//       toast.error('Booking ID not found for this room order');
//       return;
//     }

//     setIsProcessingPayment(true);
//     try {
//       const paymentData = {
//         booking_id: selectedRoomOrder.booking_id,
//         billing_id: roomBillingInfo.billing_id,
//         amount: roomBillingInfo.final_amount,
//       };

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/payments/cash`, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify(paymentData),
//       });

//       const data = await response.json();

//       if (response.ok) {
//         toast.success(`Payment successful!`);
//         setShowPaymentModal(false);
//         setRoomOrderId(null);
//         setRoomBillingInfo(null);
//         setRoomOrderItems([]);
//         setSelectedRoomOrder(null);
//         setRoomSearchText('');
//         setRoomTableSearchText('');
//         await fetchRoomOrders();
//         await fetchAvailableTablesForRoom();
//         await fetchTables();
//         setCart([]);
//         setSpecialRequest('');
//       } else {
//         throw new Error(data.detail || 'Payment failed');
//       }
//     } catch (error) {
//       console.error('Cash payment error:', error);
//       toast.error(error.message || 'Failed to process cash payment');
//     } finally {
//       setIsProcessingPayment(false);
//     }
//   };

//   const handleRoomRazorpayPayment = async () => {
//     if (!razorpayLoaded) {
//       toast.error('Razorpay script is not loaded yet.');
//       return;
//     }

//     if (!roomOrderId || !roomBillingInfo) {
//       toast.error('No room order or billing information found');
//       return;
//     }

//     if (!selectedRoomOrder?.booking_id) {
//       toast.error('Booking ID not found for this room order');
//       return;
//     }

//     setIsProcessingPayment(true);

//     try {
//       const apiUrl = `${BQ_BASE_URL}/bq/api/razorpay/create_payment_order?billingid=${roomBillingInfo.billing_id}&bookingid=${selectedRoomOrder.booking_id}&strict_target=true`;

//       const response = await fetch(apiUrl, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Accept: 'application/json',
//         },
//         body: '',
//       });

//       if (!response.ok) {
//         const errorData = await response.json();
//         throw new Error(errorData.detail || 'Failed to create payment order');
//       }

//       const orderData = await response.json();

//       if (orderData && orderData.razorpay_order_id) {
//         const options = {
//           key: REACT_APP_RAZORPAY_KEY,
//           amount: orderData.amount,
//           currency: 'INR',
//           name: 'Pagoda Hotel',
//           description: `Room Order - Room ${roomNumber}`,
//           order_id: orderData.razorpay_order_id,
//           handler: async function (response) {
//             const paymentDetails = {
//               razorpay_order_id: response.razorpay_order_id,
//               razorpay_payment_id: response.razorpay_payment_id,
//               razorpay_signature: response.razorpay_signature,
//             };

//             try {
//               const verificationResponse = await fetch(
//                 `${BQ_BASE_URL}/bq/api/razorpay/verify_payment`,
//                 {
//                   method: 'POST',
//                   headers: {
//                     'Content-Type': 'application/json',
//                     Accept: 'application/json',
//                   },
//                   body: JSON.stringify(paymentDetails),
//                 }
//               );

//               if (verificationResponse.ok) {
//                 toast.success('Payment successful and verified!');
//                 setShowPaymentModal(false);
//                 setRoomOrderId(null);
//                 setRoomBillingInfo(null);
//                 setRoomOrderItems([]);
//                 setSelectedRoomOrder(null);
//                 setRoomSearchText('');
//                 setRoomTableSearchText('');
//                 await fetchRoomOrders();
//                 await fetchAvailableTablesForRoom();
//                 await fetchTables();
//                 setCart([]);
//                 setSpecialRequest('');
//               } else {
//                 toast.error('Payment verification failed.');
//               }
//             } catch (error) {
//               console.error('Verification error:', error);
//               toast.error('Error verifying payment. Please try again.');
//             } finally {
//               setIsProcessingPayment(false);
//             }
//           },
//           prefill: {
//             name: customerName || 'Guest',
//             email: customerEmail || 'guest@example.com',
//             contact: phoneNumber || '9999999999',
//           },
//           theme: { color: '#3399cc' },
//         };

//         const rzp = new window.Razorpay(options);
//         rzp.open();
//       } else {
//         setIsProcessingPayment(false);
//       }
//     } catch (error) {
//       console.error('Payment error:', error);
//       toast.error(error.message || 'Error processing payment. Please try again.');
//       setIsProcessingPayment(false);
//     }
//   };

//   const handlePaymentOption = (method) => {
//     setSelectedPaymentMethod(method);
//     setShowPaymentModal(true);
//   };

//   const confirmPayment = async () => {
//     try {
//       if (orderType === 'dining' || orderType === 'parcel') {
//         if (selectedPaymentMethod === 'cash') {
//           await handleCashPayment();
//         } else if (selectedPaymentMethod === 'razorpay') {
//           await handleRazorpayPayment();
//         }
//       } else if (orderType === 'room') {
//         if (selectedPaymentMethod === 'cash') {
//           await handleRoomCashPayment();
//         } else if (selectedPaymentMethod === 'razorpay') {
//           await handleRoomRazorpayPayment();
//         }
//       }
//     } finally {
//       setSelectedPaymentMethod(null);
//     }
//   };

//   const handlePrintInvoice = async () => {
//     if (orderType === 'dining' || orderType === 'parcel') {
//       if (!orderId) {
//         toast.error('No order found');
//         return;
//       }

//       setIsPrinting(true);
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/print/${orderId}`, {
//           method: 'POST',
//           headers: { 'Content-Type': 'application/json' },
//         });

//         const data = await response.json();

//         if (response.ok) {
//           toast.success('Invoice printed successfully!');
//         } else {
//           toast.error(data.detail || 'Failed to print invoice');
//         }
//       } catch (error) {
//         console.error('Print error:', error);
//         toast.error('Failed to print invoice');
//       } finally {
//         setIsPrinting(false);
//       }
//     } else if (orderType === 'room') {
//       if (!roomOrderId) {
//         toast.error('No room order found');
//         return;
//       }

//       setIsPrinting(true);
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/food/print/${roomOrderId}`, {
//           method: 'POST',
//           headers: { 'Content-Type': 'application/json' },
//         });

//         const data = await response.json();

//         if (response.ok) {
//           toast.success(`Room invoice printed successfully!`);
//         } else {
//           toast.error(data.detail || 'Failed to print room invoice');
//         }
//       } catch (error) {
//         console.error('Print error:', error);
//         toast.error('Failed to print room invoice');
//       } finally {
//         setIsPrinting(false);
//       }
//     } else {
//       toast.error('No order found');
//     }
//   };

//   const filteredItems = menuItems.filter((item) => {
//     if (item.status && item.status.toLowerCase() === 'delivered') {
//       return false;
//     }

//     const categoryid = getCategoryIdFromSubCategoryId(item.subCategoryId);
//     const matchesCategory = selectedCategory ? categoryid === selectedCategory : true;
//     const matchesSubcategory = selectedSubcategory
//       ? item.subCategoryId === selectedSubcategory
//       : true;

//     const searchLower = search.toLowerCase();
//     const matchesTitle = item.title.toLowerCase().includes(searchLower);
//     const matchesItemcode =
//       !isNaN(search) && search.trim() !== '' ? item.itemcode === parseInt(search) : false;
//     const matchesSearch = matchesTitle || matchesItemcode;

//     return matchesCategory && matchesSubcategory && matchesSearch;
//   });

//   if (loading) {
//     return (
//       <div className="min-h-screen bg-white flex items-center justify-center">
//         <div className="text-center">
//           <div className="w-12 h-12 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
//           <p className="text-gray-400">Loading menu...</p>
//         </div>
//       </div>
//     );
//   }

//   // Determine cancelled states for the header/banner
//   const activeOrderIsCancelled =
//     orderType === 'dining' || orderType === 'parcel'
//       ? isOrderCancelled(currentOrder) || isOrderCancelled(selectedParcelOrder)
//       : orderType === 'room'
//         ? isOrderCancelled(selectedRoomOrder)
//         : false;

//   const hasItemsInOrder = orderType === 'room' ? roomOrderItems.length > 0 : orderItems.length > 0;

//   const allOrderItems = orderType === 'room' ? roomOrderItems : orderItems;
//   const activeItemCount = allOrderItems.filter((it) => !isItemCancelled(it)).length;
//   const cancelledItemCount = allOrderItems.filter((it) => isItemCancelled(it)).length;

//   return (
//     <div className="min-h-screen bg-gray-50 p-6">
//       <header className="flex justify-between items-center mb-3 bg-white p-3">
//         <h1 className="text-black text-xl font-bold ml-2">Food & Beverages Management</h1>
//         <div className="flex space-x-6">
//           <button
//             onClick={() => navigate('/dashboard/core-modules/f&b-management/track-order-frontdesk')}
//             className="btn "
//           >
//             Order Overview
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/f&b-management/order-food')}
//             className="btn font-bold text-black"
//           >
//             Order Food
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/f&b-management/dining-tables')}
//             className="btn "
//           >
//             Dining Tables
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/f&b-management/track-order-kitchen')}
//             className="btn"
//           >
//             Kitchen Display System(KDS)
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/f&b-management/order-invoicing')}
//             className="btn text-gray-600 hover:text-black"
//           >
//             Billing & Invoicing
//           </button>
//         </div>
//       </header>

//       <div className="flex flex-col lg:flex-row">
//         <div className="flex-1 overflow-y-auto" style={{ height: 'calc(100vh - 80px)' }}>
//           <div className="p-2">
//             <div className="sticky top-0 z-10 bg-white pt-2 pb-2">
//               <div className="relative">
//                 <input
//                   type="text"
//                   placeholder="Search by name or item code.."
//                   value={search}
//                   onChange={(e) => setSearch(e.target.value)}
//                   className="w-full px-5 py-3 pl-11 pr-12 border border-gray-200 rounded-lg transition-all bg-white shadow-sm"
//                 />
//                 <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-300 text-sm" />

//                 {isSupported && (
//                   <button
//                     onClick={(e) => {
//                       e.preventDefault();
//                       e.stopPropagation();
//                       if (isListening && activeSpeechTarget === 'search') {
//                         stopSearchListening();
//                       } else {
//                         startSearchListening();
//                       }
//                     }}
//                     className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-2 rounded-full transition-all z-10 ${
//                       isListening && activeSpeechTarget === 'search'
//                         ? 'text-red-500 bg-red-50 animate-pulse scale-110'
//                         : 'text-gray-400 hover:text-black hover:bg-gray-100'
//                     }`}
//                     title={
//                       isListening && activeSpeechTarget === 'search'
//                         ? 'Stop listening'
//                         : 'Search by voice'
//                     }
//                     type="button"
//                   >
//                     <FaMicrophone size={18} />
//                   </button>
//                 )}

//                 {isListening && activeSpeechTarget === 'search' && (
//                   <div className="absolute -bottom-6 left-0 right-0 text-xs text-red-500 font-medium text-center animate-pulse">
//                     🎤 Listening for search... Speak now
//                   </div>
//                 )}
//               </div>
//             </div>

//             <div className="overflow-x-auto mb-2 pb-2 mt-2">
//               <div className="flex gap-1 min-w-min">
//                 <button
//                   onClick={() => {
//                     setSelectedCategory(null);
//                     setSelectedSubcategory(null);
//                   }}
//                   className={`px-3 py-2 text-[13px] lg:text-[13px] rounded-full transition-all whitespace-nowrap ${
//                     selectedCategory === null
//                       ? 'bg-black text-white'
//                       : 'bg-white text-gray-600 border border-gray-200 hover:border-black'
//                   }`}
//                 >
//                   All
//                 </button>
//                 {categories.map((category) => (
//                   <button
//                     key={category.id}
//                     onClick={() => {
//                       setSelectedCategory(category.id);
//                       setSelectedSubcategory(null);
//                     }}
//                     className={`px-3 sm:px-3 py-2 text-[13px] lg:text-[14px] font-medium rounded-full transition-all whitespace-nowrap ${
//                       selectedCategory === category.id
//                         ? 'bg-black text-white'
//                         : 'bg-white text-gray-600 border border-gray-200 hover:border-black'
//                     }`}
//                   >
//                     {category.name}
//                   </button>
//                 ))}
//               </div>
//             </div>

//             {selectedCategory &&
//               subcategories.filter((s) => s.categoryid === selectedCategory).length > 0 && (
//                 <div className="overflow-x-auto mb-5 pb-2">
//                   <div className="flex gap-2 min-w-min">
//                     <button
//                       onClick={() => setSelectedSubcategory(null)}
//                       className={`px-3 sm:px-4 py-1.5 text-xs rounded-full transition-all whitespace-nowrap ${
//                         selectedSubcategory === null
//                           ? 'bg-black text-white'
//                           : 'bg-white text-gray-500 border border-gray-200 hover:border-black'
//                       }`}
//                     >
//                       All
//                     </button>
//                     {subcategories
//                       .filter((sub) => sub.categoryid === selectedCategory)
//                       .map((sub) => (
//                         <button
//                           key={sub.id}
//                           onClick={() => setSelectedSubcategory(sub.id)}
//                           className={`px-3 sm:px-4 py-1.5 text-xs rounded-full transition-all whitespace-nowrap ${
//                             selectedSubcategory === sub.id
//                               ? 'bg-black text-white'
//                               : 'bg-white text-gray-500 border border-gray-200 hover:border-black'
//                           }`}
//                         >
//                           {sub.name}
//                         </button>
//                       ))}
//                   </div>
//                 </div>
//               )}

//             <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 gap-2">
//               {filteredItems.map((item) => (
//                 <div
//                   key={item.id}
//                   className="group border border-gray-100 rounded-xl hover:shadow-md transition-all duration-300 bg-white overflow-hidden"
//                 >
//                   <div className="relative h-40 bg-gray-50">
//                     {item.image ? (
//                       <img
//                         src={item.image}
//                         alt={item.title}
//                         className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
//                         loading="lazy"
//                         onError={(e) => {
//                           e.target.style.display = 'none';
//                           e.target.parentElement.innerHTML = `
//           <div class="flex items-center justify-center h-full bg-gray-100">
//             <div class="text-center">
//               <FaImage class="text-4xl text-gray-300 mx-auto mb-2" />
//               <span class="text-gray-400 text-xs">No Image Uploaded</span>
//             </div>
//           </div>
//         `;
//                         }}
//                       />
//                     ) : (
//                       <div className="flex items-center justify-center h-full bg-gray-100">
//                         <div className="text-center">
//                           <FaImage className="text-4xl text-gray-300 mx-auto mb-2" />
//                           <span className="text-gray-400 text-xs">No Image Uploaded</span>
//                         </div>
//                       </div>
//                     )}

//                     <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-1 sm:gap-2">
//                       {item.foodType === 'Veg' ? (
//                         <FaLeaf className="text-green-600 bg-white p-1 rounded-full text-base sm:text-lg" />
//                       ) : (
//                         <FaDrumstickBite className="text-orange-600 bg-white p-1 rounded-full text-base sm:text-lg" />
//                       )}

//                       {item.itemcode > 0 && (
//                         <span className="bg-purple-100 text-purple-800 text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full flex items-center gap-1">
//                           <FaBarcode className="text-xs" />
//                           Code: {item.itemcode}
//                         </span>
//                       )}
//                     </div>

//                     {!item.isActive && (
//                       <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
//                         <span className="bg-white text-black px-3 py-1 rounded-full text-xs font-medium">
//                           Unavailable
//                         </span>
//                       </div>
//                     )}
//                   </div>

//                   <div className="p-3 sm:p-4">
//                     <div className="flex justify-between items-start mb-2">
//                       <h3 className="font-semibold text-black text-sm">{item.title}</h3>

//                       {item.value > 0 && item.value_type && (
//                         <span className="bg-yellow-50 text-yellow-700 text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full flex items-center gap-1">
//                           <FaTag className="text-xs" />
//                           Tax:{item.value}
//                           {item.value_type === 'P' ? '%' : ' ₹'}
//                         </span>
//                       )}
//                     </div>

//                     <p className="text-gray-400 text-xs sm:text-sm mb-2 line-clamp-2">
//                       {item.description}
//                     </p>

//                     <div className="flex flex-wrap gap-1 sm:gap-2 mb-3">
//                       {item.price_type && (
//                         <span className="bg-blue-50 text-blue-700 text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full flex items-center gap-1">
//                           <span className="font-bold text-black">₹{item.price}</span>/
//                           {item.price_type}
//                         </span>
//                       )}
//                     </div>

//                     <button
//                       onClick={() => handleAddToCart(item)}
//                       disabled={!item.isActive}
//                       className={`w-full py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
//                         item.isActive
//                           ? justAddedItemId === item.id
//                             ? 'bg-green-500 text-white'
//                             : 'bg-black text-white hover:bg-gray-800'
//                           : 'bg-gray-100 text-gray-400 cursor-not-allowed'
//                       }`}
//                     >
//                       {justAddedItemId === item.id ? 'Added!' : 'Add to Cart'}
//                     </button>
//                   </div>
//                 </div>
//               ))}
//             </div>

//             {filteredItems.length === 0 && (
//               <div className="text-center py-20">
//                 <p className="text-gray-400">No items found</p>
//               </div>
//             )}
//           </div>
//         </div>

//         {/* Right Side - Order Panel */}
//         <div
//           className="w-full lg:w-[420px] border-l border-gray-100 bg-white flex flex-col"
//           style={{ height: 'calc(100vh - 80px)' }}
//         >
//           <div className="px-6 py-2 border-b border-gray-100">
//             <div className="flex justify-between items-center mb-4">
//               <div className="flex items-center gap-2">
//                 <FaShoppingCart className="text-black text-lg" />
//                 <h2 className="font-semibold text-black">Place Order</h2>
//               </div>
//               {cart.length > 0 && (
//                 <button
//                   onClick={clearAllFields}
//                   className="text-white text-sm transition-all flex items-center gap-1 px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600"
//                 >
//                   <FaTrash size={12} /> Clear All
//                 </button>
//               )}
//             </div>

//             {/* Cancelled order banner */}
//             {activeOrderIsCancelled && (
//               <div className="mb-3 rounded-xl border-2 border-red-200 bg-red-50 p-3 flex items-start gap-2">
//                 <FaBan className="text-red-600 mt-0.5 flex-shrink-0" />
//                 <div>
//                   <p className="text-sm font-semibold text-red-800">
//                     This order has been cancelled
//                   </p>
//                   <p className="text-xs text-red-600 mt-0.5">
//                     You can view the items below, but no changes are allowed.
//                   </p>
//                 </div>
//               </div>
//             )}

//             <div className="grid grid-cols-3 gap-2">
//               <button
//                 onClick={() => {
//                   setOrderType('dining');
//                 }}
//                 className={`py-2 text-sm rounded-lg transition-all ${
//                   orderType === 'dining'
//                     ? 'bg-black text-white'
//                     : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
//                 }`}
//               >
//                 <FaUsers className="inline mr-1 text-xs" /> Dine In
//               </button>
//               <button
//                 onClick={() => {
//                   setOrderType('room');
//                   setRoomOrderId(null);
//                   setRoomBillingInfo(null);
//                   setRoomOrderItems([]);
//                   setSelectedRoomOrder(null);
//                   setRoomSearchText('');
//                 }}
//                 className={`py-2 text-sm rounded-lg transition-all ${
//                   orderType === 'room'
//                     ? 'bg-black text-white'
//                     : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
//                 }`}
//               >
//                 <FaHotel className="inline mr-1 text-xs" /> Room
//               </button>
//               <button
//                 onClick={() => {
//                   setOrderType('parcel');
//                   setOrderId(null);
//                   setBillingInfo(null);
//                   setOrderItems([]);
//                   setSelectedParcelOrder(null);
//                   setParcelSearchText('');
//                 }}
//                 className={`py-2 text-sm rounded-lg transition-all ${
//                   orderType === 'parcel'
//                     ? 'bg-black text-white'
//                     : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
//                 }`}
//               >
//                 <FaBox className="inline mr-1 text-xs" /> Parcel
//               </button>
//             </div>

//             {/* Table Selection Dropdown for Dining */}
//             {orderType === 'dining' && (
//               <div className="mt-4 relative">
//                 <div className="relative">
//                   <FaTable className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm z-10" />
//                   <input
//                     type="text"
//                     placeholder="Select or search table..."
//                     value={tableSearchText}
//                     onChange={(e) => {
//                       setTableSearchText(e.target.value);
//                       setShowTableDropdown(true);
//                     }}
//                     onFocus={() => setShowTableDropdown(true)}
//                     className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                   />
//                 </div>

//                 {showTableDropdown && filteredTables.length > 0 && (
//                   <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
//                     {filteredTables.map((table) => (
//                       <button
//                         key={table.id}
//                         onClick={() => handleTableSelect(table)}
//                         className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between border-b border-gray-100 last:border-0"
//                       >
//                         <div className="flex items-center gap-2">
//                           <FaCircle className={`${getStatusColor(table.status)} text-xs`} />
//                           <span className="font-medium">{table.table_number}</span>
//                           <span className="text-sm text-gray-500">({table.table_name})</span>
//                           <span className="text-xs text-gray-400">Cap: {table.capacity}</span>
//                         </div>
//                         <span
//                           className={`text-xs px-2 py-1 rounded-full ${
//                             table.status === 'available'
//                               ? 'bg-green-100 text-green-700'
//                               : table.status === 'occupied'
//                                 ? 'bg-red-100 text-red-700'
//                                 : 'bg-yellow-100 text-yellow-700'
//                           }`}
//                         >
//                           {getStatusText(table.status)}
//                         </span>
//                       </button>
//                     ))}
//                   </div>
//                 )}
//               </div>
//             )}

//             {/* Room Order Selection Dropdown */}
//             {orderType === 'room' && (
//               <div className="mt-4">
//                 <div className="relative">
//                   <div className="flex gap-2">
//                     <div className="relative flex-1">
//                       <FaHotel className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm z-10" />
//                       <input
//                         type="text"
//                         placeholder="Search room number or enter new room number..."
//                         value={roomSearchText}
//                         onChange={(e) => {
//                           const value = e.target.value;
//                           setRoomSearchText(value);
//                           setShowRoomDropdown(true);

//                           if (/^\d+$/.test(value)) {
//                             setRoomNumber(value);
//                             setSelectedRoomOrder(null);
//                             setRoomOrderId(null);
//                             setRoomOrderItems([]);
//                             setRoomBillingInfo(null);
//                             setCustomerName('');
//                             setPhoneNumber('');
//                             setSpecialRequest('');
//                           }
//                         }}
//                         onFocus={() => {
//                           setShowRoomDropdown(true);
//                         }}
//                         onBlur={() => {
//                           setTimeout(() => {
//                             setShowRoomDropdown(false);
//                           }, 200);
//                         }}
//                         className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                       />
//                     </div>
//                     <button
//                       onClick={handleNewRoomOrder}
//                       className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-all text-sm whitespace-nowrap"
//                     >
//                       + New Order
//                     </button>
//                   </div>

//                   {showRoomDropdown && filteredRoomOrders.length > 0 && (
//                     <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
//                       {filteredRoomOrders.map((order) => (
//                         <button
//                           key={order.id || order.order_id}
//                           onClick={() => {
//                             handleRoomSelect(order);
//                             setShowRoomDropdown(false);
//                           }}
//                           className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
//                         >
//                           <div className="flex justify-between items-center">
//                             <div>
//                               <div className="font-medium text-sm">Room {order.room_number}</div>
//                               <div className="text-xs text-gray-500">
//                                 {order.customer_name || 'Guest'}
//                                 {order.table_number && ` • Table ${order.table_number}`}
//                               </div>
//                             </div>
//                             <div className="text-right">
//                               <div className="font-semibold text-sm">
//                                 ₹{order.totalprice || order.total_amount || 0}
//                               </div>
//                               <div
//                                 className={`text-xs capitalize ${
//                                   order.status === 'Pending' || order.order_status === 'Pending'
//                                     ? 'text-yellow-600'
//                                     : order.status === 'Served' || order.order_status === 'Served'
//                                       ? 'text-blue-600'
//                                       : 'text-green-600'
//                                 }`}
//                               >
//                                 {order.status || order.order_status || 'Pending'}
//                               </div>
//                             </div>
//                           </div>
//                         </button>
//                       ))}
//                     </div>
//                   )}
//                 </div>

//                 <div className="mt-3 space-y-2">
//                   <button
//                     onClick={() => setShowAdditionalDetails(!showAdditionalDetails)}
//                     className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all"
//                   >
//                     <span className="text-sm font-medium text-gray-800">
//                       Additional Details (Optional)
//                     </span>
//                     {showAdditionalDetails ? (
//                       <FaChevronUp size={12} />
//                     ) : (
//                       <FaChevronDown size={12} />
//                     )}
//                   </button>

//                   {showAdditionalDetails && (
//                     <div className="mt-2 space-y-2 animate-fadeIn">
//                       <div className="relative">
//                         <FaTable className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm z-10" />
//                         <input
//                           type="text"
//                           placeholder="Select or search table number..."
//                           value={roomTableSearchText}
//                           onChange={(e) => {
//                             setRoomTableSearchText(e.target.value);
//                             setShowRoomTableDropdown(true);
//                             if (e.target.value) {
//                               setTableNumber(e.target.value);
//                             }
//                           }}
//                           onFocus={() => setShowRoomTableDropdown(true)}
//                           onBlur={() => {
//                             setTimeout(() => {
//                               setShowRoomTableDropdown(false);
//                             }, 200);
//                           }}
//                           className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                         />

//                         {showRoomTableDropdown && filteredRoomTables.length > 0 && (
//                           <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
//                             {filteredRoomTables.map((table) => (
//                               <button
//                                 key={table.id}
//                                 onClick={() => {
//                                   setTableNumber(table.table_number);
//                                   setRoomTableSearchText(
//                                     `${table.table_number} (${table.table_name || 'Table'})`
//                                   );
//                                   setShowRoomTableDropdown(false);
//                                 }}
//                                 className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-center justify-between"
//                               >
//                                 <div>
//                                   <span className="font-medium text-sm">{table.table_number}</span>
//                                   <span className="text-xs text-gray-500 ml-2">
//                                     {table.table_name}
//                                   </span>
//                                 </div>
//                                 <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
//                                   Available
//                                 </span>
//                               </button>
//                             ))}
//                           </div>
//                         )}
//                       </div>

//                       <div className="relative">
//                         <FaIdCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
//                         <input
//                           type="text"
//                           placeholder="GST Number"
//                           value={gstNumber}
//                           onChange={(e) => setGstNumber(e.target.value)}
//                           className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                           onClick={(e) => {
//                             e.stopPropagation();
//                           }}
//                         />
//                       </div>
//                     </div>
//                   )}
//                 </div>
//               </div>
//             )}

//             {/* Parcel Order Selection Dropdown */}
//             {orderType === 'parcel' && (
//               <div className="mt-4 relative">
//                 <div className="flex gap-2">
//                   <div className="relative flex-1">
//                     <FaList className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm z-10" />
//                     <input
//                       type="text"
//                       placeholder="Search parcel order by number, name or phone..."
//                       value={parcelSearchText}
//                       onChange={(e) => {
//                         setParcelSearchText(e.target.value);
//                         setShowParcelDropdown(true);
//                       }}
//                       onFocus={() => setShowParcelDropdown(true)}
//                       className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                     />
//                   </div>
//                   <button
//                     onClick={handleNewParcelOrder}
//                     className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-all text-sm whitespace-nowrap"
//                   >
//                     + New Order
//                   </button>
//                 </div>

//                 {showParcelDropdown && filteredParcelOrders.length > 0 && (
//                   <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
//                     {filteredParcelOrders.map((order) => (
//                       <button
//                         key={order.order_id}
//                         onClick={() => handleParcelSelect(order)}
//                         className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
//                       >
//                         <div className="flex justify-between items-center">
//                           <div>
//                             <div className="font-medium text-sm">{order.parcel_number}</div>
//                             <div className="text-xs text-gray-500">
//                               {order.customer_name || 'Guest'}
//                             </div>
//                           </div>
//                           <div className="text-right">
//                             <div className="font-semibold text-sm">₹{order.total_amount}</div>
//                             <div
//                               className={`text-xs ${
//                                 order.order_status === 'pending'
//                                   ? 'text-yellow-600'
//                                   : 'text-blue-600'
//                               }`}
//                             >
//                               {order.order_status}
//                             </div>
//                           </div>
//                         </div>
//                       </button>
//                     ))}
//                   </div>
//                 )}
//               </div>
//             )}

//             {/* Order Items Summary with Update Button */}
//             {((orderItems.length > 0 && (orderType === 'dining' || orderType === 'parcel')) ||
//               ((roomOrderItems.length > 0 || selectedRoomOrder) && orderType === 'room')) && (
//               <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
//                 <button
//                   onClick={() => setShowOrderItems(!showOrderItems)}
//                   className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
//                 >
//                   <div className="flex items-center gap-2">
//                     <FaList className="text-gray-400" />
//                     <span className="font-semibold text-sm">Order Items</span>
//                     <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
//                       {activeItemCount} active
//                     </span>
//                     {cancelledItemCount > 0 && (
//                       <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full flex items-center gap-1">
//                         <FaBan size={9} /> {cancelledItemCount} cancelled
//                       </span>
//                     )}
//                     {showOrderItems ? (
//                       <FaChevronUp size={12} className="text-gray-400" />
//                     ) : (
//                       <FaChevronDown size={12} className="text-gray-400" />
//                     )}
//                   </div>
//                   {(billingInfo || roomBillingInfo) && (
//                     <span className="font-bold text-sm text-black">
//                       ₹{billingInfo?.final_amount || roomBillingInfo?.final_amount || 0}
//                     </span>
//                   )}
//                 </button>

//                 {showOrderItems && (
//                   <div className="border-t border-gray-100">
//                     <div className="p-3 space-y-3 max-h-64 overflow-y-auto">
//                       {orderType === 'room' && roomOrderItems.length === 0 && (
//                         <div className="text-xs text-gray-500 text-center py-4">
//                           This order has no items to show. They may all have been delivered already.
//                         </div>
//                       )}
//                       {(orderType === 'room' ? roomOrderItems : orderItems).map((item, idx) => {
//                         const cancelled = isItemCancelled(item);
//                         const pendingQty =
//                           pendingQuantities[item.order_item_id] !== undefined
//                             ? pendingQuantities[item.order_item_id]
//                             : item.quantity || 1;

//                         const isQuantityChanged =
//                           !cancelled &&
//                           pendingQuantities[item.order_item_id] !== undefined &&
//                           pendingQuantities[item.order_item_id] !== item.quantity;

//                         return (
//                           <div
//                             key={idx}
//                             className={`rounded-lg p-3 border transition-all ${
//                               cancelled
//                                 ? 'bg-red-50/40 border-red-200 opacity-75'
//                                 : 'bg-gray-50 border-gray-100 hover:border-gray-200'
//                             }`}
//                           >
//                             <div className="flex justify-between items-start mb-2">
//                               <div className="flex-1">
//                                 <div className="flex items-center gap-2 flex-wrap">
//                                   <span
//                                     className={`font-medium text-sm ${
//                                       cancelled ? 'text-gray-400 line-through' : 'text-gray-800'
//                                     }`}
//                                   >
//                                     {item.food_name || item.description || 'Item'}
//                                   </span>
//                                   {cancelled && (
//                                     <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full flex items-center gap-1 font-semibold">
//                                       <FaBan size={9} /> CANCELLED
//                                     </span>
//                                   )}
//                                   {item.is_complimentary && (
//                                     <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
//                                       Complimentary
//                                     </span>
//                                   )}
//                                 </div>
//                               </div>
//                               <div className="flex items-center gap-3">
//                                 <span
//                                   className={`font-semibold text-sm ${
//                                     cancelled ? 'text-gray-400 line-through' : ''
//                                   }`}
//                                 >
//                                   ₹{((item.unit_price || 0) * (item.quantity || 1)).toFixed(2)}
//                                 </span>
//                                 <button
//                                   onClick={() => handleUpdateQuantityWithButton(item)}
//                                   disabled={cancelled || !isQuantityChanged}
//                                   className={`px-3 py-1 rounded-lg transition-all text-xs font-medium flex items-center gap-1 ${
//                                     cancelled
//                                       ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
//                                       : isQuantityChanged
//                                         ? 'bg-blue-600 text-white hover:bg-blue-700'
//                                         : 'bg-gray-200 text-gray-500 cursor-not-allowed'
//                                   }`}
//                                   title={
//                                     cancelled ? 'Cannot update a cancelled item' : 'Update quantity'
//                                   }
//                                 >
//                                   <FaCheckCircle size={12} />
//                                   Update
//                                 </button>
//                               </div>
//                             </div>

//                             <div className="flex items-center justify-between mt-2">
//                               <div className="flex items-center gap-3">
//                                 <div
//                                   className={`flex items-center gap-2 rounded-lg border px-2 py-1 ${
//                                     cancelled
//                                       ? 'bg-gray-100 border-gray-200 opacity-60'
//                                       : 'bg-white border-gray-200'
//                                   }`}
//                                 >
//                                   <button
//                                     onClick={() => {
//                                       if (cancelled) {
//                                         toast.info('This item is cancelled');
//                                         return;
//                                       }
//                                       const currentQty =
//                                         pendingQuantities[item.order_item_id] !== undefined
//                                           ? pendingQuantities[item.order_item_id]
//                                           : item.quantity || 1;
//                                       if (currentQty > 1) {
//                                         handlePendingQuantityChange(
//                                           item.order_item_id,
//                                           currentQty - 1
//                                         );
//                                       } else {
//                                         if (
//                                           window.confirm(
//                                             `Cancel ${item.food_name || 'this item'} from order?`
//                                           )
//                                         ) {
//                                           handleRemoveItem(item);
//                                         }
//                                       }
//                                     }}
//                                     disabled={cancelled}
//                                     className={`w-7 h-7 flex items-center justify-center rounded transition-all ${
//                                       cancelled
//                                         ? 'text-gray-300 cursor-not-allowed'
//                                         : 'hover:bg-gray-100 text-gray-600'
//                                     }`}
//                                     title="Decrease quantity"
//                                   >
//                                     <FaMinus size={11} />
//                                   </button>
//                                   <input
//                                     type="number"
//                                     min="1"
//                                     max="99"
//                                     value={pendingQty}
//                                     onChange={(e) => {
//                                       if (cancelled) return;
//                                       const val = parseInt(e.target.value);
//                                       if (!isNaN(val) && val > 0) {
//                                         handlePendingQuantityChange(item.order_item_id, val);
//                                       }
//                                     }}
//                                     disabled={cancelled}
//                                     className={`w-10 text-center text-sm font-semibold border-0 focus:outline-none bg-transparent ${
//                                       cancelled ? 'text-gray-400' : ''
//                                     }`}
//                                   />
//                                   <button
//                                     onClick={() => {
//                                       if (cancelled) {
//                                         toast.info('This item is cancelled');
//                                         return;
//                                       }
//                                       const currentQty =
//                                         pendingQuantities[item.order_item_id] !== undefined
//                                           ? pendingQuantities[item.order_item_id]
//                                           : item.quantity || 1;
//                                       handlePendingQuantityChange(
//                                         item.order_item_id,
//                                         currentQty + 1
//                                       );
//                                     }}
//                                     disabled={cancelled}
//                                     className={`w-7 h-7 flex items-center justify-center rounded transition-all ${
//                                       cancelled
//                                         ? 'text-gray-300 cursor-not-allowed'
//                                         : 'hover:bg-gray-100 text-gray-600'
//                                     }`}
//                                     title="Increase quantity"
//                                   >
//                                     <FaPlus size={11} />
//                                   </button>
//                                 </div>

//                                 <button
//                                   onClick={() => {
//                                     if (cancelled) {
//                                       toast.info('This item is already cancelled');
//                                       return;
//                                     }
//                                     if (
//                                       window.confirm(
//                                         `Cancel ${item.food_name || 'this item'} from order?`
//                                       )
//                                     ) {
//                                       handleRemoveItem(item);
//                                     }
//                                   }}
//                                   disabled={cancelled}
//                                   className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all text-xs font-medium ${
//                                     cancelled
//                                       ? 'text-gray-400 cursor-not-allowed'
//                                       : 'text-red-600 hover:bg-red-50'
//                                   }`}
//                                 >
//                                   {cancelled ? <FaBan size={11} /> : <FaTrash size={11} />}
//                                   {cancelled ? 'Cancelled' : 'Cancel Item'}
//                                 </button>
//                               </div>

//                               <div className="flex items-center gap-2">
//                                 <span
//                                   className={`text-xs ${
//                                     cancelled ? 'text-gray-400' : 'text-gray-500'
//                                   }`}
//                                 >
//                                   Qty: {item.quantity || 1}
//                                 </span>
//                                 {isQuantityChanged && (
//                                   <span className="text-xs text-blue-600 font-medium">
//                                     → {pendingQty}
//                                   </span>
//                                 )}
//                               </div>
//                             </div>
//                           </div>
//                         );
//                       })}
//                     </div>

//                     {activeOrderIsCancelled ? (
//                       <div className="border-t border-red-200 px-3 py-2 bg-red-50 flex items-center justify-center gap-2">
//                         <FaBan className="text-red-600" size={12} />
//                         <span className="text-xs text-red-700 font-semibold">
//                           Order cancelled — no further actions allowed
//                         </span>
//                       </div>
//                     ) : (
//                       <>
//                         {(orderType === 'dining' || orderType === 'parcel') &&
//                           orderItems.length > 0 && (
//                             <div className="border-t border-gray-100 px-3 py-2 bg-gray-50 flex justify-between items-center">
//                               <button
//                                 onClick={handleClearAllItems}
//                                 className="text-xs text-red-600 hover:text-red-800 font-medium flex items-center gap-1 transition-colors"
//                               >
//                                 <FaTrash size={10} />
//                                 Cancel Entire Order
//                               </button>
//                               <span className="text-xs text-gray-500">
//                                 {orderItems.length} items in order
//                               </span>
//                             </div>
//                           )}

//                         {orderType === 'room' && roomOrderItems.length > 0 && (
//                           <div className="border-t border-gray-100 px-3 py-2 bg-gray-50 flex justify-between items-center">
//                             <button
//                               onClick={handleClearAllItems}
//                               className="text-xs text-red-600 hover:text-red-800 font-medium flex items-center gap-1 transition-colors"
//                             >
//                               <FaTrash size={10} />
//                               Cancel Entire Order
//                             </button>
//                             <span className="text-xs text-gray-500">
//                               {roomOrderItems.length} items in order
//                             </span>
//                           </div>
//                         )}
//                       </>
//                     )}
//                   </div>
//                 )}
//               </div>
//             )}

//             {/* Customer Details for Dining & Parcel with Company Search INSIDE */}
//             {(orderType === 'dining' || orderType === 'parcel') && !orderId && (
//               <div className="mt-2">
//                 <button
//                   onClick={() => setShowAdditionalDetails(!showAdditionalDetails)}
//                   className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all"
//                 >
//                   <span className="text-sm font-medium text-gray-800">
//                     Customer Details (Optional)
//                   </span>
//                   {showAdditionalDetails ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
//                 </button>

//                 {showAdditionalDetails && (
//                   <div className="mt-2 space-y-2 animate-fadeIn">
//                     <div className="relative" ref={companyDropdownRef}>
//                       <div className="relative">
//                         <FaBuilding className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm z-10" />
//                         <input
//                           type="text"
//                           placeholder="Search company name..."
//                           value={companySearchText}
//                           onChange={(e) => {
//                             setCompanySearchText(e.target.value);
//                             setSelectedCompany(null);
//                             if (!e.target.value) {
//                               setGstNumber('');
//                             }
//                           }}
//                           onFocus={() => {
//                             if (companySearchText.length > 1) {
//                               setShowCompanyDropdown(true);
//                             }
//                           }}
//                           className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                         />
//                       </div>

//                       {showCompanyDropdown && filteredCompanies.length > 0 && (
//                         <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
//                           {filteredCompanies.map((company) => (
//                             <button
//                               key={company.id}
//                               onClick={() => handleCompanySelect(company)}
//                               className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
//                             >
//                               <div className="flex justify-between items-center">
//                                 <div>
//                                   <div className="font-medium text-sm">{company.companyname}</div>
//                                   {company.companyid && (
//                                     <div className="text-xs text-gray-400">
//                                       ID: {company.companyid}
//                                     </div>
//                                   )}
//                                 </div>
//                                 {company.gstnumber && (
//                                   <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
//                                     GST: {company.gstnumber}
//                                   </span>
//                                 )}
//                               </div>
//                             </button>
//                           ))}
//                         </div>
//                       )}
//                     </div>

//                     <div className="relative">
//                       <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
//                       <input
//                         type="text"
//                         placeholder="Customer name"
//                         value={customerName}
//                         onChange={(e) => setCustomerName(e.target.value)}
//                         className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                       />
//                     </div>

//                     <div className="relative">
//                       <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
//                       <input
//                         type="email"
//                         placeholder="Email address"
//                         value={customerEmail}
//                         onChange={(e) => setCustomerEmail(e.target.value)}
//                         className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                       />
//                     </div>

//                     <div className="relative">
//                       <FaPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
//                       <input
//                         type="tel"
//                         placeholder="Phone number"
//                         value={phoneNumber}
//                         onChange={(e) => setPhoneNumber(e.target.value)}
//                         className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                       />
//                     </div>

//                     <div className="relative">
//                       <FaIdCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
//                       <input
//                         type="text"
//                         placeholder="GST number"
//                         value={gstNumber}
//                         onChange={(e) => setGstNumber(e.target.value)}
//                         className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                       />
//                     </div>
//                   </div>
//                 )}
//               </div>
//             )}

//             {/* Number of Guests for Dining */}
//             {orderType === 'dining' && !orderId && (
//               <div className="mt-2">
//                 <div className="flex items-center justify-between">
//                   <div className="flex items-center gap-2">
//                     <FaUsers className="text-gray-400 text-sm" />
//                     <span className="text-sm text-gray-600">Number of guests</span>
//                   </div>
//                   <div className="flex items-center gap-3">
//                     <button
//                       onClick={handleDecreaseGuests}
//                       className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center hover:border-black transition-all"
//                     >
//                       <FaMinus size={10} />
//                     </button>
//                     <span className="text-sm font-medium w-6 text-center">{numberOfGuests}</span>
//                     <button
//                       onClick={handleIncreaseGuests}
//                       className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center hover:border-black transition-all"
//                     >
//                       <FaPlus size={10} />
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             )}
//           </div>

//           <div className="flex-1 overflow-y-auto px-6 py-2">
//             {cart.length === 0 ? (
//               <div className="text-center py-5">
//                 <FaShoppingCart className="text-4xl text-gray-200 mx-auto mb-3" />
//                 <p className="text-gray-400 text-sm">Cart is empty</p>
//               </div>
//             ) : (
//               <div className="space-y-4">
//                 {cart.map((item) => (
//                   <div key={item.id} className="border-b border-gray-100 pb-4">
//                     <div className="flex justify-between mb-2">
//                       <div>
//                         <h4 className="font-medium text-black text-sm">{item.title}</h4>
//                         <div className="flex flex-wrap gap-2 mt-1">
//                           <span className="text-gray-400 text-xs">₹{item.price}</span>
//                           {item.price_type && (
//                             <span className="text-gray-400 text-xs">/{item.price_type}</span>
//                           )}
//                           {item.value > 0 && item.value_type && (
//                             <span className="text-gray-400 text-xs">
//                               Tax: {item.value}
//                               {item.value_type === 'P' ? '%' : ' ₹'}
//                             </span>
//                           )}
//                         </div>
//                       </div>
//                       <button
//                         onClick={() => handleRemoveFromCart(item.id)}
//                         className="text-gray-300 hover:text-black"
//                       >
//                         <FaTimes size={12} />
//                       </button>
//                     </div>
//                     <div className="flex justify-between items-center">
//                       <div className="flex items-center gap-3">
//                         <button
//                           onClick={() => handleDecreaseQuantity(item.id)}
//                           className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center hover:border-black transition-all"
//                         >
//                           <FaMinus size={10} />
//                         </button>
//                         <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
//                         <button
//                           onClick={() => handleIncreaseQuantity(item.id)}
//                           className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center hover:border-black transition-all"
//                         >
//                           <FaPlus size={10} />
//                         </button>
//                       </div>
//                       <span className="font-medium">₹{item.price * item.quantity}</span>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </div>

//           {cart.length > 0 && (
//             <div className="border-t border-gray-100 px-6 py-2">
//               <div className="relative">
//                 <textarea
//                   placeholder="Special instructions (optional)..."
//                   value={specialRequest}
//                   onChange={(e) => setSpecialRequest(e.target.value)}
//                   rows={1}
//                   className="w-full px-3 py-2 pr-12 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black resize-none"
//                 />
//                 {isSupported && (
//                   <button
//                     onClick={(e) => {
//                       e.preventDefault();
//                       e.stopPropagation();
//                       if (isListening && activeSpeechTarget === 'special') {
//                         stopSpecialListening();
//                       } else {
//                         startSpecialListening();
//                       }
//                     }}
//                     className={`absolute right-2 bottom-2 p-1.5 rounded-full transition-all z-10 ${
//                       isListening && activeSpeechTarget === 'special'
//                         ? 'text-red-500 bg-red-50 animate-pulse scale-110'
//                         : 'text-gray-400 hover:text-black hover:bg-gray-100'
//                     }`}
//                     title={
//                       isListening && activeSpeechTarget === 'special'
//                         ? 'Stop listening'
//                         : 'Add special instructions by voice'
//                     }
//                     type="button"
//                   >
//                     <FaMicrophone size={16} />
//                   </button>
//                 )}
//                 {isListening && activeSpeechTarget === 'special' && (
//                   <div className="absolute -bottom-5 left-0 right-0 text-xs text-red-500 font-medium text-center animate-pulse">
//                     🎤 Listening for instructions... Speak now
//                   </div>
//                 )}
//               </div>
//               {!orderId && !roomOrderId && (
//                 <div className="mt-1">
//                   <div className="flex items-center gap-3">
//                     <div className="flex-[2]">
//                       <label className="text-xs text-gray-500 font-medium block mb-1">
//                         Discount (%)
//                       </label>
//                       <div className="relative">
//                         <input
//                           type="text"
//                           value={discountPercent || ''}
//                           onChange={(e) => {
//                             const rawValue = e.target.value;
//                             const cleanValue = rawValue.replace(/[^0-9.]/g, '');

//                             let processedValue = cleanValue;
//                             if (
//                               processedValue.length > 1 &&
//                               processedValue.startsWith('0') &&
//                               processedValue[1] !== '.'
//                             ) {
//                               processedValue = processedValue.replace(/^0+/, '');
//                             }

//                             if (processedValue === '') {
//                               setDiscountPercent(0);
//                               return;
//                             }

//                             const value = parseFloat(processedValue);
//                             if (isNaN(value)) return;

//                             if (value > 25) {
//                               toast.error('Discount cannot exceed 25%');
//                               setDiscountPercent(25);
//                               return;
//                             }
//                             if (value < 0) {
//                               setDiscountPercent(0);
//                               return;
//                             }
//                             setDiscountPercent(value);
//                           }}
//                           className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-black ${
//                             discountError ? 'border-red-500' : 'border-gray-200'
//                           }`}
//                           placeholder="Enter discount %"
//                           inputMode="decimal"
//                         />
//                         <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">
//                           Max 25%
//                         </span>
//                       </div>
//                       {discountError && (
//                         <p className="text-red-500 text-xs mt-1">{discountError}</p>
//                       )}
//                     </div>
//                     {discountAmount > 0 && (
//                       <div className="flex-1">
//                         <label className="text-xs text-gray-500 font-medium block mb-1">
//                           Discount Amount
//                         </label>
//                         <div className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm font-semibold text-green-700">
//                           -₹{discountAmount.toFixed(2)}
//                         </div>
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               )}

//               <div className="space-y-2 mt-3">
//                 <div className="flex justify-between text-sm">
//                   <span className="text-gray-500">Subtotal (Food)</span>
//                   <span>₹{totalAmount.toFixed(2)}</span>
//                 </div>

//                 {!orderId && !roomOrderId && discountAmount > 0 && (
//                   <div className="flex justify-between text-sm text-green-600">
//                     <span>Discount ({discountPercent}%)</span>
//                     <span>-₹{discountAmount.toFixed(2)}</span>
//                   </div>
//                 )}

//                 {!orderId && !roomOrderId ? (
//                   <div className="flex justify-between text-sm border-t border-dashed border-gray-200 pt-1">
//                     <span className="text-gray-600 font-medium">Taxable Amount</span>
//                     <span className="font-medium">₹{taxableAmount.toFixed(2)}</span>
//                   </div>
//                 ) : (
//                   <div className="flex justify-between text-sm border-t border-dashed border-gray-200 pt-1">
//                     <span className="text-gray-600 font-medium">Taxable Amount</span>
//                     <span className="font-medium">₹{totalAmount.toFixed(2)}</span>
//                   </div>
//                 )}

//                 {orderType === 'dining' && taxes > 0 ? (
//                   <>
//                     <div className="flex justify-between text-sm items-center">
//                       <div className="flex items-center gap-2">
//                         <span className="text-gray-500">Taxes (incl. GST)</span>
//                         <button
//                           onClick={() => setShowTaxBreakdown(!showTaxBreakdown)}
//                           className="text-gray-400 hover:text-black transition-colors"
//                           title="View tax breakdown"
//                         >
//                           {showTaxBreakdown ? (
//                             <FaChevronUp size={10} />
//                           ) : (
//                             <FaChevronDown size={10} />
//                           )}
//                         </button>
//                       </div>
//                       <span>₹{taxes.toFixed(2)}</span>
//                     </div>

//                     {showTaxBreakdown && (
//                       <div className="ml-4 pl-2 border-l-2 border-gray-200 space-y-1 mt-1 animate-fadeIn">
//                         <div className="flex justify-between text-xs text-gray-500">
//                           <span>CGST</span>
//                           <span>₹{cgstAmount.toFixed(2)}</span>
//                         </div>
//                         <div className="flex justify-between text-xs text-gray-500">
//                           <span>SGST</span>
//                           <span>₹{sgstAmount.toFixed(2)}</span>
//                         </div>
//                       </div>
//                     )}
//                   </>
//                 ) : (
//                   <div className="flex justify-between text-sm">
//                     <span className="text-gray-500">Taxes</span>
//                     <span>₹{taxes.toFixed(2)}</span>
//                   </div>
//                 )}

//                 <div className="border-t border-gray-200 pt-2 mt-2">
//                   <div className="flex justify-between font-semibold text-lg">
//                     <span>Total</span>
//                     <span
//                       className={
//                         !orderId && !roomOrderId && discountAmount > 0 ? 'text-green-700' : ''
//                       }
//                     >
//                       {!orderId && !roomOrderId
//                         ? `₹${finalTotalWithDiscount.toFixed(2)}`
//                         : `₹${(totalAmount + taxes).toFixed(2)}`}
//                     </span>
//                   </div>
//                 </div>
//               </div>

//               <button
//                 onClick={handlePlaceOrder}
//                 disabled={
//                   isPlacingOrder || isAddingItems || !!discountError || activeOrderIsCancelled
//                 }
//                 className={`w-full py-3 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-all flex items-center justify-center gap-2 mt-3 ${
//                   isPlacingOrder || isAddingItems || discountError || activeOrderIsCancelled
//                     ? 'opacity-50 cursor-not-allowed'
//                     : ''
//                 }`}
//               >
//                 {isPlacingOrder || isAddingItems ? (
//                   <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
//                 ) : activeOrderIsCancelled ? (
//                   <>
//                     <FaBan size={14} /> Order Cancelled
//                   </>
//                 ) : (
//                   <>
//                     {(orderId && (orderType === 'parcel' ? selectedParcelOrder : orderId)) ||
//                     roomOrderId
//                       ? 'Add Items to Order'
//                       : 'Place Order'}
//                     <FaArrowRight size={14} />
//                   </>
//                 )}
//               </button>
//             </div>
//           )}

//           {/* Payment Options Section */}
//           {((orderType === 'dining' || orderType === 'parcel') &&
//             orderId &&
//             billingInfo &&
//             !billingInfo.is_paid) ||
//           (orderType === 'room' &&
//             roomOrderId &&
//             roomBillingInfo &&
//             !roomBillingInfo.is_paid &&
//             selectedRoomOrder?.booking_id) ? (
//             <div className="border-t border-gray-100 px-6 py-3 bg-gray-50">
//               <h3 className="font-semibold text-sm mb-3 text-gray-700">Complete Payment</h3>
//               <div className="flex gap-2">
//                 <button
//                   onClick={() => handlePaymentOption('cash')}
//                   disabled={isProcessingPayment || activeOrderIsCancelled}
//                   className={`flex-1 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-all flex items-center justify-center gap-2 text-sm ${
//                     isProcessingPayment || activeOrderIsCancelled
//                       ? 'opacity-50 cursor-not-allowed'
//                       : ''
//                   }`}
//                 >
//                   {isProcessingPayment ? (
//                     <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
//                   ) : (
//                     <>
//                       <FaMoneyBill /> Cash
//                     </>
//                   )}
//                 </button>
//                 <button
//                   onClick={() => handlePaymentOption('razorpay')}
//                   disabled={isProcessingPayment || activeOrderIsCancelled}
//                   className={`flex-1 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all flex items-center justify-center gap-2 text-sm ${
//                     isProcessingPayment || activeOrderIsCancelled
//                       ? 'opacity-50 cursor-not-allowed'
//                       : ''
//                   }`}
//                 >
//                   {isProcessingPayment ? (
//                     <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
//                   ) : (
//                     <>
//                       <FaDigitalOcean /> Digital
//                     </>
//                   )}
//                 </button>
//                 <button
//                   onClick={handlePrintInvoice}
//                   disabled={isPrinting}
//                   className={`flex-1 py-2 bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-800 transition-all flex items-center justify-center gap-2 text-sm ${
//                     isPrinting ? 'opacity-50 cursor-not-allowed' : ''
//                   }`}
//                 >
//                   {isPrinting ? (
//                     <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
//                   ) : (
//                     <>
//                       <FaFileInvoice /> Print
//                     </>
//                   )}
//                 </button>
//               </div>
//             </div>
//           ) : null}

//           {/* Payment Confirmation Modal */}
//           {showPaymentModal && (
//             <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//               <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
//                 <h2 className="text-xl font-bold mb-4">Confirm Payment</h2>
//                 <div className="space-y-4">
//                   <div>
//                     <label className="block text-sm font-medium mb-2">Order ID</label>
//                     <input
//                       type="text"
//                       value={(orderId || roomOrderId || '').slice(0, 8)}
//                       disabled
//                       className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
//                     />
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium mb-2">Total Amount</label>
//                     <input
//                       type="text"
//                       value={`₹${billingInfo?.final_amount || roomBillingInfo?.final_amount || 0}`}
//                       disabled
//                       className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
//                     />
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium mb-2">Payment Method</label>
//                     <input
//                       type="text"
//                       value={selectedPaymentMethod === 'cash' ? 'Cash' : 'Razorpay'}
//                       disabled
//                       className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
//                     />
//                   </div>
//                   <div className="flex gap-3 pt-4">
//                     <button
//                       onClick={() => setShowPaymentModal(false)}
//                       className="flex-1 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
//                     >
//                       Cancel
//                     </button>
//                     <button
//                       onClick={confirmPayment}
//                       disabled={isProcessingPayment}
//                       className={`flex-1 py-2 bg-black text-white rounded-lg hover:bg-gray-800 ${
//                         isProcessingPayment ? 'opacity-50 cursor-not-allowed' : ''
//                       }`}
//                     >
//                       {isProcessingPayment ? (
//                         <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
//                       ) : (
//                         'Confirm Payment'
//                       )}
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}
//         </div>
//       </div>

//       <ToastContainer
//         position="bottom-right"
//         autoClose={2000}
//         hideProgressBar={false}
//         newestOnTop
//         closeOnClick
//         rtl={false}
//         pauseOnFocusLoss
//         draggable
//         pauseOnHover
//         theme="light"
//       />
//     </div>
//   );
// }

// export default FoodOrder;

// import React, { useState, useEffect, useRef } from 'react';
// import {
//   FaShoppingCart,
//   FaSearch,
//   FaLeaf,
//   FaDrumstickBite,
//   FaPlus,
//   FaMinus,
//   FaTimes,
//   FaTrash,
//   FaHotel,
//   FaUsers,
//   FaBox,
//   FaArrowRight,
//   FaChevronDown,
//   FaChevronUp,
//   FaUser,
//   FaPhone,
//   FaIdCard,
//   FaFileInvoice,
//   FaCreditCard,
//   FaTable,
//   FaCircle,
//   FaMoneyBill,
//   FaDigitalTiling,
//   FaList,
//   FaPlusCircle,
//   FaDigitalOcean,
//   FaInfoCircle,
//   FaBarcode,
//   FaCheckCircle,
//   FaBuilding,
//   FaSpinner,
//   FaRupeeSign,
//   FaClock,
//   FaUtensils,
//   FaTag,
//   FaImage,
//   FaMicrophone,
// } from 'react-icons/fa';
// import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
// import { useNavigate } from 'react-router-dom';
// import { toast, ToastContainer } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
// const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;
// const REACT_APP_RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

// function FoodOrder() {
//   const [menuItems, setMenuItems] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [search, setSearch] = useState('');
//   const [cart, setCart] = useState([]);
//   const [categories, setCategories] = useState([]);
//   const [subcategories, setSubcategories] = useState([]);
//   const [selectedCategory, setSelectedCategory] = useState(null);
//   const [selectedSubcategory, setSelectedSubcategory] = useState(null);
//   const [justAddedItemId, setJustAddedItemId] = useState(null);
//   const [specialRequest, setSpecialRequest] = useState('');
//   const [discount, setDiscount] = useState(0);
//   const [orderType, setOrderType] = useState('dining');
//   const [tableNumber, setTableNumber] = useState('');
//   const [roomNumber, setRoomNumber] = useState('');
//   const [customerName, setCustomerName] = useState('');
//   const [customerEmail, setCustomerEmail] = useState('');
//   const [phoneNumber, setPhoneNumber] = useState('');
//   const [numberOfGuests, setNumberOfGuests] = useState(1);
//   const [gstNumber, setGstNumber] = useState('');
//   const [deliveryAddress, setDeliveryAddress] = useState('');
//   const [showAdditionalDetails, setShowAdditionalDetails] = useState(false);
//   const [currentUser, setCurrentUser] = useState(null);
//   const [propertyId, setPropertyId] = useState(null);
//   const [currentOrder, setCurrentOrder] = useState(null);
//   const [orderItems, setOrderItems] = useState([]);
//   const [orderId, setOrderId] = useState(null);
//   const [billingInfo, setBillingInfo] = useState(null);
//   const [tables, setTables] = useState([]);
//   const [filteredTables, setFilteredTables] = useState([]);
//   const [tableSearchText, setTableSearchText] = useState('');
//   const [showTableDropdown, setShowTableDropdown] = useState(false);
//   const [showOrderItems, setShowOrderItems] = useState(false);
//   const [razorpayLoaded, setRazorpayLoaded] = useState(false);
//   const [showPaymentModal, setShowPaymentModal] = useState(false);
//   const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
//   const [parcelOrders, setParcelOrders] = useState([]);
//   const [filteredParcelOrders, setFilteredParcelOrders] = useState([]);
//   const [parcelSearchText, setParcelSearchText] = useState('');
//   const [showParcelDropdown, setShowParcelDropdown] = useState(false);
//   const [selectedParcelOrder, setSelectedParcelOrder] = useState(null);
//   const [parcelOrderDetails, setParcelOrderDetails] = useState(null);
//   const [showParcelItems, setShowParcelItems] = useState(false);
//   const [roomOrders, setRoomOrders] = useState([]);
//   const [filteredRoomOrders, setFilteredRoomOrders] = useState([]);
//   const [roomSearchText, setRoomSearchText] = useState('');
//   const [showRoomDropdown, setShowRoomDropdown] = useState(false);
//   const [selectedRoomOrder, setSelectedRoomOrder] = useState(null);
//   const [roomOrderId, setRoomOrderId] = useState(null);
//   const [roomBillingInfo, setRoomBillingInfo] = useState(null);
//   const [roomOrderItems, setRoomOrderItems] = useState([]);
//   const [roomTableSearchText, setRoomTableSearchText] = useState('');
//   const [showRoomTableDropdown, setShowRoomTableDropdown] = useState(false);
//   const [availableTables, setAvailableTables] = useState([]);
//   const [filteredRoomTables, setFilteredRoomTables] = useState([]);
//   const [showTaxBreakdown, setShowTaxBreakdown] = useState(false);
//   const [cgstAmount, setCgstAmount] = useState(0);
//   const [sgstAmount, setSgstAmount] = useState(0);
//   const [isPrinting, setIsPrinting] = useState(false);
//   const [isPlacingOrder, setIsPlacingOrder] = useState(false);
//   const [isAddingItems, setIsAddingItems] = useState(false);
//   const [isProcessingPayment, setIsProcessingPayment] = useState(false);
//   const [companies, setCompanies] = useState([]);
//   const [companySearchText, setCompanySearchText] = useState('');
//   const [filteredCompanies, setFilteredCompanies] = useState([]);
//   const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
//   const [selectedCompany, setSelectedCompany] = useState(null);
//   const companyDropdownRef = useRef(null);
//   const [pendingQuantities, setPendingQuantities] = useState({});
//   const [activeSpeechTarget, setActiveSpeechTarget] = useState(null);

//   // Discount States
//   const [discountPercent, setDiscountPercent] = useState(0);
//   const [discountAmount, setDiscountAmount] = useState(0);
//   const [discountError, setDiscountError] = useState('');

//   const navigate = useNavigate();

//   const {
//     transcript,
//     listening: isListening,
//     resetTranscript,
//     browserSupportsSpeechRecognition: isSupported,
//   } = useSpeechRecognition({
//     clearTranscriptOnListen: true,
//   });

//   // ============== DISCOUNT & TAX CALCULATION LOGIC ==============

//   // 1. Calculate subtotal (food amount only)
//   const totalAmount = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

//   // 2. Calculate discount amount (applied to food only)
//   useEffect(() => {
//     const discountValue = (totalAmount * discountPercent) / 100;
//     setDiscountAmount(discountValue);

//     // Validate discount
//     if (discountPercent > 25) {
//       setDiscountError('Discount cannot exceed 25%');
//     } else {
//       setDiscountError('');
//     }
//   }, [discountPercent, totalAmount]);

//   // 3. Taxable amount = subtotal - discount
//   const taxableAmount = totalAmount - discountAmount;

//   // 4. Calculate taxes on taxable amount (not on subtotal)
//   const calculateTaxes = () => {
//     let totalTax = 0;

//     if (cart.length === 0) return 0;

//     cart.forEach((item) => {
//       const itemSubtotal = item.price * item.quantity;
//       // Calculate what percentage of total this item represents
//       const itemPercentage = totalAmount > 0 ? itemSubtotal / totalAmount : 0;
//       // Apply discount proportionally to each item
//       const discountedItemAmount = itemSubtotal - discountAmount * itemPercentage;

//       if (item.value_type === 'P') {
//         totalTax += discountedItemAmount * (item.value / 100);
//       } else {
//         totalTax += item.value * item.quantity;
//       }
//     });

//     return totalTax;
//   };

//   const taxes = calculateTaxes();

//   // 5. CGST/SGST breakdown
//   useEffect(() => {
//     const halfTax = taxes / 2;
//     setCgstAmount(halfTax);
//     setSgstAmount(halfTax);
//   }, [taxes]);

//   // 6. Final total = taxable amount + taxes
//   const finalTotalWithDiscount = taxableAmount + taxes;

//   // 7. Handle discount input change - FIXED
//   const handleDiscountChange = (e) => {
//     // Remove any leading zeros and get the raw value
//     let rawValue = e.target.value.replace(/^0+/, '');

//     // If empty, set to empty string
//     if (rawValue === '') {
//       setDiscountPercent(0);
//       return;
//     }

//     const value = parseFloat(rawValue);

//     // Check if it's a valid number
//     if (isNaN(value)) {
//       return;
//     }

//     if (value > 25) {
//       toast.error('Discount cannot exceed 25%');
//       setDiscountPercent(25);
//       return;
//     }

//     if (value < 0) {
//       setDiscountPercent(0);
//       return;
//     }

//     setDiscountPercent(value);
//   };
//   // Auto-update based on active target
//   useEffect(() => {
//     if (transcript && activeSpeechTarget === 'search') {
//       setSearch(transcript);
//     }
//   }, [transcript, activeSpeechTarget]);

//   useEffect(() => {
//     if (transcript && activeSpeechTarget === 'special') {
//       setSpecialRequest(transcript);
//     }
//   }, [transcript, activeSpeechTarget]);

//   // Search voice handlers
//   const startSearchListening = () => {
//     setActiveSpeechTarget('search');
//     resetTranscript();
//     if (isListening) {
//       SpeechRecognition.stopListening();
//     }
//     SpeechRecognition.startListening({
//       continuous: false,
//       language: 'en-IN',
//       interimResults: true,
//     });
//     toast.info('🎤 Listening for search... Speak now');
//   };

//   const stopSearchListening = () => {
//     setActiveSpeechTarget(null);
//     SpeechRecognition.stopListening();
//     toast.info('👂 Stopped listening');
//   };

//   // Special instructions voice handlers
//   const startSpecialListening = () => {
//     setActiveSpeechTarget('special');
//     resetTranscript();
//     if (isListening) {
//       SpeechRecognition.stopListening();
//     }
//     SpeechRecognition.startListening({
//       continuous: false,
//       language: 'en-IN',
//       interimResults: true,
//     });
//     toast.info('🎤 Listening for special instructions... Speak now');
//   };

//   const stopSpecialListening = () => {
//     setActiveSpeechTarget(null);
//     SpeechRecognition.stopListening();
//     toast.info('👂 Stopped listening');
//   };

//   // Cleanup on unmount
//   useEffect(() => {
//     return () => {
//       if (isListening) {
//         SpeechRecognition.stopListening();
//       }
//     };
//   }, []);

//   const clearSearch = () => {
//     setSearch('');
//     setSelectedCategory(null);
//     setSelectedSubcategory(null);
//   };

//   const handlePendingQuantityChange = (itemId, newQuantity) => {
//     if (newQuantity < 1) return;
//     setPendingQuantities((prev) => ({
//       ...prev,
//       [itemId]: newQuantity,
//     }));
//   };

//   const handleUpdateQuantityWithButton = async (item) => {
//     const newQuantity = pendingQuantities[item.order_item_id] || item.quantity || 1;

//     if (newQuantity === item.quantity) {
//       toast.info('Quantity is already set to this value');
//       return;
//     }

//     await handleUpdateItemQuantity(item, newQuantity);

//     setPendingQuantities((prev) => ({
//       ...prev,
//       [item.order_item_id]: undefined,
//     }));
//   };

//   const getTodayDate = () => {
//     const today = new Date();
//     const day = String(today.getDate()).padStart(2, '0');
//     const month = String(today.getMonth() + 1).padStart(2, '0');
//     const year = String(today.getFullYear()).slice(-2);
//     return `${day}${month}${year}`;
//   };

//   useEffect(() => {
//     const loadRazorpayScript = () => {
//       const script = document.createElement('script');
//       script.src = 'https://checkout.razorpay.com/v1/checkout.js';
//       script.async = true;
//       script.onload = () => setRazorpayLoaded(true);
//       script.onerror = () => {
//         toast.error('Error loading Razorpay script. Please try again.');
//       };
//       document.body.appendChild(script);
//     };

//     loadRazorpayScript();
//   }, []);

//   useEffect(() => {
//     const fetchCompanies = async () => {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/companies`, {
//           headers: {
//             accept: 'application/json',
//           },
//         });

//         if (response.ok) {
//           const data = await response.json();
//           setCompanies(data.companies || []);
//         } else {
//           console.error('Failed to fetch companies');
//         }
//       } catch (error) {
//         console.error('Error fetching companies:', error);
//       }
//     };

//     fetchCompanies();
//   }, []);

//   useEffect(() => {
//     if (cart.length > 0) {
//       setShowAdditionalDetails(false);
//     }
//   }, [cart]);

//   useEffect(() => {
//     if (companySearchText && companySearchText.length > 1) {
//       const filtered = companies.filter((company) =>
//         company.companyname.toLowerCase().includes(companySearchText.toLowerCase())
//       );
//       setFilteredCompanies(filtered);
//       setShowCompanyDropdown(true);
//     } else {
//       setFilteredCompanies([]);
//       setShowCompanyDropdown(false);
//     }
//   }, [companySearchText, companies]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
//         setShowCompanyDropdown(false);
//       }
//     };

//     document.addEventListener('mousedown', handleClickOutside);
//     return () => {
//       document.removeEventListener('mousedown', handleClickOutside);
//     };
//   }, []);

//   useEffect(() => {
//     const fetchCurrentUser = async () => {
//       try {
//         const response = await fetch(`${AQ_BASE_URL}/aq/api/currentuser`, {
//           headers: {
//             accept: 'application/json',
//           },
//           credentials: 'include',
//         });

//         if (response.ok) {
//           const userData = await response.json();
//           setCurrentUser(userData);
//           setPropertyId(userData.property_id);
//           console.log('Current user loaded:', userData);
//         } else {
//           console.error('Failed to fetch current user');
//           setPropertyId(1);
//         }
//       } catch (error) {
//         console.error('Error fetching current user:', error);
//         setPropertyId(1);
//       }
//     };

//     fetchCurrentUser();
//   }, []);

//   const fetchTables = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/dining-tables/`, {
//         headers: {
//           accept: 'application/json',
//         },
//       });

//       if (response.ok) {
//         const data = await response.json();
//         const activeTables = data.tables.filter((table) => table.status !== 'out_of_service');
//         setTables(activeTables);
//         setFilteredTables(activeTables);
//       } else {
//         console.error('Failed to fetch tables');
//       }
//     } catch (error) {
//       console.error('Error fetching tables:', error);
//       toast.error('Failed to load tables');
//     }
//   };

//   useEffect(() => {
//     fetchTables();
//   }, []);

//   useEffect(() => {
//     if (orderType === 'room') {
//       fetchRoomOrders();
//     }
//   }, [orderType]);

//   const fetchAvailableTablesForRoom = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/dining-tables/`, {
//         headers: {
//           accept: 'application/json',
//         },
//       });

//       if (response.ok) {
//         const data = await response.json();
//         const available = data.tables.filter((table) => table.status === 'available');
//         setAvailableTables(available);
//         setFilteredRoomTables(available);
//       } else {
//         console.error('Failed to fetch available tables');
//       }
//     } catch (error) {
//       console.error('Error fetching available tables:', error);
//     }
//   };

//   useEffect(() => {
//     if (orderType === 'room') {
//       fetchAvailableTablesForRoom();
//     }
//   }, [orderType]);

//   useEffect(() => {
//     if (roomSearchText) {
//       const searchLower = roomSearchText.toLowerCase();
//       const filtered = roomOrders.filter(
//         (order) =>
//           order.room_number?.toLowerCase().includes(searchLower) ||
//           order.customer_name?.toLowerCase().includes(searchLower) ||
//           order.id?.toString().toLowerCase().includes(searchLower) ||
//           order.booking_id?.toString().toLowerCase().includes(searchLower)
//       );
//       setFilteredRoomOrders(filtered);
//     } else {
//       setFilteredRoomOrders(roomOrders);
//     }
//   }, [roomSearchText, roomOrders]);

//   const fetchRoomOrders = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/`, {
//         headers: { accept: 'application/json' },
//       });

//       if (response.ok) {
//         const data = await response.json();
//         console.log('Room orders response:', data);

//         const transformedOrders = data
//           .filter((order) => {
//             if (order.order_type !== 'room') return false;
//             if (order.status?.toLowerCase() === 'delivered') return false;
//             if (order.status === 'Completed' || order.status === 'Cancelled') return false;
//             if (order.billing && order.billing.is_paid === true) return false;
//             return true;
//           })
//           .map((order) => {
//             const hasBilling = order.billing !== null;
//             const isPaid = hasBilling ? order.billing.is_paid : false;
//             const billingId = hasBilling ? order.billing.billing_id : null;

//             let paymentMethod = 'Not Paid';
//             if (order.payments && order.payments.length > 0) {
//               const payment = order.payments[0];
//               paymentMethod = payment.payment_method || 'Not Paid';
//               paymentMethod = paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1);
//             } else if (isPaid) {
//               paymentMethod = 'Paid';
//             }

//             const filteredItems = (order.items || []).filter(
//               (item) => item.status?.toLowerCase() !== 'delivered'
//             );

//             return {
//               id: order.id,
//               order_id: order.id,
//               order_type: order.order_type,
//               order_status: order.status || 'pending',
//               customer_name: order.customer_name || 'Guest',
//               customer_phone: order.phone_number || null,
//               customer_email: null,
//               table_number: order.table_number || null,
//               parcel_number: order.parcel_number || null,
//               room_number: order.room_number || null,
//               kot_number: null,
//               totalprice: order.totalprice || 0,
//               total_amount: order.totalprice || 0,
//               is_paid: isPaid,
//               ordered_at: order.created_at,
//               created_at: order.created_at,
//               items: filteredItems,
//               billing: order.billing || null,
//               billing_id: billingId,
//               booking_id: order.booking_id || null,
//               special_request: order.special_request || null,
//               gst_number: null,
//               has_billing: hasBilling,
//               payment_method: paymentMethod,
//               billings: order.billing ? [order.billing] : [],
//               payments: order.payments || [],
//               status: order.status || 'pending',
//             };
//           });

//         transformedOrders.sort((a, b) => {
//           return new Date(b.created_at) - new Date(a.created_at);
//         });

//         setRoomOrders(transformedOrders);
//         setFilteredRoomOrders(transformedOrders);

//         return transformedOrders;
//       }
//       return [];
//     } catch (error) {
//       console.error('Error fetching room orders:', error);
//       return [];
//     }
//   };

//   const fetchRoomOrderByNumber = async (roomNum) => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/`, {
//         method: 'GET',
//         headers: {
//           accept: 'application/json',
//         },
//       });

//       if (response.ok) {
//         const data = await response.json();
//         const roomOrder = data.find(
//           (order) =>
//             order.order_type === 'room' &&
//             order.room_number === roomNum &&
//             order.status !== 'Completed' &&
//             order.status !== 'Cancelled' &&
//             !(order.billing && order.billing.is_paid === true)
//         );
//         if (roomOrder) {
//           roomOrder.billing = roomOrder.billing || null;
//           roomOrder.booking_id = roomOrder.booking_id;
//         }
//         return roomOrder;
//       }
//       return null;
//     } catch (error) {
//       console.error('Error fetching room order:', error);
//       return null;
//     }
//   };

//   const fetchRoomOrderById = async (orderId) => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/`, {
//         method: 'GET',
//         headers: {
//           accept: 'application/json',
//         },
//       });

//       if (response.ok) {
//         const data = await response.json();
//         const roomOrder = data.find((order) => order.id === orderId);

//         if (roomOrder) {
//           const items = (roomOrder.items || []).map((item) => ({
//             order_item_id: item.id,
//             food_id: item.food_id,
//             food_name: item.food_title || item.food_name || 'Item',
//             quantity: item.quantity,
//             unit_price: item.food_price || item.unit_price || 0,
//             total_amount: item.total_price || item.line_total || item.food_price * item.quantity,
//             special_request: item.special_request,
//             description: `${item.food_title || 'Item'} x ${item.quantity}`,
//           }));

//           setRoomOrderItems(items);
//           setRoomOrderId(roomOrder.id);

//           if (roomOrder.billing) {
//             setRoomBillingInfo({
//               billing_id: roomOrder.billing.billing_id,
//               final_amount: roomOrder.billing.final_amount || roomOrder.totalprice,
//               is_paid: roomOrder.billing.is_paid || false,
//             });
//           }

//           setSelectedRoomOrder((prev) => ({
//             ...prev,
//             ...roomOrder,
//             items: items,
//           }));
//         }
//       } else {
//         console.error('Failed to fetch room orders');
//       }
//     } catch (error) {
//       console.error('Error fetching room order:', error);
//       toast.error('Failed to load room order details');
//     }
//   };

//   const handleAddItemsToRoomOrder = async () => {
//     if (cart.length === 0) {
//       toast.error('Cart is empty');
//       return;
//     }

//     if (!roomNumber) {
//       toast.error('Room number is required');
//       return;
//     }

//     const items = cart.map((item) => ({
//       foodid: item.id,
//       quantity: item.quantity,
//       specialRequest: item.specialRequest || null,
//     }));

//     const addItemsData = {
//       items: items,
//       specialRequest: specialRequest || null,
//     };

//     setIsAddingItems(true);
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/orders/room/${roomNumber}/add-items`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify(addItemsData),
//       });

//       const data = await response.json();

//       if (response.ok) {
//         toast.success('Items added successfully!');
//         const roomOrder = await fetchRoomOrderByNumber(roomNumber);
//         if (roomOrder) {
//           const mappedItems = (roomOrder.items || []).map((item) => ({
//             order_item_id: item.id,
//             food_id: item.food_id,
//             food_name: item.food_title || item.food_name || 'Item',
//             quantity: item.quantity,
//             unit_price: item.food_price || item.unit_price || 0,
//             total_amount: item.total_price || item.food_price * item.quantity,
//           }));

//           setRoomOrderId(roomOrder.id);
//           setRoomOrderItems(mappedItems);
//           setSelectedRoomOrder((prev) => ({
//             ...prev,
//             ...roomOrder,
//             items: mappedItems,
//             booking_id: roomOrder.booking_id || prev?.booking_id,
//           }));

//           if (roomOrder.billing && roomOrder.billing.billing_id) {
//             setRoomBillingInfo({
//               billing_id: roomOrder.billing.billing_id,
//               final_amount: roomOrder.billing.final_amount || roomOrder.totalprice,
//               is_paid: roomOrder.billing.is_paid || false,
//             });
//             console.log('Billing refreshed from room order:', roomOrder.billing);
//           } else if (roomOrder.booking_id) {
//             console.log(
//               'Creating billing for room order:',
//               roomOrder.id,
//               'with booking_id:',
//               roomOrder.booking_id
//             );
//             await createRoomOrderBilling(roomOrder.id, roomOrder.booking_id);
//           } else {
//             console.error('No billing found and no booking_id available');
//           }
//         }
//         setCart([]);
//         setSpecialRequest('');
//         clearSearch();
//       } else {
//         throw new Error(data.detail || 'Failed to add items');
//       }
//     } catch (error) {
//       console.error('Error adding items to room order:', error);
//       toast.error(error.message || 'Failed to add items');
//     } finally {
//       setIsAddingItems(false);
//     }
//   };

//   useEffect(() => {
//     if (roomSearchText) {
//       const filtered = roomOrders.filter(
//         (order) =>
//           order.room_number?.toLowerCase().includes(roomSearchText.toLowerCase()) ||
//           order.customer_name?.toLowerCase().includes(roomSearchText.toLowerCase()) ||
//           order.id?.toLowerCase().includes(roomSearchText.toLowerCase())
//       );
//       setFilteredRoomOrders(filtered);
//     } else {
//       setFilteredRoomOrders(roomOrders);
//     }
//   }, [roomSearchText, roomOrders]);

//   useEffect(() => {
//     if (orderType === 'parcel') {
//       fetchParcelOrders();
//     }
//   }, [orderType]);

//   const fetchParcelOrders = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders?order_type=parcel`, {
//         method: 'GET',
//         headers: {
//           accept: 'application/json',
//         },
//       });

//       if (response.ok) {
//         const data = await response.json();
//         const todayDate = getTodayDate();
//         const todayOrders = data.orders.filter((order) => {
//           const parcelNumber = order.parcel_number;
//           if (parcelNumber && parcelNumber.includes(todayDate)) {
//             return order.order_status === 'pending' || order.order_status === 'ready';
//           }
//           return false;
//         });
//         setParcelOrders(todayOrders);
//         setFilteredParcelOrders(todayOrders);
//       } else {
//         console.error('Failed to fetch parcel orders');
//       }
//     } catch (error) {
//       console.error('Error fetching parcel orders:', error);
//       toast.error('Failed to load parcel orders');
//     }
//   };

//   useEffect(() => {
//     if (parcelSearchText) {
//       const filtered = parcelOrders.filter(
//         (order) =>
//           order.parcel_number?.toLowerCase().includes(parcelSearchText.toLowerCase()) ||
//           order.customer_name?.toLowerCase().includes(parcelSearchText.toLowerCase()) ||
//           order.customer_phone?.includes(parcelSearchText)
//       );
//       setFilteredParcelOrders(filtered);
//     } else {
//       setFilteredParcelOrders(parcelOrders);
//     }
//   }, [parcelSearchText, parcelOrders]);

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const [itemsRes, categoriesRes, subcategoriesRes] = await Promise.all([
//           fetch(`${BQ_BASE_URL}/bq/api/items/`),
//           fetch(`${BQ_BASE_URL}/bq/api/categories/`),
//           fetch(`${BQ_BASE_URL}/bq/api/subcategories/`),
//         ]);

//         const itemsData = await itemsRes.json();
//         const categoriesData = await categoriesRes.json();
//         const subcategoriesData = await subcategoriesRes.json();

//         setMenuItems(itemsData.items || []);
//         setCategories(categoriesData || []);
//         setSubcategories(subcategoriesData || []);
//       } catch (error) {
//         console.error('Error fetching data:', error);
//         toast.error('Failed to load menu');
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, []);

//   useEffect(() => {
//     if (tableSearchText) {
//       const filtered = tables.filter(
//         (table) =>
//           table.table_number.toLowerCase().includes(tableSearchText.toLowerCase()) ||
//           table.table_name?.toLowerCase().includes(tableSearchText.toLowerCase())
//       );
//       setFilteredTables(filtered);
//     } else {
//       setFilteredTables(tables);
//     }
//   }, [tableSearchText, tables]);

//   const getStatusColor = (status) => {
//     switch (status) {
//       case 'available':
//         return 'text-green-500';
//       case 'occupied':
//         return 'text-red-500';
//       case 'reserved':
//         return 'text-yellow-500';
//       default:
//         return 'text-gray-500';
//     }
//   };

//   const getStatusText = (status) => {
//     switch (status) {
//       case 'available':
//         return 'Available';
//       case 'occupied':
//         return 'Occupied';
//       case 'reserved':
//         return 'Reserved';
//       default:
//         return status;
//     }
//   };

//   const handleTableSelect = async (table) => {
//     setTableNumber(table.table_number);
//     setTableSearchText(`${table.table_number} (${getStatusText(table.status)})`);
//     setShowTableDropdown(false);

//     if (table.status === 'occupied') {
//       await fetchOrderByTable(table.table_number);
//     } else if (table.status === 'available') {
//       setCurrentOrder(null);
//       setOrderId(null);
//       setOrderItems([]);
//       setBillingInfo(null);
//       setCart([]);
//       toast.info(`Table ${table.table_number} is available. Create a new order.`);
//     }
//   };

//   const fetchOrderByTable = async (tableNum) => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/tables/${tableNum}/order`, {
//         method: 'GET',
//         headers: {
//           accept: 'application/json',
//         },
//       });

//       if (response.ok) {
//         const data = await response.json();
//         setCurrentOrder(data);
//         setOrderId(data.order_id);

//         const allItems = [];
//         if (data.kitchen_batches) {
//           data.kitchen_batches.forEach((batch) => {
//             if (batch.items) {
//               batch.items.forEach((item) => {
//                 allItems.push({
//                   order_item_id: item.item_id,
//                   food_id: item.food_id,
//                   food_name: item.name,
//                   quantity: item.quantity,
//                   unit_price: item.unit_price,
//                   total_amount: item.line_total,
//                   special_request: item.special_request,
//                   item_status: item.item_status,
//                   is_complimentary: item.is_complimentary,
//                   kot_number: batch.kot_number,
//                   description: `${item.name} x ${item.quantity}`,
//                 });
//               });
//             }
//           });
//         }

//         setOrderItems(allItems);
//         setBillingInfo(data.billing);
//         setCustomerName(data.customer_name || '');
//         setCustomerEmail(data.customer_email || '');
//         setPhoneNumber(data.customer_phone || '');
//         setNumberOfGuests(data.number_of_guests || 1);
//         setSpecialRequest(data.special_request || '');

//         if (data.gst_number) {
//           setGstNumber(data.gst_number);
//         }

//         toast.success(`Order found for table ${tableNum}`);
//         setCart([]);
//       } else if (response.status === 404) {
//         toast.info(`No active order found for table ${tableNum}. You can create a new order.`);
//         setCurrentOrder(null);
//         setOrderId(null);
//         setOrderItems([]);
//         setBillingInfo(null);
//       } else {
//         throw new Error('Failed to fetch order');
//       }
//     } catch (error) {
//       console.error('Error fetching order:', error);
//       toast.error('Failed to fetch order details');
//     }
//   };

//   const fetchParcelOrderDetails = async (orderId) => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders/${orderId}`, {
//         method: 'GET',
//         headers: {
//           accept: 'application/json',
//         },
//       });

//       if (response.ok) {
//         const data = await response.json();
//         setParcelOrderDetails(data);

//         const allItems = [];
//         if (data.kitchen_batches) {
//           data.kitchen_batches.forEach((batch) => {
//             if (batch.items) {
//               batch.items.forEach((item) => {
//                 allItems.push({
//                   order_item_id: item.item_id,
//                   food_id: item.food_id,
//                   food_name: item.name,
//                   quantity: item.quantity,
//                   unit_price: item.unit_price,
//                   total_amount: item.line_total,
//                   special_request: item.special_request,
//                   item_status: item.item_status,
//                   is_complimentary: item.is_complimentary,
//                   kot_number: batch.kot_number,
//                   description: `${item.name} x ${item.quantity}`,
//                 });
//               });
//             }
//           });
//         }

//         setOrderItems(allItems);
//         setBillingInfo(data.billing);
//         setCustomerName(data.customer_name || '');
//         setCustomerEmail(data.customer_email || '');
//         setPhoneNumber(data.customer_phone || '');
//         setSpecialRequest(data.special_request || '');
//         setDeliveryAddress(data.delivery_address || '');

//         if (data.gst_number) {
//           setGstNumber(data.gst_number);
//         }

//         toast.success(`Order details loaded`);
//       } else {
//         throw new Error('Failed to fetch order details');
//       }
//     } catch (error) {
//       console.error('Error fetching order details:', error);
//       toast.error('Failed to fetch order details');
//     }
//   };

//   const fetchRoomOrderDetails = async (order) => {
//     let detail = null;
//     try {
//       const detailRes = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/${order.id}`, {
//         method: 'GET',
//         headers: { accept: 'application/json' },
//       });
//       if (detailRes.ok) {
//         detail = await detailRes.json();
//       } else {
//         console.warn('Room order detail fetch failed, status', detailRes.status);
//       }
//     } catch (detailErr) {
//       console.warn('Room order detail fetch error:', detailErr);
//     }

//     const orderData = {
//       ...order,
//       ...(detail || {}),
//       items: detail && detail.items && detail.items.length ? detail.items : order.items || [],
//       billing: order.billing || (detail && detail.billing) || null,
//       payments: order.payments || (detail && detail.payments) || [],
//       booking_id: (detail && detail.booking_id) || order.booking_id,
//     };

//     setSelectedRoomOrder(orderData);
//     setRoomOrderId(order.id);

//     const mappedItems = (orderData.items || []).map((item) => ({
//       order_item_id: item.id,
//       food_id: item.food_id,
//       food_name: item.food_title || item.food_name || 'Item',
//       quantity: item.quantity,
//       unit_price: item.food_price || item.unit_price || 0,
//       total_amount: item.total_price || item.line_total || item.food_price * item.quantity,
//       special_request: item.special_request,
//       description: `${item.food_title || 'Item'} x ${item.quantity}`,
//     }));

//     setRoomOrderItems(mappedItems);
//     setCustomerName(orderData.customer_name || '');
//     setPhoneNumber(orderData.phone_number || '');
//     setRoomNumber(orderData.room_number || '');
//     setSpecialRequest(orderData.special_request || '');
//     setTableNumber(orderData.table_number || '');
//     setRoomTableSearchText(orderData.table_number ? `${orderData.table_number}` : '');
//     setGstNumber(orderData.gst_number || '');

//     setShowOrderItems(true);

//     const orderStatus = (orderData.status || orderData.order_status || '').toLowerCase();
//     const isClosedOrder =
//       orderStatus === 'delivered' || orderStatus === 'completed' || orderStatus === 'cancelled';

//     if (orderData.billing && orderData.billing.billing_id) {
//       setRoomBillingInfo({
//         billing_id: orderData.billing.billing_id,
//         final_amount: orderData.billing.final_amount || orderData.totalprice,
//         is_paid: orderData.billing.is_paid || false,
//       });
//       toast.success(`Room order loaded for room ${orderData.room_number}`);
//     } else if (isClosedOrder) {
//       setRoomBillingInfo(null);
//       toast.info('Opened a past order for viewing. No new bill was created.');
//     } else if (orderData.booking_id) {
//       await createRoomOrderBilling(orderData.id, orderData.booking_id);
//     } else {
//       console.error('Booking ID not found in order object');
//       toast.error('Cannot create billing: Booking ID missing');
//     }

//     setCart([]);
//   };

//   const handleParcelSelect = async (order) => {
//     setSelectedParcelOrder(order);
//     setParcelSearchText(`${order.parcel_number} - ${order.customer_name || 'Guest'}`);
//     setShowParcelDropdown(false);
//     setOrderId(order.order_id);
//     setParcelOrderDetails(null);
//     await fetchParcelOrderDetails(order.order_id);
//   };

//   const handleRoomSelect = async (order) => {
//     setRoomSearchText(`${order.room_number} - ${order.customer_name || 'Guest'}`);
//     setShowRoomDropdown(false);
//     await fetchRoomOrderDetails(order);
//   };

//   const handleNewRoomOrder = () => {
//     setSelectedRoomOrder(null);
//     setRoomSearchText('');
//     setRoomOrderId(null);
//     setRoomOrderItems([]);
//     setRoomBillingInfo(null);
//     setRoomNumber('');
//     setTableNumber('');
//     setRoomTableSearchText('');
//     setCustomerName('');
//     setCustomerEmail('');
//     setPhoneNumber('');
//     setGstNumber('');
//     setSpecialRequest('');
//     setCart([]);
//     setShowRoomDropdown(false);
//     toast.info('Creating new room order. Add items to cart and place order.');
//   };

//   const handleNewParcelOrder = () => {
//     setSelectedParcelOrder(null);
//     setParcelSearchText('');
//     setOrderId(null);
//     setOrderItems([]);
//     setBillingInfo(null);
//     setCustomerName('');
//     setCustomerEmail('');
//     setPhoneNumber('');
//     setDeliveryAddress('');
//     setGstNumber('');
//     setSpecialRequest('');
//     setCart([]);
//     setShowParcelDropdown(false);
//     toast.info('Creating new parcel order. Add items to cart and place order.');
//   };

//   const getCategoryIdFromSubCategoryId = (subCategoryId) => {
//     const subcategory = subcategories.find((sub) => sub.id === subCategoryId);
//     return subcategory ? subcategory.categoryid : null;
//   };

//   const handleAddToCart = (item) => {
//     setCart((prevCart) => {
//       const existingItem = prevCart.find((cartItem) => cartItem.id === item.id);
//       if (existingItem) {
//         return prevCart.map((cartItem) =>
//           cartItem.id === item.id ? { ...cartItem, quantity: cartItem.quantity + 1 } : cartItem
//         );
//       }
//       return [...prevCart, { ...item, quantity: 1, specialRequest: '' }];
//     });

//     setJustAddedItemId(item.id);
//     setTimeout(() => setJustAddedItemId(null), 1000);
//     toast.success(`${item.title} added to cart`);
//     clearSearch();
//   };

//   const handleRemoveFromCart = (id) => {
//     setCart(cart.filter((item) => item.id !== id));
//     toast.info('Item removed from cart');
//   };

//   const handleIncreaseQuantity = (id) => {
//     setCart((prevCart) =>
//       prevCart.map((item) => (item.id === id ? { ...item, quantity: item.quantity + 1 } : item))
//     );
//   };

//   const handleDecreaseQuantity = (id) => {
//     setCart((prevCart) =>
//       prevCart.map((item) =>
//         item.id === id && item.quantity > 1 ? { ...item, quantity: item.quantity - 1 } : item
//       )
//     );
//   };

//   const handleIncreaseGuests = () => {
//     setNumberOfGuests((prev) => prev + 1);
//   };

//   const handleDecreaseGuests = () => {
//     setNumberOfGuests((prev) => (prev > 1 ? prev - 1 : 1));
//   };

//   const clearCart = () => {
//     setCart([]);
//     setSpecialRequest('');
//     setSearch('');
//     setSelectedCategory(null);
//     setSelectedSubcategory(null);
//     setShowAdditionalDetails(false);
//     toast.info('Cart cleared');
//   };

//   const clearAllFields = () => {
//     // Clear cart
//     setCart([]);
//     setSpecialRequest('');
//     setSearch('');
//     setSelectedCategory(null);
//     setSelectedSubcategory(null);
//     setShowAdditionalDetails(false);
//     setDiscountPercent(0);
//     setDiscountAmount(0);
//     setDiscountError('');
//     setDiscount(0);

//     // Clear customer details
//     setCustomerName('');
//     setCustomerEmail('');
//     setPhoneNumber('');
//     setGstNumber('');
//     setDeliveryAddress('');
//     setSelectedCompany(null);
//     setCompanySearchText('');

//     // Clear order-specific fields based on order type
//     if (orderType === 'dining') {
//       setTableNumber('');
//       setTableSearchText('');
//       setNumberOfGuests(1);
//       setOrderId(null);
//       setCurrentOrder(null);
//       setOrderItems([]);
//       setBillingInfo(null);
//       setShowTableDropdown(false);
//     } else if (orderType === 'parcel') {
//       setOrderId(null);
//       setOrderItems([]);
//       setBillingInfo(null);
//       setSelectedParcelOrder(null);
//       setParcelSearchText('');
//       setParcelOrderDetails(null);
//       setShowParcelDropdown(false);
//     } else if (orderType === 'room') {
//       setRoomNumber('');
//       setRoomSearchText('');
//       setRoomOrderId(null);
//       setRoomOrderItems([]);
//       setRoomBillingInfo(null);
//       setSelectedRoomOrder(null);
//       setTableNumber('');
//       setRoomTableSearchText('');
//       setShowRoomDropdown(false);
//       setShowRoomTableDropdown(false);
//     }

//     toast.info('All fields cleared');
//   };

//   const handleCompanySelect = (company) => {
//     setSelectedCompany(company);
//     setCompanySearchText(company.companyname);
//     setGstNumber(company.gstnumber || '');
//     setCustomerName(company.companyname);
//     setShowCompanyDropdown(false);
//   };

//   const handleUpdateItemQuantity = async (item, newQuantity) => {
//     if (!item.order_item_id) {
//       toast.error('Item ID not found');
//       return;
//     }

//     const itemId = item.order_item_id;
//     const orderIdToUse = orderType === 'room' ? roomOrderId : orderId;

//     if (!orderIdToUse) {
//       toast.error('No order found');
//       return;
//     }

//     const toastId = toast.loading('Updating quantity...');

//     try {
//       let response;
//       let url;

//       if (orderType === 'room') {
//         url = `${BQ_BASE_URL}/bq/api/orders/room/${roomNumber}/items/${itemId}`;
//         response = await fetch(url, {
//           method: 'PATCH',
//           headers: {
//             'Content-Type': 'application/json',
//             accept: 'application/json',
//           },
//           body: JSON.stringify({
//             quantity: newQuantity,
//             specialRequest: item.special_request || '',
//           }),
//         });
//       } else if (orderType === 'dining' || orderType === 'parcel') {
//         url = `${BQ_BASE_URL}/bq/api/restaurant/orders/${orderIdToUse}/items/${itemId}`;
//         response = await fetch(url, {
//           method: 'PATCH',
//           headers: {
//             accept: 'application/json',
//             'Content-Type': 'application/json',
//           },
//           body: JSON.stringify({
//             quantity: newQuantity,
//             special_request: item.special_request || '',
//           }),
//         });
//       }

//       const data = await response.json();

//       toast.dismiss(toastId);

//       if (response.ok) {
//         toast.success(`${item.food_name || 'Item'} quantity updated to ${newQuantity}`);

//         if (orderType === 'dining' && tableNumber) {
//           await fetchOrderByTable(tableNumber);
//           await fetchTables();
//         } else if (orderType === 'parcel' && orderId) {
//           await fetchParcelOrderDetails(orderId);
//           await fetchParcelOrders();
//         } else if (orderType === 'room' && roomNumber) {
//           const roomOrder = await fetchRoomOrderByNumber(roomNumber);
//           if (roomOrder) {
//             const mappedItems = (roomOrder.items || []).map((item) => ({
//               order_item_id: item.id,
//               food_id: item.food_id,
//               food_name: item.food_title || item.food_name || 'Item',
//               quantity: item.quantity,
//               unit_price: item.food_price || item.unit_price || 0,
//               total_amount: item.total_price || item.food_price * item.quantity,
//               special_request: item.special_request,
//             }));

//             setRoomOrderId(roomOrder.id);
//             setRoomOrderItems(mappedItems);
//             setSelectedRoomOrder((prev) => ({
//               ...prev,
//               ...roomOrder,
//               items: mappedItems,
//             }));

//             if (roomOrder.billing && roomOrder.billing.billing_id) {
//               setRoomBillingInfo({
//                 billing_id: roomOrder.billing.billing_id,
//                 final_amount: roomOrder.billing.final_amount || roomOrder.totalprice,
//                 is_paid: roomOrder.billing.is_paid || false,
//               });
//             }
//           }
//           await fetchRoomOrders();
//         }
//       } else {
//         throw new Error(data.detail || 'Failed to update quantity');
//       }
//     } catch (error) {
//       console.error('Error updating item quantity:', error);
//       toast.dismiss(toastId);
//       toast.error(error.message || 'Failed to update quantity');
//     }
//   };

//   const handleRemoveItemFromOrder = async (itemId, orderIdToUse, itemName) => {
//     const toastId = toast.loading('Removing item...');

//     try {
//       let response;
//       let url;

//       if (orderType === 'room') {
//         url = `${BQ_BASE_URL}/bq/api/orders/${orderIdToUse}/items/${itemId}`;
//         response = await fetch(url, {
//           method: 'DELETE',
//           headers: {
//             accept: 'application/json',
//           },
//         });
//       } else {
//         url = `${BQ_BASE_URL}/bq/api/restaurant/orders/${orderIdToUse}/items/${itemId}`;
//         response = await fetch(url, {
//           method: 'DELETE',
//           headers: {
//             accept: 'application/json',
//           },
//         });
//       }

//       const data = await response.json();

//       toast.dismiss(toastId);

//       if (response.ok) {
//         toast.success(`${itemName || 'Item'} removed from order`);

//         if (orderType === 'dining' && tableNumber) {
//           await fetchOrderByTable(tableNumber);
//           await fetchTables();
//         } else if (orderType === 'parcel' && orderId) {
//           await fetchParcelOrderDetails(orderId);
//           await fetchParcelOrders();
//         } else if (orderType === 'room' && roomNumber) {
//           const roomOrder = await fetchRoomOrderByNumber(roomNumber);
//           if (roomOrder) {
//             const mappedItems = (roomOrder.items || []).map((item) => ({
//               order_item_id: item.id,
//               food_id: item.food_id,
//               food_name: item.food_title || item.food_name || 'Item',
//               quantity: item.quantity,
//               unit_price: item.food_price || item.unit_price || 0,
//               total_amount: item.total_price || item.food_price * item.quantity,
//               special_request: item.special_request,
//             }));

//             setRoomOrderId(roomOrder.id);
//             setRoomOrderItems(mappedItems);
//             setSelectedRoomOrder((prev) => ({
//               ...prev,
//               ...roomOrder,
//               items: mappedItems,
//             }));

//             if (roomOrder.billing && roomOrder.billing.billing_id) {
//               setRoomBillingInfo({
//                 billing_id: roomOrder.billing.billing_id,
//                 final_amount: roomOrder.billing.final_amount || roomOrder.totalprice,
//                 is_paid: roomOrder.billing.is_paid || false,
//               });
//             }
//           }
//           await fetchRoomOrders();
//         }
//       } else {
//         throw new Error(data.detail || 'Failed to remove item');
//       }
//     } catch (error) {
//       console.error('Error removing item:', error);
//       toast.dismiss(toastId);
//       toast.error(error.message || 'Failed to remove item');
//     }
//   };

//   const handleClearAllItems = async () => {
//     if (!window.confirm('Are you sure you want to remove all items from this order?')) {
//       return;
//     }

//     const itemsToRemove = orderType === 'room' ? roomOrderItems : orderItems;

//     if (itemsToRemove.length === 0) {
//       toast.info('No items to remove');
//       return;
//     }

//     const toastId = toast.loading(`Removing all items from order...`);

//     try {
//       if (orderType === 'room') {
//         const orderIdToDelete = roomOrderId;

//         if (!orderIdToDelete) {
//           throw new Error('Order ID not found');
//         }

//         const response = await fetch(`${BQ_BASE_URL}/bq/api/orders/${orderIdToDelete}`, {
//           method: 'DELETE',
//           headers: {
//             accept: 'application/json',
//           },
//         });

//         const data = await response.json();

//         if (response.ok) {
//           toast.dismiss(toastId);
//           toast.success(`Order deleted successfully`);

//           setRoomOrderId(null);
//           setRoomOrderItems([]);
//           setRoomBillingInfo(null);
//           setSelectedRoomOrder(null);
//           setRoomSearchText('');
//           setRoomNumber('');
//           setRoomTableSearchText('');
//           setTableNumber('');
//           setCustomerName('');
//           setPhoneNumber('');
//           setGstNumber('');
//           setSpecialRequest('');
//           setCart([]);

//           await fetchRoomOrders();
//           await fetchAvailableTablesForRoom();
//           await fetchTables();
//         } else {
//           throw new Error(data.detail || 'Failed to delete order');
//         }
//       } else {
//         const orderIdToDelete = orderId;

//         if (!orderIdToDelete) {
//           throw new Error('Order ID not found');
//         }

//         const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders/${orderIdToDelete}`, {
//           method: 'DELETE',
//           headers: {
//             accept: 'application/json',
//           },
//         });

//         const data = await response.json();

//         if (response.ok) {
//           toast.dismiss(toastId);
//           toast.success(`Order deleted successfully`);

//           setOrderId(null);
//           setOrderItems([]);
//           setBillingInfo(null);
//           setCurrentOrder(null);
//           setCart([]);
//           setSpecialRequest('');
//           setCustomerName('');
//           setCustomerEmail('');
//           setPhoneNumber('');
//           setGstNumber('');
//           setNumberOfGuests(1);

//           if (orderType === 'dining') {
//             setTableNumber('');
//             setTableSearchText('');
//             await fetchTables();
//           } else if (orderType === 'parcel') {
//             setSelectedParcelOrder(null);
//             setParcelSearchText('');
//             setDeliveryAddress('');
//             await fetchParcelOrders();
//           }
//         } else {
//           throw new Error(data.detail || 'Failed to delete order');
//         }
//       }
//     } catch (error) {
//       console.error('Error deleting order:', error);
//       toast.dismiss(toastId);
//       toast.error(error.message || 'Failed to delete order');
//     }
//   };

//   const handleRemoveItem = (item) => {
//     const itemId = item.order_item_id;
//     const orderIdToUse = orderType === 'room' ? roomOrderId : orderId;
//     const itemName = item.food_name || 'this item';

//     if (!itemId) {
//       toast.error('Item ID not found');
//       return;
//     }

//     if (!orderIdToUse) {
//       toast.error('Order ID not found');
//       return;
//     }

//     handleRemoveItemFromOrder(itemId, orderIdToUse, itemName);
//   };

//   const handleAddItemsToOrder = async () => {
//     if (cart.length === 0) {
//       toast.error('Cart is empty');
//       return;
//     }

//     const items = cart.map((item) => ({
//       food_id: item.id,
//       quantity: item.quantity,
//       special_request: item.specialRequest || '',
//     }));

//     const addItemsData = {
//       items: items,
//       kitchen_notes: specialRequest || null,
//     };

//     try {
//       let response;
//       let data;

//       if (orderType === 'dining') {
//         if (!tableNumber) {
//           toast.error('Table number is required');
//           return;
//         }

//         response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/tables/${tableNumber}/add-items`, {
//           method: 'POST',
//           headers: {
//             'Content-Type': 'application/json',
//           },
//           body: JSON.stringify(addItemsData),
//         });
//       } else {
//         if (!orderId) {
//           toast.error('No order found. Please select a parcel order.');
//           return;
//         }

//         response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders/${orderId}/add-items`, {
//           method: 'POST',
//           headers: {
//             'Content-Type': 'application/json',
//           },
//           body: JSON.stringify(addItemsData),
//         });
//       }

//       data = await response.json();

//       if (response.ok) {
//         toast.success('Items added successfully!');

//         if (orderType === 'dining') {
//           await fetchOrderByTable(tableNumber);
//           await fetchTables();
//           if (typeof fetchAvailableTablesForRoom === 'function') {
//             await fetchAvailableTablesForRoom();
//           }
//         } else {
//           await fetchParcelOrderDetails(orderId);
//           await fetchParcelOrders();
//         }

//         setCart([]);
//         setSpecialRequest('');
//         setCustomerName('');
//         setCustomerEmail('');
//         setPhoneNumber('');
//         setGstNumber('');
//         setDeliveryAddress('');
//         setSelectedCompany(null);
//         setCompanySearchText('');
//         setShowAdditionalDetails(false);
//         clearSearch();
//       } else {
//         throw new Error(data.detail || 'Failed to add items');
//       }
//     } catch (error) {
//       console.error('Error adding items:', error);
//       toast.error(error.message || 'Failed to add items');
//     }
//   };

//   const placeNewOrder = async () => {
//     if (!propertyId) {
//       toast.error('Property information not loaded. Please try again.');
//       return;
//     }

//     if (cart.length === 0) {
//       toast.error('Cart is empty');
//       return;
//     }

//     const items = cart.map((item) => ({
//       food_id: item.id,
//       quantity: item.quantity,
//       special_request: item.specialRequest,
//     }));

//     const orderData = {
//       order_type: 'dining',
//       customer_name: customerName || null,
//       customer_phone: phoneNumber || null,
//       customer_email: customerEmail || null,
//       table_number: tableNumber,
//       number_of_guests: numberOfGuests,
//       gst_number: gstNumber || null,
//       delivery_address: null,
//       items: items,
//       special_request: specialRequest || null,
//       kitchen_notes: null,
//       discount_percent: discountPercent,
//       service_charge_percent: 0,
//       property_id: propertyId,
//     };

//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify(orderData),
//       });

//       const data = await response.json();

//       if (response.ok) {
//         toast.success(`Order placed successfully!`);
//         await fetchOrderByTable(tableNumber);
//         await fetchTables();
//         setCart([]);
//         setSpecialRequest('');
//         setDiscountPercent(0);
//         setSelectedCompany(null);
//         setCustomerName('');
//         setCustomerEmail('');
//         setPhoneNumber('');
//         setGstNumber('');
//         setSelectedCompany(null);
//         setCompanySearchText('');
//         setNumberOfGuests(1);
//         clearSearch();
//       } else {
//         throw new Error(data.detail || 'Failed to place order');
//       }
//     } catch (error) {
//       console.error('Error:', error);
//       toast.error(error.message || 'Failed to place order');
//     }
//   };

//   const placeParcelOrder = async () => {
//     if (!propertyId) {
//       toast.error('Property information not loaded. Please try again.');
//       return;
//     }

//     if (cart.length === 0) {
//       toast.error('Cart is empty');
//       return;
//     }

//     const items = cart.map((item) => ({
//       food_id: item.id,
//       quantity: item.quantity,
//       special_request: item.specialRequest,
//     }));

//     const orderData = {
//       order_type: 'parcel',
//       customer_name: customerName || null,
//       customer_phone: phoneNumber || null,
//       customer_email: customerEmail || null,
//       table_number: null,
//       number_of_guests: 1,
//       gst_number: gstNumber || null,
//       delivery_address: deliveryAddress || null,
//       items: items,
//       special_request: specialRequest || null,
//       kitchen_notes: null,
//       discount_percent: discountPercent,
//       service_charge_percent: 0,
//       property_id: propertyId,
//     };

//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify(orderData),
//       });

//       const data = await response.json();

//       if (response.ok) {
//         toast.success(`Parcel order placed successfully!`);
//         setOrderId(data.order_id);
//         await fetchParcelOrderDetails(data.order_id);
//         setCart([]);
//         setSpecialRequest('');
//         setDiscountPercent(0);
//         setShowParcelDropdown(false);
//         await fetchParcelOrders();
//         setCustomerName('');
//         setCustomerEmail('');
//         setPhoneNumber('');
//         setGstNumber('');
//         setDeliveryAddress('');
//         setSelectedCompany(null);
//         setCompanySearchText('');
//         setShowAdditionalDetails(false);
//         clearSearch();
//       } else {
//         throw new Error(data.detail || 'Failed to place parcel order');
//       }
//     } catch (error) {
//       console.error('Error:', error);
//       toast.error(error.message || 'Failed to place parcel order');
//     }
//   };

//   const placeRoomOrder = async () => {
//     if (!propertyId) {
//       toast.error('Property information not loaded. Please try again.');
//       return;
//     }

//     if (cart.length === 0) {
//       toast.error('Cart is empty');
//       return;
//     }

//     if (!roomNumber) {
//       toast.error('Please enter room number');
//       return;
//     }

//     const items = cart.map((item) => ({
//       foodid: item.id,
//       quantity: item.quantity,
//       specialRequest: item.specialRequest || null,
//     }));

//     const orderData = {
//       items: items,
//       orderType: 'room',
//       customerName: customerName || 'Guest',
//       phoneNumber: phoneNumber || null,
//       tableNumber: tableNumber || null,
//       roomNumber: roomNumber,
//       parcelNumber: null,
//       specialRequest: specialRequest || null,
//       gstNumber: gstNumber || null,
//       discount: discountPercent,
//     };

//     setIsPlacingOrder(true);
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify(orderData),
//       });

//       const data = await response.json();

//       if (response.ok) {
//         toast.success(`Room order placed successfully!`);

//         setRoomOrderId(null);
//         setRoomOrderItems([]);
//         setRoomBillingInfo(null);
//         setSelectedRoomOrder(null);
//         setRoomSearchText('');
//         setRoomNumber('');
//         setTableNumber('');
//         setRoomTableSearchText('');
//         setCustomerName('');
//         setCustomerEmail('');
//         setPhoneNumber('');
//         setGstNumber('');
//         setSpecialRequest('');
//         setDiscountPercent(0);
//         setCart([]);
//         setShowRoomDropdown(false);

//         toast.success(`Order placed! Order ID: ${data.order_id || 'N/A'}`);

//         await fetchRoomOrders();
//         await fetchAvailableTablesForRoom();
//         await fetchTables();
//         clearSearch();
//       } else {
//         throw new Error(data.detail || 'Failed to place room order');
//       }
//     } catch (error) {
//       console.error('Error:', error);
//       toast.error(error.message || 'Failed to place room order');
//     } finally {
//       setIsPlacingOrder(false);
//     }
//   };

//   const createRoomOrderBilling = async (foodOrderId, bookingId) => {
//     try {
//       if (!bookingId) {
//         console.error('Booking ID is required for billing');
//         toast.error('Cannot create billing: Booking ID missing');
//         return;
//       }

//       const url = `${BQ_BASE_URL}/bq/api/create_food_order_billing?bookingid=${bookingId}&food_order_id=${foodOrderId}`;
//       const response = await fetch(url, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//         },
//       });

//       if (response.ok) {
//         const data = await response.json();
//         setRoomBillingInfo({
//           billing_id: data.billingid,
//           final_amount: parseFloat(data.finalamount),
//           is_paid: false,
//         });
//         console.log('Room billing info set:', data);
//         toast.success('Billing created successfully!');
//       } else {
//         const error = await response.json();
//         console.error('Failed to create billing for room order:', error);
//         toast.error(error.detail || 'Failed to create billing');
//       }
//     } catch (error) {
//       console.error('Error creating room order billing:', error);
//       toast.error('Failed to create billing');
//     }
//   };

//   const handlePlaceOrder = async () => {
//     if (cart.length === 0) {
//       toast.error('Cart is empty');
//       return;
//     }

//     if (!propertyId) {
//       toast.error('Loading property information. Please wait...');
//       return;
//     }

//     if (discountPercent > 25) {
//       toast.error('Discount cannot exceed 25%');
//       return;
//     }

//     if (orderType === 'dining') {
//       if (!tableNumber) {
//         toast.error('Please select a table');
//         return;
//       }

//       if (orderId) {
//         setIsAddingItems(true);
//         try {
//           await handleAddItemsToOrder();
//         } finally {
//           setIsAddingItems(false);
//         }
//       } else {
//         setIsPlacingOrder(true);
//         try {
//           await placeNewOrder();
//         } finally {
//           setIsPlacingOrder(false);
//         }
//       }
//       return;
//     }

//     if (orderType === 'parcel') {
//       if (orderId && selectedParcelOrder) {
//         setIsAddingItems(true);
//         try {
//           await handleAddItemsToOrder();
//         } finally {
//           setIsAddingItems(false);
//         }
//       } else {
//         setIsPlacingOrder(true);
//         try {
//           await placeParcelOrder();
//         } finally {
//           setIsPlacingOrder(false);
//         }
//       }
//       return;
//     }

//     if (orderType === 'room') {
//       if (roomOrderId && selectedRoomOrder) {
//         await handleAddItemsToRoomOrder();
//       } else {
//         setIsPlacingOrder(true);
//         try {
//           await placeRoomOrder();
//         } finally {
//           setIsPlacingOrder(false);
//         }
//       }
//       return;
//     }
//   };

//   const handleCashPayment = async () => {
//     if (!orderId || !billingInfo) {
//       toast.error('No order or billing information found');
//       return;
//     }

//     try {
//       const paymentData = {
//         order_id: orderId,
//         billing_id: billingInfo.billing_id,
//         amount: billingInfo.final_amount,
//         notes:
//           orderType === 'dining'
//             ? `Cash payment for table ${tableNumber}`
//             : 'Cash payment for parcel order',
//       };

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/payments/cash`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify(paymentData),
//       });

//       const data = await response.json();

//       if (response.ok) {
//         toast.success(`Payment successful! Change due: ₹${data.change_due}`);
//         setShowPaymentModal(false);
//         if (orderType === 'dining') {
//           setCurrentOrder(null);
//           setOrderId(null);
//           setOrderItems([]);
//           setBillingInfo(null);
//           setTableNumber('');
//           setTableSearchText('');
//           setCart([]);
//           setSpecialRequest('');
//           setCustomerName('');
//           setCustomerEmail('');
//           setPhoneNumber('');
//           setGstNumber('');
//           setNumberOfGuests(1);
//           await fetchTables();
//         } else {
//           await fetchParcelOrderDetails(orderId);
//           await fetchParcelOrders();
//         }
//       } else {
//         throw new Error(data.detail || 'Payment failed');
//       }
//     } catch (error) {
//       console.error('Cash payment error:', error);
//       toast.error(error.message || 'Failed to process cash payment');
//     }
//   };

//   const handleRazorpayPayment = async () => {
//     if (!razorpayLoaded) {
//       toast.error('Razorpay script is not loaded yet.');
//       return;
//     }

//     if (!orderId || !billingInfo) {
//       toast.error('No order or billing information found');
//       return;
//     }

//     try {
//       const apiUrl = `${BQ_BASE_URL}/bq/api/restaurant/payments/razorpay/create-order`;

//       const response = await fetch(apiUrl, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Accept: 'application/json',
//         },
//         body: JSON.stringify({
//           order_id: orderId,
//           billing_id: billingInfo.billing_id,
//         }),
//       });

//       if (!response.ok) {
//         const errorData = await response.json();
//         throw new Error(errorData.detail || 'Failed to create payment order');
//       }

//       const orderData = await response.json();

//       if (orderData && orderData.razorpay_order_id) {
//         const options = {
//           key: REACT_APP_RAZORPAY_KEY,
//           amount: orderData.amount,
//           currency: 'INR',
//           name: 'Pagoda Hotel',
//           description:
//             orderType === 'dining' ? `Restaurant Order - Table ${tableNumber}` : 'Parcel Order',
//           order_id: orderData.razorpay_order_id,
//           handler: async function (response) {
//             const paymentDetails = {
//               razorpay_order_id: response.razorpay_order_id,
//               razorpay_payment_id: response.razorpay_payment_id,
//               razorpay_signature: response.razorpay_signature,
//             };

//             try {
//               const verificationResponse = await fetch(
//                 `${BQ_BASE_URL}/bq/api/restaurant/payments/razorpay/verify`,
//                 {
//                   method: 'POST',
//                   headers: {
//                     'Content-Type': 'application/json',
//                     Accept: 'application/json',
//                   },
//                   body: JSON.stringify(paymentDetails),
//                 }
//               );

//               if (verificationResponse.ok) {
//                 toast.success('Payment successful and verified!');
//                 setShowPaymentModal(false);
//                 if (orderType === 'dining') {
//                   setCurrentOrder(null);
//                   setOrderId(null);
//                   setOrderItems([]);
//                   setBillingInfo(null);
//                   setTableNumber('');
//                   setTableSearchText('');
//                   setCart([]);
//                   setSpecialRequest('');
//                   await fetchTables();
//                 } else {
//                   await fetchParcelOrderDetails(orderId);
//                   await fetchParcelOrders();
//                 }
//               } else {
//                 toast.error('Payment verification failed.');
//               }
//             } catch (error) {
//               console.error('Verification error:', error);
//               toast.error('Error verifying payment. Please try again.');
//             }
//           },
//           prefill: {
//             name: customerName || 'Guest',
//             email: customerEmail || 'guest@example.com',
//             contact: phoneNumber || '9999999999',
//           },
//           theme: {
//             color: '#3399cc',
//           },
//         };

//         const rzp = new window.Razorpay(options);
//         rzp.open();
//       }
//     } catch (error) {
//       console.error('Payment error:', error);
//       toast.error(error.message || 'Error processing payment. Please try again.');
//     }
//   };

//   const handleRoomCashPayment = async () => {
//     if (!roomOrderId || !roomBillingInfo) {
//       toast.error('No room order or billing information found');
//       return;
//     }

//     if (!selectedRoomOrder?.booking_id) {
//       toast.error('Booking ID not found for this room order');
//       return;
//     }

//     setIsProcessingPayment(true);
//     try {
//       const paymentData = {
//         booking_id: selectedRoomOrder.booking_id,
//         billing_id: roomBillingInfo.billing_id,
//         amount: roomBillingInfo.final_amount,
//       };

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/payments/cash`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify(paymentData),
//       });

//       const data = await response.json();

//       if (response.ok) {
//         toast.success(`Payment successful!`);
//         setShowPaymentModal(false);
//         setRoomOrderId(null);
//         setRoomBillingInfo(null);
//         setRoomOrderItems([]);
//         setSelectedRoomOrder(null);
//         setRoomSearchText('');
//         setRoomTableSearchText('');
//         await fetchRoomOrders();
//         await fetchAvailableTablesForRoom();
//         await fetchTables();
//         setCart([]);
//         setSpecialRequest('');
//       } else {
//         throw new Error(data.detail || 'Payment failed');
//       }
//     } catch (error) {
//       console.error('Cash payment error:', error);
//       toast.error(error.message || 'Failed to process cash payment');
//     } finally {
//       setIsProcessingPayment(false);
//     }
//   };

//   const handleRoomRazorpayPayment = async () => {
//     if (!razorpayLoaded) {
//       toast.error('Razorpay script is not loaded yet.');
//       return;
//     }

//     if (!roomOrderId || !roomBillingInfo) {
//       toast.error('No room order or billing information found');
//       return;
//     }

//     if (!selectedRoomOrder?.booking_id) {
//       toast.error('Booking ID not found for this room order');
//       return;
//     }

//     setIsProcessingPayment(true);

//     try {
//       const apiUrl = `${BQ_BASE_URL}/bq/api/razorpay/create_payment_order?billingid=${roomBillingInfo.billing_id}&bookingid=${selectedRoomOrder.booking_id}&strict_target=true`;

//       const response = await fetch(apiUrl, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Accept: 'application/json',
//         },
//         body: '',
//       });

//       if (!response.ok) {
//         const errorData = await response.json();
//         throw new Error(errorData.detail || 'Failed to create payment order');
//       }

//       const orderData = await response.json();

//       if (orderData && orderData.razorpay_order_id) {
//         const options = {
//           key: REACT_APP_RAZORPAY_KEY,
//           amount: orderData.amount,
//           currency: 'INR',
//           name: 'Pagoda Hotel',
//           description: `Room Order - Room ${roomNumber}`,
//           order_id: orderData.razorpay_order_id,
//           handler: async function (response) {
//             const paymentDetails = {
//               razorpay_order_id: response.razorpay_order_id,
//               razorpay_payment_id: response.razorpay_payment_id,
//               razorpay_signature: response.razorpay_signature,
//             };

//             try {
//               const verificationResponse = await fetch(
//                 `${BQ_BASE_URL}/bq/api/razorpay/verify_payment`,
//                 {
//                   method: 'POST',
//                   headers: {
//                     'Content-Type': 'application/json',
//                     Accept: 'application/json',
//                   },
//                   body: JSON.stringify(paymentDetails),
//                 }
//               );

//               if (verificationResponse.ok) {
//                 toast.success('Payment successful and verified!');
//                 setShowPaymentModal(false);
//                 setRoomOrderId(null);
//                 setRoomBillingInfo(null);
//                 setRoomOrderItems([]);
//                 setSelectedRoomOrder(null);
//                 setRoomSearchText('');
//                 setRoomTableSearchText('');
//                 await fetchRoomOrders();
//                 await fetchAvailableTablesForRoom();
//                 await fetchTables();
//                 setCart([]);
//                 setSpecialRequest('');
//               } else {
//                 toast.error('Payment verification failed.');
//               }
//             } catch (error) {
//               console.error('Verification error:', error);
//               toast.error('Error verifying payment. Please try again.');
//             } finally {
//               setIsProcessingPayment(false);
//             }
//           },
//           prefill: {
//             name: customerName || 'Guest',
//             email: customerEmail || 'guest@example.com',
//             contact: phoneNumber || '9999999999',
//           },
//           theme: {
//             color: '#3399cc',
//           },
//         };

//         const rzp = new window.Razorpay(options);
//         rzp.open();
//       } else {
//         setIsProcessingPayment(false);
//       }
//     } catch (error) {
//       console.error('Payment error:', error);
//       toast.error(error.message || 'Error processing payment. Please try again.');
//       setIsProcessingPayment(false);
//     }
//   };

//   const handlePaymentOption = (method) => {
//     setSelectedPaymentMethod(method);
//     setShowPaymentModal(true);
//   };

//   const confirmPayment = async () => {
//     try {
//       if (orderType === 'dining' || orderType === 'parcel') {
//         if (selectedPaymentMethod === 'cash') {
//           await handleCashPayment();
//         } else if (selectedPaymentMethod === 'razorpay') {
//           await handleRazorpayPayment();
//         }
//       } else if (orderType === 'room') {
//         if (selectedPaymentMethod === 'cash') {
//           await handleRoomCashPayment();
//         } else if (selectedPaymentMethod === 'razorpay') {
//           await handleRoomRazorpayPayment();
//         }
//       }
//     } finally {
//       setSelectedPaymentMethod(null);
//     }
//   };

//   const handlePrintInvoice = async () => {
//     if (orderType === 'dining' || orderType === 'parcel') {
//       if (!orderId) {
//         toast.error('No order found');
//         return;
//       }

//       setIsPrinting(true);
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/print/${orderId}`, {
//           method: 'POST',
//           headers: {
//             'Content-Type': 'application/json',
//           },
//         });

//         const data = await response.json();

//         if (response.ok) {
//           toast.success('Invoice printed successfully!');
//         } else {
//           toast.error(data.detail || 'Failed to print invoice');
//         }
//       } catch (error) {
//         console.error('Print error:', error);
//         toast.error('Failed to print invoice');
//       } finally {
//         setIsPrinting(false);
//       }
//     } else if (orderType === 'room') {
//       if (!roomOrderId) {
//         toast.error('No room order found');
//         return;
//       }

//       setIsPrinting(true);
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/food/print/${roomOrderId}`, {
//           method: 'POST',
//           headers: {
//             'Content-Type': 'application/json',
//           },
//         });

//         const data = await response.json();

//         if (response.ok) {
//           toast.success(`Room invoice printed successfully!`);
//         } else {
//           toast.error(data.detail || 'Failed to print room invoice');
//         }
//       } catch (error) {
//         console.error('Print error:', error);
//         toast.error('Failed to print room invoice');
//       } finally {
//         setIsPrinting(false);
//       }
//     } else {
//       toast.error('No order found');
//     }
//   };

//   const filteredItems = menuItems.filter((item) => {
//     if (item.status && item.status.toLowerCase() === 'delivered') {
//       return false;
//     }

//     const categoryid = getCategoryIdFromSubCategoryId(item.subCategoryId);
//     const matchesCategory = selectedCategory ? categoryid === selectedCategory : true;
//     const matchesSubcategory = selectedSubcategory
//       ? item.subCategoryId === selectedSubcategory
//       : true;

//     const searchLower = search.toLowerCase();
//     const matchesTitle = item.title.toLowerCase().includes(searchLower);
//     const matchesItemcode =
//       !isNaN(search) && search.trim() !== '' ? item.itemcode === parseInt(search) : false;
//     const matchesSearch = matchesTitle || matchesItemcode;

//     return matchesCategory && matchesSubcategory && matchesSearch;
//   });

//   if (loading) {
//     return (
//       <div className="min-h-screen bg-white flex items-center justify-center">
//         <div className="text-center">
//           <div className="w-12 h-12 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
//           <p className="text-gray-400">Loading menu...</p>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="min-h-screen bg-gray-50 p-6">
//       <header className="flex justify-between items-center mb-3 bg-white p-3">
//         <h1 className="text-black text-xl font-bold ml-2">Food & Beverages Management</h1>
//         <div className="flex space-x-6">
//           <button
//             onClick={() => navigate('/dashboard/core-modules/f&b-management/track-order-frontdesk')}
//             className="btn "
//           >
//             Order Overview
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/f&b-management/order-food')}
//             className="btn font-bold text-black"
//           >
//             Order Food
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/f&b-management/dining-tables')}
//             className="btn "
//           >
//             Dining Tables
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/f&b-management/track-order-kitchen')}
//             className="btn"
//           >
//             Kitchen Display System(KDS)
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/f&b-management/order-invoicing')}
//             className="btn text-gray-600 hover:text-black"
//           >
//             Billing & Invoicing
//           </button>
//         </div>
//       </header>

//       <div className="flex flex-col lg:flex-row">
//         <div className="flex-1 overflow-y-auto" style={{ height: 'calc(100vh - 80px)' }}>
//           <div className="p-2">
//             <div className="sticky top-0 z-10 bg-white pt-2 pb-2">
//               <div className="relative">
//                 <input
//                   type="text"
//                   placeholder="Search by name or item code.."
//                   value={search}
//                   onChange={(e) => setSearch(e.target.value)}
//                   className="w-full px-5 py-3 pl-11 pr-12 border border-gray-200 rounded-lg transition-all bg-white shadow-sm"
//                 />
//                 <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-300 text-sm" />

//                 {isSupported && (
//                   <button
//                     onClick={(e) => {
//                       e.preventDefault();
//                       e.stopPropagation();
//                       if (isListening && activeSpeechTarget === 'search') {
//                         stopSearchListening();
//                       } else {
//                         startSearchListening();
//                       }
//                     }}
//                     className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-2 rounded-full transition-all z-10 ${
//                       isListening && activeSpeechTarget === 'search'
//                         ? 'text-red-500 bg-red-50 animate-pulse scale-110'
//                         : 'text-gray-400 hover:text-black hover:bg-gray-100'
//                     }`}
//                     title={
//                       isListening && activeSpeechTarget === 'search'
//                         ? 'Stop listening'
//                         : 'Search by voice'
//                     }
//                     type="button"
//                   >
//                     <FaMicrophone size={18} />
//                   </button>
//                 )}

//                 {isListening && activeSpeechTarget === 'search' && (
//                   <div className="absolute -bottom-6 left-0 right-0 text-xs text-red-500 font-medium text-center animate-pulse">
//                     🎤 Listening for search... Speak now
//                   </div>
//                 )}
//               </div>
//             </div>

//             <div className="overflow-x-auto mb-2 pb-2 mt-2">
//               <div className="flex gap-1 min-w-min">
//                 <button
//                   onClick={() => {
//                     setSelectedCategory(null);
//                     setSelectedSubcategory(null);
//                   }}
//                   className={`px-3 py-2 text-[13px] lg:text-[13px] rounded-full transition-all whitespace-nowrap ${
//                     selectedCategory === null
//                       ? 'bg-black text-white'
//                       : 'bg-white text-gray-600 border border-gray-200 hover:border-black'
//                   }`}
//                 >
//                   All
//                 </button>
//                 {categories.map((category) => (
//                   <button
//                     key={category.id}
//                     onClick={() => {
//                       setSelectedCategory(category.id);
//                       setSelectedSubcategory(null);
//                     }}
//                     className={`px-3 sm:px-3 py-2 text-[13px] lg:text-[14px] font-medium rounded-full transition-all whitespace-nowrap ${
//                       selectedCategory === category.id
//                         ? 'bg-black text-white'
//                         : 'bg-white text-gray-600 border border-gray-200 hover:border-black'
//                     }`}
//                   >
//                     {category.name}
//                   </button>
//                 ))}
//               </div>
//             </div>

//             {selectedCategory &&
//               subcategories.filter((s) => s.categoryid === selectedCategory).length > 0 && (
//                 <div className="overflow-x-auto mb-5 pb-2">
//                   <div className="flex gap-2 min-w-min">
//                     <button
//                       onClick={() => setSelectedSubcategory(null)}
//                       className={`px-3 sm:px-4 py-1.5 text-xs rounded-full transition-all whitespace-nowrap ${
//                         selectedSubcategory === null
//                           ? 'bg-black text-white'
//                           : 'bg-white text-gray-500 border border-gray-200 hover:border-black'
//                       }`}
//                     >
//                       All
//                     </button>
//                     {subcategories
//                       .filter((sub) => sub.categoryid === selectedCategory)
//                       .map((sub) => (
//                         <button
//                           key={sub.id}
//                           onClick={() => setSelectedSubcategory(sub.id)}
//                           className={`px-3 sm:px-4 py-1.5 text-xs rounded-full transition-all whitespace-nowrap ${
//                             selectedSubcategory === sub.id
//                               ? 'bg-black text-white'
//                               : 'bg-white text-gray-500 border border-gray-200 hover:border-black'
//                           }`}
//                         >
//                           {sub.name}
//                         </button>
//                       ))}
//                   </div>
//                 </div>
//               )}

//             <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 gap-2">
//               {filteredItems.map((item) => (
//                 <div
//                   key={item.id}
//                   className="group border border-gray-100 rounded-xl hover:shadow-md transition-all duration-300 bg-white overflow-hidden"
//                 >
//                   <div className="relative h-40 bg-gray-50">
//                     {item.image ? (
//                       <img
//                         src={item.image}
//                         alt={item.title}
//                         className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
//                         loading="lazy"
//                         onError={(e) => {
//                           e.target.style.display = 'none';
//                           e.target.parentElement.innerHTML = `
//           <div class="flex items-center justify-center h-full bg-gray-100">
//             <div class="text-center">
//               <FaImage class="text-4xl text-gray-300 mx-auto mb-2" />
//               <span class="text-gray-400 text-xs">No Image Uploaded</span>
//             </div>
//           </div>
//         `;
//                         }}
//                       />
//                     ) : (
//                       <div className="flex items-center justify-center h-full bg-gray-100">
//                         <div className="text-center">
//                           <FaImage className="text-4xl text-gray-300 mx-auto mb-2" />
//                           <span className="text-gray-400 text-xs">No Image Uploaded</span>
//                         </div>
//                       </div>
//                     )}

//                     <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-1 sm:gap-2">
//                       {item.foodType === 'Veg' ? (
//                         <FaLeaf className="text-green-600 bg-white p-1 rounded-full text-base sm:text-lg" />
//                       ) : (
//                         <FaDrumstickBite className="text-orange-600 bg-white p-1 rounded-full text-base sm:text-lg" />
//                       )}

//                       {item.itemcode > 0 && (
//                         <span className="bg-purple-100 text-purple-800 text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full flex items-center gap-1">
//                           <FaBarcode className="text-xs" />
//                           Code: {item.itemcode}
//                         </span>
//                       )}
//                     </div>

//                     {!item.isActive && (
//                       <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
//                         <span className="bg-white text-black px-3 py-1 rounded-full text-xs font-medium">
//                           Unavailable
//                         </span>
//                       </div>
//                     )}
//                   </div>

//                   <div className="p-3 sm:p-4">
//                     <div className="flex justify-between items-start mb-2">
//                       <h3 className="font-semibold text-black text-sm">{item.title}</h3>

//                       {item.value > 0 && item.value_type && (
//                         <span className="bg-yellow-50 text-yellow-700 text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full flex items-center gap-1">
//                           <FaTag className="text-xs" />
//                           Tax:{item.value}
//                           {item.value_type === 'P' ? '%' : ' ₹'}
//                         </span>
//                       )}
//                     </div>

//                     <p className="text-gray-400 text-xs sm:text-sm mb-2 line-clamp-2">
//                       {item.description}
//                     </p>

//                     <div className="flex flex-wrap gap-1 sm:gap-2 mb-3">
//                       {item.price_type && (
//                         <span className="bg-blue-50 text-blue-700 text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full flex items-center gap-1">
//                           <span className="font-bold text-black">₹{item.price}</span>/
//                           {item.price_type}
//                         </span>
//                       )}
//                     </div>

//                     <button
//                       onClick={() => handleAddToCart(item)}
//                       disabled={!item.isActive}
//                       className={`w-full py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
//                         item.isActive
//                           ? justAddedItemId === item.id
//                             ? 'bg-green-500 text-white'
//                             : 'bg-black text-white hover:bg-gray-800'
//                           : 'bg-gray-100 text-gray-400 cursor-not-allowed'
//                       }`}
//                     >
//                       {justAddedItemId === item.id ? 'Added!' : 'Add to Cart'}
//                     </button>
//                   </div>
//                 </div>
//               ))}
//             </div>

//             {filteredItems.length === 0 && (
//               <div className="text-center py-20">
//                 <p className="text-gray-400">No items found</p>
//               </div>
//             )}
//           </div>
//         </div>

//         {/* Right Side - Order Panel */}
//         <div
//           className="w-full lg:w-[420px] border-l border-gray-100 bg-white flex flex-col"
//           style={{ height: 'calc(100vh - 80px)' }}
//         >
//           <div className="px-6 py-2 border-b border-gray-100">
//             <div className="flex justify-between items-center mb-4">
//               <div className="flex items-center gap-2">
//                 <FaShoppingCart className="text-black text-lg" />
//                 <h2 className="font-semibold text-black">Place Order</h2>
//               </div>
//               {cart.length > 0 && (
//                 <button
//                   onClick={clearAllFields}
//                   className="text-white text-sm transition-all flex items-center gap-1 px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600"
//                 >
//                   <FaTrash size={12} /> Clear All
//                 </button>
//               )}
//             </div>

//             <div className="grid grid-cols-3 gap-2">
//               <button
//                 onClick={() => {
//                   setOrderType('dining');
//                 }}
//                 className={`py-2 text-sm rounded-lg transition-all ${
//                   orderType === 'dining'
//                     ? 'bg-black text-white'
//                     : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
//                 }`}
//               >
//                 <FaUsers className="inline mr-1 text-xs" /> Dine In
//               </button>
//               <button
//                 onClick={() => {
//                   setOrderType('room');
//                   setRoomOrderId(null);
//                   setRoomBillingInfo(null);
//                   setRoomOrderItems([]);
//                   setSelectedRoomOrder(null);
//                   setRoomSearchText('');
//                 }}
//                 className={`py-2 text-sm rounded-lg transition-all ${
//                   orderType === 'room'
//                     ? 'bg-black text-white'
//                     : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
//                 }`}
//               >
//                 <FaHotel className="inline mr-1 text-xs" /> Room
//               </button>
//               <button
//                 onClick={() => {
//                   setOrderType('parcel');
//                   setOrderId(null);
//                   setBillingInfo(null);
//                   setOrderItems([]);
//                   setSelectedParcelOrder(null);
//                   setParcelSearchText('');
//                 }}
//                 className={`py-2 text-sm rounded-lg transition-all ${
//                   orderType === 'parcel'
//                     ? 'bg-black text-white'
//                     : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
//                 }`}
//               >
//                 <FaBox className="inline mr-1 text-xs" /> Parcel
//               </button>
//             </div>

//             {/* Table Selection Dropdown for Dining */}
//             {orderType === 'dining' && (
//               <div className="mt-4 relative">
//                 <div className="relative">
//                   <FaTable className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm z-10" />
//                   <input
//                     type="text"
//                     placeholder="Select or search table..."
//                     value={tableSearchText}
//                     onChange={(e) => {
//                       setTableSearchText(e.target.value);
//                       setShowTableDropdown(true);
//                     }}
//                     onFocus={() => setShowTableDropdown(true)}
//                     className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                   />
//                 </div>

//                 {showTableDropdown && filteredTables.length > 0 && (
//                   <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
//                     {filteredTables.map((table) => (
//                       <button
//                         key={table.id}
//                         onClick={() => handleTableSelect(table)}
//                         className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between border-b border-gray-100 last:border-0"
//                       >
//                         <div className="flex items-center gap-2">
//                           <FaCircle className={`${getStatusColor(table.status)} text-xs`} />
//                           <span className="font-medium">{table.table_number}</span>
//                           <span className="text-sm text-gray-500">({table.table_name})</span>
//                           <span className="text-xs text-gray-400">Cap: {table.capacity}</span>
//                         </div>
//                         <span
//                           className={`text-xs px-2 py-1 rounded-full ${
//                             table.status === 'available'
//                               ? 'bg-green-100 text-green-700'
//                               : table.status === 'occupied'
//                                 ? 'bg-red-100 text-red-700'
//                                 : 'bg-yellow-100 text-yellow-700'
//                           }`}
//                         >
//                           {getStatusText(table.status)}
//                         </span>
//                       </button>
//                     ))}
//                   </div>
//                 )}
//               </div>
//             )}

//             {/* Room Order Selection Dropdown */}
//             {orderType === 'room' && (
//               <div className="mt-4">
//                 <div className="relative">
//                   <div className="flex gap-2">
//                     <div className="relative flex-1">
//                       <FaHotel className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm z-10" />
//                       <input
//                         type="text"
//                         placeholder="Search room number or enter new room number..."
//                         value={roomSearchText}
//                         onChange={(e) => {
//                           const value = e.target.value;
//                           setRoomSearchText(value);
//                           setShowRoomDropdown(true);

//                           if (/^\d+$/.test(value)) {
//                             setRoomNumber(value);
//                             setSelectedRoomOrder(null);
//                             setRoomOrderId(null);
//                             setRoomOrderItems([]);
//                             setRoomBillingInfo(null);
//                             setCustomerName('');
//                             setPhoneNumber('');
//                             setSpecialRequest('');
//                           }
//                         }}
//                         onFocus={() => {
//                           setShowRoomDropdown(true);
//                         }}
//                         onBlur={() => {
//                           setTimeout(() => {
//                             setShowRoomDropdown(false);
//                           }, 200);
//                         }}
//                         className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                       />
//                     </div>
//                     <button
//                       onClick={handleNewRoomOrder}
//                       className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-all text-sm whitespace-nowrap"
//                     >
//                       + New Order
//                     </button>
//                   </div>

//                   {showRoomDropdown && filteredRoomOrders.length > 0 && (
//                     <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
//                       {filteredRoomOrders.map((order) => (
//                         <button
//                           key={order.id || order.order_id}
//                           onClick={() => {
//                             handleRoomSelect(order);
//                             setShowRoomDropdown(false);
//                           }}
//                           className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
//                         >
//                           <div className="flex justify-between items-center">
//                             <div>
//                               <div className="font-medium text-sm">Room {order.room_number}</div>
//                               <div className="text-xs text-gray-500">
//                                 {order.customer_name || 'Guest'}
//                                 {order.table_number && ` • Table ${order.table_number}`}
//                               </div>
//                             </div>
//                             <div className="text-right">
//                               <div className="font-semibold text-sm">
//                                 ₹{order.totalprice || order.total_amount || 0}
//                               </div>
//                               <div
//                                 className={`text-xs capitalize ${
//                                   order.status === 'Pending' || order.order_status === 'Pending'
//                                     ? 'text-yellow-600'
//                                     : order.status === 'Served' || order.order_status === 'Served'
//                                       ? 'text-blue-600'
//                                       : 'text-green-600'
//                                 }`}
//                               >
//                                 {order.status || order.order_status || 'Pending'}
//                               </div>
//                             </div>
//                           </div>
//                         </button>
//                       ))}
//                     </div>
//                   )}
//                 </div>

//                 <div className="mt-3 space-y-2">
//                   <button
//                     onClick={() => setShowAdditionalDetails(!showAdditionalDetails)}
//                     className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all"
//                   >
//                     <span className="text-sm font-medium text-gray-800">
//                       Additional Details (Optional)
//                     </span>
//                     {showAdditionalDetails ? (
//                       <FaChevronUp size={12} />
//                     ) : (
//                       <FaChevronDown size={12} />
//                     )}
//                   </button>

//                   {showAdditionalDetails && (
//                     <div className="mt-2 space-y-2 animate-fadeIn">
//                       <div className="relative">
//                         <FaTable className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm z-10" />
//                         <input
//                           type="text"
//                           placeholder="Select or search table number..."
//                           value={roomTableSearchText}
//                           onChange={(e) => {
//                             setRoomTableSearchText(e.target.value);
//                             setShowRoomTableDropdown(true);
//                             if (e.target.value) {
//                               setTableNumber(e.target.value);
//                             }
//                           }}
//                           onFocus={() => setShowRoomTableDropdown(true)}
//                           onBlur={() => {
//                             setTimeout(() => {
//                               setShowRoomTableDropdown(false);
//                             }, 200);
//                           }}
//                           className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                         />

//                         {showRoomTableDropdown && filteredRoomTables.length > 0 && (
//                           <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
//                             {filteredRoomTables.map((table) => (
//                               <button
//                                 key={table.id}
//                                 onClick={() => {
//                                   setTableNumber(table.table_number);
//                                   setRoomTableSearchText(
//                                     `${table.table_number} (${table.table_name || 'Table'})`
//                                   );
//                                   setShowRoomTableDropdown(false);
//                                 }}
//                                 className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-center justify-between"
//                               >
//                                 <div>
//                                   <span className="font-medium text-sm">{table.table_number}</span>
//                                   <span className="text-xs text-gray-500 ml-2">
//                                     {table.table_name}
//                                   </span>
//                                 </div>
//                                 <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
//                                   Available
//                                 </span>
//                               </button>
//                             ))}
//                           </div>
//                         )}
//                       </div>

//                       <div className="relative">
//                         <FaIdCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
//                         <input
//                           type="text"
//                           placeholder="GST Number"
//                           value={gstNumber}
//                           onChange={(e) => setGstNumber(e.target.value)}
//                           className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                           onClick={(e) => {
//                             e.stopPropagation();
//                           }}
//                         />
//                       </div>
//                     </div>
//                   )}
//                 </div>
//               </div>
//             )}

//             {/* Parcel Order Selection Dropdown */}
//             {orderType === 'parcel' && (
//               <div className="mt-4 relative">
//                 <div className="flex gap-2">
//                   <div className="relative flex-1">
//                     <FaList className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm z-10" />
//                     <input
//                       type="text"
//                       placeholder="Search parcel order by number, name or phone..."
//                       value={parcelSearchText}
//                       onChange={(e) => {
//                         setParcelSearchText(e.target.value);
//                         setShowParcelDropdown(true);
//                       }}
//                       onFocus={() => setShowParcelDropdown(true)}
//                       className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                     />
//                   </div>
//                   <button
//                     onClick={handleNewParcelOrder}
//                     className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-all text-sm whitespace-nowrap"
//                   >
//                     + New Order
//                   </button>
//                 </div>

//                 {showParcelDropdown && filteredParcelOrders.length > 0 && (
//                   <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
//                     {filteredParcelOrders.map((order) => (
//                       <button
//                         key={order.order_id}
//                         onClick={() => handleParcelSelect(order)}
//                         className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
//                       >
//                         <div className="flex justify-between items-center">
//                           <div>
//                             <div className="font-medium text-sm">{order.parcel_number}</div>
//                             <div className="text-xs text-gray-500">
//                               {order.customer_name || 'Guest'}
//                             </div>
//                           </div>
//                           <div className="text-right">
//                             <div className="font-semibold text-sm">₹{order.total_amount}</div>
//                             <div
//                               className={`text-xs ${
//                                 order.order_status === 'pending'
//                                   ? 'text-yellow-600'
//                                   : 'text-blue-600'
//                               }`}
//                             >
//                               {order.order_status}
//                             </div>
//                           </div>
//                         </div>
//                       </button>
//                     ))}
//                   </div>
//                 )}
//               </div>
//             )}

//             {/* Order Items Summary with Update Button */}
//             {((orderItems.length > 0 && (orderType === 'dining' || orderType === 'parcel')) ||
//               ((roomOrderItems.length > 0 || selectedRoomOrder) && orderType === 'room')) && (
//               <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
//                 <button
//                   onClick={() => setShowOrderItems(!showOrderItems)}
//                   className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
//                 >
//                   <div className="flex items-center gap-2">
//                     <FaList className="text-gray-400" />
//                     <span className="font-semibold text-sm">Order Items</span>
//                     <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
//                       {orderType === 'room' ? roomOrderItems.length : orderItems.length} items
//                     </span>
//                     {showOrderItems ? (
//                       <FaChevronUp size={12} className="text-gray-400" />
//                     ) : (
//                       <FaChevronDown size={12} className="text-gray-400" />
//                     )}
//                   </div>
//                   {(billingInfo || roomBillingInfo) && (
//                     <span className="font-bold text-sm text-black">
//                       ₹{billingInfo?.final_amount || roomBillingInfo?.final_amount || 0}
//                     </span>
//                   )}
//                 </button>

//                 {showOrderItems && (
//                   <div className="border-t border-gray-100">
//                     <div className="p-3 space-y-3 max-h-64 overflow-y-auto">
//                       {orderType === 'room' && roomOrderItems.length === 0 && (
//                         <div className="text-xs text-gray-500 text-center py-4">
//                           This order has no items to show. They may all have been delivered already.
//                         </div>
//                       )}
//                       {(orderType === 'room' ? roomOrderItems : orderItems).map((item, idx) => {
//                         const pendingQty =
//                           pendingQuantities[item.order_item_id] !== undefined
//                             ? pendingQuantities[item.order_item_id]
//                             : item.quantity || 1;

//                         const isQuantityChanged =
//                           pendingQuantities[item.order_item_id] !== undefined &&
//                           pendingQuantities[item.order_item_id] !== item.quantity;

//                         return (
//                           <div
//                             key={idx}
//                             className="bg-gray-50 rounded-lg p-3 border border-gray-100 hover:border-gray-200 transition-all"
//                           >
//                             <div className="flex justify-between items-start mb-2">
//                               <div className="flex-1">
//                                 <div className="flex items-center gap-2">
//                                   <span className="font-medium text-gray-800 text-sm">
//                                     {item.food_name || item.description || 'Item'}
//                                   </span>
//                                   {item.is_complimentary && (
//                                     <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
//                                       Complimentary
//                                     </span>
//                                   )}
//                                 </div>
//                               </div>
//                               <div className="flex items-center gap-3">
//                                 <span className="font-semibold text-sm">
//                                   ₹{((item.unit_price || 0) * (item.quantity || 1)).toFixed(2)}
//                                 </span>
//                                 <button
//                                   onClick={() => handleUpdateQuantityWithButton(item)}
//                                   className={`px-3 py-1 rounded-lg transition-all text-xs font-medium flex items-center gap-1 ${
//                                     isQuantityChanged
//                                       ? 'bg-blue-600 text-white hover:bg-blue-700'
//                                       : 'bg-gray-200 text-gray-500 cursor-not-allowed'
//                                   }`}
//                                   disabled={!isQuantityChanged}
//                                 >
//                                   <FaCheckCircle size={12} />
//                                   Update
//                                 </button>
//                               </div>
//                             </div>

//                             <div className="flex items-center justify-between mt-2">
//                               <div className="flex items-center gap-3">
//                                 <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-2 py-1">
//                                   <button
//                                     onClick={() => {
//                                       const currentQty =
//                                         pendingQuantities[item.order_item_id] !== undefined
//                                           ? pendingQuantities[item.order_item_id]
//                                           : item.quantity || 1;
//                                       if (currentQty > 1) {
//                                         handlePendingQuantityChange(
//                                           item.order_item_id,
//                                           currentQty - 1
//                                         );
//                                       } else {
//                                         if (
//                                           window.confirm(
//                                             `Remove ${item.food_name || 'this item'} from order?`
//                                           )
//                                         ) {
//                                           handleRemoveItem(item);
//                                         }
//                                       }
//                                     }}
//                                     className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded transition-all text-gray-600"
//                                     title="Decrease quantity"
//                                   >
//                                     <FaMinus size={11} />
//                                   </button>
//                                   <input
//                                     type="number"
//                                     min="1"
//                                     max="99"
//                                     value={pendingQty}
//                                     onChange={(e) => {
//                                       const val = parseInt(e.target.value);
//                                       if (!isNaN(val) && val > 0) {
//                                         handlePendingQuantityChange(item.order_item_id, val);
//                                       }
//                                     }}
//                                     className="w-10 text-center text-sm font-semibold border-0 focus:outline-none bg-transparent"
//                                   />
//                                   <button
//                                     onClick={() => {
//                                       const currentQty =
//                                         pendingQuantities[item.order_item_id] !== undefined
//                                           ? pendingQuantities[item.order_item_id]
//                                           : item.quantity || 1;
//                                       handlePendingQuantityChange(
//                                         item.order_item_id,
//                                         currentQty + 1
//                                       );
//                                     }}
//                                     className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded transition-all text-gray-600"
//                                     title="Increase quantity"
//                                   >
//                                     <FaPlus size={11} />
//                                   </button>
//                                 </div>

//                                 <button
//                                   onClick={() => {
//                                     if (
//                                       window.confirm(
//                                         `Remove ${item.food_name || 'this item'} from order?`
//                                       )
//                                     ) {
//                                       handleRemoveItem(item);
//                                     }
//                                   }}
//                                   className="flex items-center gap-1 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-all text-xs font-medium"
//                                 >
//                                   <FaTrash size={11} />
//                                   Remove
//                                 </button>
//                               </div>

//                               <div className="flex items-center gap-2">
//                                 <span className="text-xs text-gray-500">
//                                   Qty: {item.quantity || 1}
//                                 </span>
//                                 {isQuantityChanged && (
//                                   <span className="text-xs text-blue-600 font-medium">
//                                     → {pendingQty}
//                                   </span>
//                                 )}
//                               </div>
//                             </div>
//                           </div>
//                         );
//                       })}
//                     </div>

//                     {(orderType === 'dining' || orderType === 'parcel') &&
//                       orderItems.length > 0 && (
//                         <div className="border-t border-gray-100 px-3 py-2 bg-gray-50 flex justify-between items-center">
//                           <button
//                             onClick={handleClearAllItems}
//                             className="text-xs text-red-600 hover:text-red-800 font-medium flex items-center gap-1 transition-colors"
//                           >
//                             <FaTrash size={10} />
//                             Clear All Items
//                           </button>
//                           <span className="text-xs text-gray-500">
//                             {orderItems.length} items in order
//                           </span>
//                         </div>
//                       )}

//                     {orderType === 'room' && roomOrderItems.length > 0 && (
//                       <div className="border-t border-gray-100 px-3 py-2 bg-gray-50 flex justify-between items-center">
//                         <button
//                           onClick={handleClearAllItems}
//                           className="text-xs text-red-600 hover:text-red-800 font-medium flex items-center gap-1 transition-colors"
//                         >
//                           <FaTrash size={10} />
//                           Clear All Items
//                         </button>
//                         <span className="text-xs text-gray-500">
//                           {roomOrderItems.length} items in order
//                         </span>
//                       </div>
//                     )}
//                   </div>
//                 )}
//               </div>
//             )}

//             {/* Customer Details for Dining & Parcel with Company Search INSIDE */}
//             {(orderType === 'dining' || orderType === 'parcel') && !orderId && (
//               <div className="mt-2">
//                 <button
//                   onClick={() => setShowAdditionalDetails(!showAdditionalDetails)}
//                   className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all"
//                 >
//                   <span className="text-sm font-medium text-gray-800">
//                     Customer Details (Optional)
//                   </span>
//                   {showAdditionalDetails ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
//                 </button>

//                 {showAdditionalDetails && (
//                   <div className="mt-2 space-y-2 animate-fadeIn">
//                     <div className="relative" ref={companyDropdownRef}>
//                       <div className="relative">
//                         <FaBuilding className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm z-10" />
//                         <input
//                           type="text"
//                           placeholder="Search company name..."
//                           value={companySearchText}
//                           onChange={(e) => {
//                             setCompanySearchText(e.target.value);
//                             setSelectedCompany(null);
//                             if (!e.target.value) {
//                               setGstNumber('');
//                             }
//                           }}
//                           onFocus={() => {
//                             if (companySearchText.length > 1) {
//                               setShowCompanyDropdown(true);
//                             }
//                           }}
//                           className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                         />
//                       </div>

//                       {showCompanyDropdown && filteredCompanies.length > 0 && (
//                         <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
//                           {filteredCompanies.map((company) => (
//                             <button
//                               key={company.id}
//                               onClick={() => handleCompanySelect(company)}
//                               className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
//                             >
//                               <div className="flex justify-between items-center">
//                                 <div>
//                                   <div className="font-medium text-sm">{company.companyname}</div>
//                                   {company.companyid && (
//                                     <div className="text-xs text-gray-400">
//                                       ID: {company.companyid}
//                                     </div>
//                                   )}
//                                 </div>
//                                 {company.gstnumber && (
//                                   <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
//                                     GST: {company.gstnumber}
//                                   </span>
//                                 )}
//                               </div>
//                             </button>
//                           ))}
//                         </div>
//                       )}
//                     </div>

//                     <div className="relative">
//                       <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
//                       <input
//                         type="text"
//                         placeholder="Customer name"
//                         value={customerName}
//                         onChange={(e) => setCustomerName(e.target.value)}
//                         className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                       />
//                     </div>

//                     <div className="relative">
//                       <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
//                       <input
//                         type="email"
//                         placeholder="Email address"
//                         value={customerEmail}
//                         onChange={(e) => setCustomerEmail(e.target.value)}
//                         className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                       />
//                     </div>

//                     <div className="relative">
//                       <FaPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
//                       <input
//                         type="tel"
//                         placeholder="Phone number"
//                         value={phoneNumber}
//                         onChange={(e) => setPhoneNumber(e.target.value)}
//                         className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                       />
//                     </div>

//                     <div className="relative">
//                       <FaIdCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
//                       <input
//                         type="text"
//                         placeholder="GST number"
//                         value={gstNumber}
//                         onChange={(e) => setGstNumber(e.target.value)}
//                         className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                       />
//                     </div>
//                   </div>
//                 )}
//               </div>
//             )}

//             {/* Number of Guests for Dining */}
//             {orderType === 'dining' && !orderId && (
//               <div className="mt-2">
//                 <div className="flex items-center justify-between">
//                   <div className="flex items-center gap-2">
//                     <FaUsers className="text-gray-400 text-sm" />
//                     <span className="text-sm text-gray-600">Number of guests</span>
//                   </div>
//                   <div className="flex items-center gap-3">
//                     <button
//                       onClick={handleDecreaseGuests}
//                       className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center hover:border-black transition-all"
//                     >
//                       <FaMinus size={10} />
//                     </button>
//                     <span className="text-sm font-medium w-6 text-center">{numberOfGuests}</span>
//                     <button
//                       onClick={handleIncreaseGuests}
//                       className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center hover:border-black transition-all"
//                     >
//                       <FaPlus size={10} />
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             )}
//           </div>

//           <div className="flex-1 overflow-y-auto px-6 py-2">
//             {cart.length === 0 ? (
//               <div className="text-center py-5">
//                 <FaShoppingCart className="text-4xl text-gray-200 mx-auto mb-3" />
//                 <p className="text-gray-400 text-sm">Cart is empty</p>
//               </div>
//             ) : (
//               <div className="space-y-4">
//                 {cart.map((item) => (
//                   <div key={item.id} className="border-b border-gray-100 pb-4">
//                     <div className="flex justify-between mb-2">
//                       <div>
//                         <h4 className="font-medium text-black text-sm">{item.title}</h4>
//                         <div className="flex flex-wrap gap-2 mt-1">
//                           <span className="text-gray-400 text-xs">₹{item.price}</span>
//                           {item.price_type && (
//                             <span className="text-gray-400 text-xs">/{item.price_type}</span>
//                           )}
//                           {item.value > 0 && item.value_type && (
//                             <span className="text-gray-400 text-xs">
//                               Tax: {item.value}
//                               {item.value_type === 'P' ? '%' : ' ₹'}
//                             </span>
//                           )}
//                         </div>
//                       </div>
//                       <button
//                         onClick={() => handleRemoveFromCart(item.id)}
//                         className="text-gray-300 hover:text-black"
//                       >
//                         <FaTimes size={12} />
//                       </button>
//                     </div>
//                     <div className="flex justify-between items-center">
//                       <div className="flex items-center gap-3">
//                         <button
//                           onClick={() => handleDecreaseQuantity(item.id)}
//                           className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center hover:border-black transition-all"
//                         >
//                           <FaMinus size={10} />
//                         </button>
//                         <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
//                         <button
//                           onClick={() => handleIncreaseQuantity(item.id)}
//                           className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center hover:border-black transition-all"
//                         >
//                           <FaPlus size={10} />
//                         </button>
//                       </div>
//                       <span className="font-medium">₹{item.price * item.quantity}</span>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </div>

//           {cart.length > 0 && (
//             <div className="border-t border-gray-100 px-6 py-2">
//               {/* Special instructions */}
//               <div className="relative">
//                 <textarea
//                   placeholder="Special instructions (optional)..."
//                   value={specialRequest}
//                   onChange={(e) => setSpecialRequest(e.target.value)}
//                   rows={1}
//                   className="w-full px-3 py-2 pr-12 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black resize-none"
//                 />
//                 {isSupported && (
//                   <button
//                     onClick={(e) => {
//                       e.preventDefault();
//                       e.stopPropagation();
//                       if (isListening && activeSpeechTarget === 'special') {
//                         stopSpecialListening();
//                       } else {
//                         startSpecialListening();
//                       }
//                     }}
//                     className={`absolute right-2 bottom-2 p-1.5 rounded-full transition-all z-10 ${
//                       isListening && activeSpeechTarget === 'special'
//                         ? 'text-red-500 bg-red-50 animate-pulse scale-110'
//                         : 'text-gray-400 hover:text-black hover:bg-gray-100'
//                     }`}
//                     title={
//                       isListening && activeSpeechTarget === 'special'
//                         ? 'Stop listening'
//                         : 'Add special instructions by voice'
//                     }
//                     type="button"
//                   >
//                     <FaMicrophone size={16} />
//                   </button>
//                 )}
//                 {isListening && activeSpeechTarget === 'special' && (
//                   <div className="absolute -bottom-5 left-0 right-0 text-xs text-red-500 font-medium text-center animate-pulse">
//                     🎤 Listening for instructions... Speak now
//                   </div>
//                 )}
//               </div>
//               {!orderId && !roomOrderId && (
//                 <div className="mt-1">
//                   <div className="flex items-center gap-3">
//                     <div className="flex-[2]">
//                       <label className="text-xs text-gray-500 font-medium block mb-1">
//                         Discount (%)
//                       </label>
//                       <div className="relative">
//                         <input
//                           type="text"
//                           value={discountPercent || ''}
//                           onChange={(e) => {
//                             const rawValue = e.target.value;
//                             // Only allow numbers and decimal point
//                             const cleanValue = rawValue.replace(/[^0-9.]/g, '');

//                             // Remove leading zeros (but keep single zero for decimal)
//                             let processedValue = cleanValue;
//                             if (
//                               processedValue.length > 1 &&
//                               processedValue.startsWith('0') &&
//                               processedValue[1] !== '.'
//                             ) {
//                               processedValue = processedValue.replace(/^0+/, '');
//                             }

//                             // If empty, set to 0
//                             if (processedValue === '') {
//                               setDiscountPercent(0);
//                               return;
//                             }

//                             const value = parseFloat(processedValue);
//                             if (isNaN(value)) {
//                               return;
//                             }

//                             if (value > 25) {
//                               toast.error('Discount cannot exceed 25%');
//                               setDiscountPercent(25);
//                               return;
//                             }

//                             if (value < 0) {
//                               setDiscountPercent(0);
//                               return;
//                             }

//                             setDiscountPercent(value);
//                           }}
//                           className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-black ${
//                             discountError ? 'border-red-500' : 'border-gray-200'
//                           }`}
//                           placeholder="Enter discount %"
//                           inputMode="decimal"
//                         />
//                         <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">
//                           Max 25%
//                         </span>
//                       </div>
//                       {discountError && (
//                         <p className="text-red-500 text-xs mt-1">{discountError}</p>
//                       )}
//                     </div>
//                     {discountAmount > 0 && (
//                       <div className="flex-1">
//                         <label className="text-xs text-gray-500 font-medium block mb-1">
//                           Discount Amount
//                         </label>
//                         <div className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm font-semibold text-green-700">
//                           -₹{discountAmount.toFixed(2)}
//                         </div>
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               )}

//               {/* TOTALS - Correct Order */}
//               <div className="space-y-2 mt-3">
//                 {/* 1. Subtotal (Food Amount) */}
//                 <div className="flex justify-between text-sm">
//                   <span className="text-gray-500">Subtotal (Food)</span>
//                   <span>₹{totalAmount.toFixed(2)}</span>
//                 </div>

//                 {/* 2. Discount (if applied) - Only show when creating new order (no existing order) */}
//                 {!orderId && !roomOrderId && discountAmount > 0 && (
//                   <div className="flex justify-between text-sm text-green-600">
//                     <span>Discount ({discountPercent}%)</span>
//                     <span>-₹{discountAmount.toFixed(2)}</span>
//                   </div>
//                 )}

//                 {/* 3. Taxable Amount */}
//                 {!orderId && !roomOrderId ? (
//                   // For new orders: Taxable Amount = Subtotal - Discount
//                   <div className="flex justify-between text-sm border-t border-dashed border-gray-200 pt-1">
//                     <span className="text-gray-600 font-medium">Taxable Amount</span>
//                     <span className="font-medium">₹{taxableAmount.toFixed(2)}</span>
//                   </div>
//                 ) : (
//                   // For existing orders (adding items): Taxable Amount = Subtotal (no discount)
//                   <div className="flex justify-between text-sm border-t border-dashed border-gray-200 pt-1">
//                     <span className="text-gray-600 font-medium">Taxable Amount</span>
//                     <span className="font-medium">₹{totalAmount.toFixed(2)}</span>
//                   </div>
//                 )}

//                 {/* 4. Taxes (calculated on taxable amount) */}
//                 {orderType === 'dining' && taxes > 0 ? (
//                   <>
//                     <div className="flex justify-between text-sm items-center">
//                       <div className="flex items-center gap-2">
//                         <span className="text-gray-500">Taxes (incl. GST)</span>
//                         <button
//                           onClick={() => setShowTaxBreakdown(!showTaxBreakdown)}
//                           className="text-gray-400 hover:text-black transition-colors"
//                           title="View tax breakdown"
//                         >
//                           {showTaxBreakdown ? (
//                             <FaChevronUp size={10} />
//                           ) : (
//                             <FaChevronDown size={10} />
//                           )}
//                         </button>
//                       </div>
//                       <span>₹{taxes.toFixed(2)}</span>
//                     </div>

//                     {showTaxBreakdown && (
//                       <div className="ml-4 pl-2 border-l-2 border-gray-200 space-y-1 mt-1 animate-fadeIn">
//                         <div className="flex justify-between text-xs text-gray-500">
//                           <span>CGST</span>
//                           <span>₹{cgstAmount.toFixed(2)}</span>
//                         </div>
//                         <div className="flex justify-between text-xs text-gray-500">
//                           <span>SGST</span>
//                           <span>₹{sgstAmount.toFixed(2)}</span>
//                         </div>
//                       </div>
//                     )}
//                   </>
//                 ) : (
//                   <div className="flex justify-between text-sm">
//                     <span className="text-gray-500">Taxes</span>
//                     <span>₹{taxes.toFixed(2)}</span>
//                   </div>
//                 )}

//                 {/* 5. Total */}
//                 <div className="border-t border-gray-200 pt-2 mt-2">
//                   <div className="flex justify-between font-semibold text-lg">
//                     <span>Total</span>
//                     <span
//                       className={
//                         !orderId && !roomOrderId && discountAmount > 0 ? 'text-green-700' : ''
//                       }
//                     >
//                       {!orderId && !roomOrderId
//                         ? `₹${finalTotalWithDiscount.toFixed(2)}`
//                         : `₹${(totalAmount + taxes).toFixed(2)}`}
//                     </span>
//                   </div>
//                 </div>
//               </div>

//               {/* Place Order Button */}
//               <button
//                 onClick={handlePlaceOrder}
//                 disabled={isPlacingOrder || isAddingItems || !!discountError}
//                 className={`w-full py-3 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-all flex items-center justify-center gap-2 mt-3 ${
//                   isPlacingOrder || isAddingItems || discountError
//                     ? 'opacity-50 cursor-not-allowed'
//                     : ''
//                 }`}
//               >
//                 {isPlacingOrder || isAddingItems ? (
//                   <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
//                 ) : (
//                   <>
//                     {(orderId && (orderType === 'parcel' ? selectedParcelOrder : orderId)) ||
//                     roomOrderId
//                       ? 'Add Items to Order'
//                       : 'Place Order'}
//                     <FaArrowRight size={14} />
//                   </>
//                 )}
//               </button>
//             </div>
//           )}

//           {/* Payment Options Section */}
//           {((orderType === 'dining' || orderType === 'parcel') &&
//             orderId &&
//             billingInfo &&
//             !billingInfo.is_paid) ||
//           (orderType === 'room' &&
//             roomOrderId &&
//             roomBillingInfo &&
//             !roomBillingInfo.is_paid &&
//             selectedRoomOrder?.booking_id) ? (
//             <div className="border-t border-gray-100 px-6 py-3 bg-gray-50">
//               <h3 className="font-semibold text-sm mb-3 text-gray-700">Complete Payment</h3>
//               <div className="flex gap-2">
//                 <button
//                   onClick={() => handlePaymentOption('cash')}
//                   disabled={isProcessingPayment}
//                   className={`flex-1 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-all flex items-center justify-center gap-2 text-sm ${
//                     isProcessingPayment ? 'opacity-50 cursor-not-allowed' : ''
//                   }`}
//                 >
//                   {isProcessingPayment ? (
//                     <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
//                   ) : (
//                     <>
//                       <FaMoneyBill /> Cash
//                     </>
//                   )}
//                 </button>
//                 <button
//                   onClick={() => handlePaymentOption('razorpay')}
//                   disabled={isProcessingPayment}
//                   className={`flex-1 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all flex items-center justify-center gap-2 text-sm ${
//                     isProcessingPayment ? 'opacity-50 cursor-not-allowed' : ''
//                   }`}
//                 >
//                   {isProcessingPayment ? (
//                     <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
//                   ) : (
//                     <>
//                       <FaDigitalOcean /> Digital
//                     </>
//                   )}
//                 </button>
//                 <button
//                   onClick={handlePrintInvoice}
//                   disabled={isPrinting}
//                   className={`flex-1 py-2 bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-800 transition-all flex items-center justify-center gap-2 text-sm ${
//                     isPrinting ? 'opacity-50 cursor-not-allowed' : ''
//                   }`}
//                 >
//                   {isPrinting ? (
//                     <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
//                   ) : (
//                     <>
//                       <FaFileInvoice /> Print
//                     </>
//                   )}
//                 </button>
//               </div>
//             </div>
//           ) : null}

//           {/* Payment Confirmation Modal */}
//           {showPaymentModal && (
//             <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//               <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
//                 <h2 className="text-xl font-bold mb-4">Confirm Payment</h2>
//                 <div className="space-y-4">
//                   <div>
//                     <label className="block text-sm font-medium mb-2">Order ID</label>
//                     <input
//                       type="text"
//                       value={(orderId || roomOrderId || '').slice(0, 8)}
//                       disabled
//                       className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
//                     />
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium mb-2">Total Amount</label>
//                     <input
//                       type="text"
//                       value={`₹${billingInfo?.final_amount || roomBillingInfo?.final_amount || 0}`}
//                       disabled
//                       className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
//                     />
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium mb-2">Payment Method</label>
//                     <input
//                       type="text"
//                       value={selectedPaymentMethod === 'cash' ? 'Cash' : 'Razorpay'}
//                       disabled
//                       className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
//                     />
//                   </div>
//                   <div className="flex gap-3 pt-4">
//                     <button
//                       onClick={() => setShowPaymentModal(false)}
//                       className="flex-1 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
//                     >
//                       Cancel
//                     </button>
//                     <button
//                       onClick={confirmPayment}
//                       disabled={isProcessingPayment}
//                       className={`flex-1 py-2 bg-black text-white rounded-lg hover:bg-gray-800 ${
//                         isProcessingPayment ? 'opacity-50 cursor-not-allowed' : ''
//                       }`}
//                     >
//                       {isProcessingPayment ? (
//                         <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
//                       ) : (
//                         'Confirm Payment'
//                       )}
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}
//         </div>
//       </div>

//       <ToastContainer
//         position="bottom-right"
//         autoClose={2000}
//         hideProgressBar={false}
//         newestOnTop
//         closeOnClick
//         rtl={false}
//         pauseOnFocusLoss
//         draggable
//         pauseOnHover
//         theme="light"
//       />
//     </div>
//   );
// }

// export default FoodOrder;
